// ===== Packages =====

import React, {
    useRef,
    useMemo,
    useState,
    useEffect,
    useCallback,
}                                       from 'react';
import {
    useLocation,
    useNavigate,
}                                       from 'react-router-dom';
import Stripe                           from 'stripe';
import {
    useStripe,
    useElements,
    PaymentElement,
}                                       from '@stripe/react-stripe-js';
import {
    StripePaymentElementOptions,
    StripePaymentElementChangeEvent,
    ConfirmPaymentData,
    StripeError,
}                                       from '@stripe/stripe-js';
import {
    Rate,
    Address,
}                                       from 'shippo';
import { ReactSVG }                     from 'react-svg';
import {
    CountryCode,
    parsePhoneNumberFromString,
}                                       from 'libphonenumber-js';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment                           from 'moment-timezone';

// ===== Firebase =====

import fire                             from '../../fire';

// ===== Components =====

import PixelDropdown                    from '../PixelDropdown';
import PixelInput                       from '../PixelInput';
import { ICheckoutPromotionReviewResult } from '../../interfaces/ICheckoutPromotionReview';
import PixelRadioInput                  from '../PixelRadioInput';
import PixelCheckbox                    from '../PixelCheckbox';

// ===== Hooks =====

import {
    useInterval,
    useLatest,
}                                       from '../../hooks';

// ===== Enums =====

import {
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    CHECKOUT_CHECKPOINT,
    BILLING_ADDRESS_TYPE,
    SHIPPO_RATE_ATTRIBUTE_TYPE,
    PAGE_ROUTE,
    CHECKOUT_SESSION_STATUS_TYPE,
    STRIPE_PAYMENT_INTENT_ERROR_TYPE,
    STRIPE_PAYMENT_INTENT_STATUS,
    STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE,
    STRIPE_PAYMENT_METHOD_TYPES_SUPPORTING_SAVE,
    USER_ACTION_TYPE,
    AUTHENTICATION_TYPE,
    FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE,
    FIREBASE_CALLABLE_ERROR_CODE,
    TAX_CALCULATION_ERROR_REASON,
    CHECKOUT_PAYMENT_INTENT_UPDATE_ERROR_RESULT,
    BOOK_INVITE_RECIPIENT_ELIGIBILITY_INVALID_ARGUMENT_REASON,
    BOOK_INVITE_FIELD_KEY,
    FAILURE_TELEMETRY_TYPE,
}                                       from '../../enums';

// ===== Services =====

import {
    playAudio,
    validateName,
    validateEmail,
    detectTouchDevice,
    capitalizeFirstLetter,
    getBookInvitePreview,
    shippoAddressValidation,
    shippoCreateShipment,
    stripeUpdatePaymentIntent,
    stripeGetPaymentIntent,
    stripeGetTaxCalculationLineItems,
    stripeCreateCustomer,
    recordUserAction,
    updateUserStripeCustomerId,
    authenticateWithEmailConversion,
    isEmailUnusedByOtherUsers,
    getBookInviteRecipientEligibility,
    getUserEmailSessionItemsAndUpdateTimestamps,
    getUser,
    roundToNDecimals,
    getPressEventHandlers,
    getScrollAwareTapPressEventHandlers,
    getTouchMouseEventHandlers,
    isTestMode,
} from '../../services';
import inferNameFromEmail                               from '../../services/inferNameFromEmail';
import useLazyAudioRef                                 from '../../services/useLazyAudioRef';

// ===== Interfaces =====

import {
    IRadioItem,
    IDropdownItem,
    IShippoAddressObject,
    IShippoRateObject,
    ICountry,
    ICountryState,
    ISnackbarItem,
    IAddress,
    IPartialUserItem,
    IOrderCost,
    ISaleItem,
    ICartItem,
    IBookInviteRecipientEligibilityItem,
    IBookInviteRecipientEligibilityInvalidArgumentDetails,
    IBookInviteWithLocalId,
    IBookInvitePreview,
    ICheckoutLocationState,
}                                       from '../../interfaces';

// ===== Images =====

import PixelCartIcon                    from '../../images/8-bit_cart.svg';
import PixelChevronIcon                 from '../../images/8-bit_chevron.svg';
import PixelCrossIcon                   from '../../images/8-bit_cross.svg';
import Spinner                          from '../../images/editor/spinner.svg';

// ===== Sounds =====

import Success                          from '../../sounds/message_success.mp3';
import SwooshIn                         from '../../sounds/swoosh_in.mp3';
import SwooshOut                        from '../../sounds/swoosh_out.mp3';

// ===== Constants =====

import {
    DEFAULT_AUDIO_VOLUME,
    SUPPORT_EMAIL_ADDRESS,
    HOVER_TARGET_CLASSNAME,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    SHIPPO_DISABLED_FALLBACK_RATE_ID,
    CENTS_IN_A_DOLLAR,
    BODY_FONT_SIZE,
    CHECKOUT_UPDATE_SKIPPED_STATUS_NOT_MUTABLE_METADATA_KEY,
    BOOK_NAME,
    bookInviteEmailSubjectFromGifter,
    BUILD_BOOK_INVITE_PREVIEW,
    BOOK_INVITE_PROFILE_QUERY_PARAM_OPEN_PROFILE,
    BOOK_INVITE_PROFILE_QUERY_PARAM_PANEL,
    BOOK_INVITE_PROFILE_PANEL_BOOK_INVITES,
}                                       from '../../constants/generalConstants';
import {
    COUNTRY_SUBDIVISION_LABEL_BY_CODE2,
}                                       from '../../constants/checkoutCountryMetadata';
import CURSOR_SIGN                      from '../../constants/cursorSigns';
import countriesJSON                    from '../../constants/countries.json';

// ===== Styles =====

import {
    CheckpointFormContainer,
    InputGroupContainer,
    InputContainer,
    CheckpointTitle,
    ButtonsContainer,
    BackButton,
    BackButtonIconContainer,
    BackButtonTextContainer,
    ActionButton,
    CheckpointSubtitle,
    PaymentWidgetContainer,
    BillingAddressContainer,
    ReviewShippingDetailContainer,
    ReviewShippingDetail,
    ReviewShippingDetailTitle,
    ReviewShippingDetailBodyLine,
    ReviewShippingDetailBody,
    ReviewTransactionDetailContainer,
    ReviewTransactionDetail,
    ReviewTransactionDetailTitle,
    ReviewTransactionDetailBody,
    ReviewShippingDetailBodyLineHighlight,
    ReviewBookInviteIndexText,
    ReviewBookInviteBodyText,
    ReviewBookInviteEntry,
    ReviewTransactionTotal,
    ReviewTransactionTotalTitle,
    ReviewTransactionTotalBody,
    ReviewShippingContainer,
    ReviewShippingDetailEditButtonContainer,
    ReviewShippingDetailEditButton,
    BillingContainer,
    ReviewContainer,
    ReviewShippingDetailBodyLink,
    MailingListContainer,
    ActionButtonLoadingContainer,
    ActionButtonLoadingMessageText,
    SavePaymentMethodContainer,
    PaymentElementContainer,
    PaymentTrustText,
    CheckoutPolicyText,
    CheckoutPolicyLink,
    BookInviteSectionContainer,
    BookInviteSectionHeaderButton,
    BookInviteSectionHeaderTextContainer,
    BookInviteSectionHeaderTitle,
    BookInviteSectionHeaderSummary,
    BookInviteSectionHeaderSummaryValue,
    BookInviteSectionToggleButtonContainer,
    BookInviteSectionToggleButton,
    BookInviteSectionBody,
    BookInviteSelfAssignmentRow,
    BookInviteActionRow,
    BookInviteToggleActionButton,
    BookInviteSubtext,
    BookInviteSubtextLink,
    BookInviteRecipientInputRow,
    BookInviteRecipientInputContainer,
    BookInviteRecipientAddButton,
    BookInviteValidationText,
    BookInviteAssignedSummaryText,
    BookInviteChipList,
    BookInviteCard,
    BookInviteIndex,
    BookInviteIndexText,
    BookInviteBody,
    BookInviteActionsRow,
    BookInviteActionButtonContainer,
    BookInviteActionButton,
    BookInviteEmailPreviewContainer,
    BookInviteEmailPreviewHeader,
    BookInviteEmailPreviewToLine,
    BookInviteEmailPreviewSubject,
    BookInviteEmailPreviewBody,
    BookInviteEmailPreviewLoadingContainer,
    BookInvitePreviewSpinner,
    BookInviteSecondaryInputScale,
    BookInviteRecipientNameInputScale,
    BookInviteChipRow,
    BookInviteChipText,
    BookInviteChipRemoveButton,
}                                       from './styles';
import {
    SpinnerContainer,
}                                       from '../CartView/styles';
import {
    PlaceholderBox,
}                                       from '../../styles';

// ===== Initialize Firebase =====

const { auth } = fire();

interface Props {
    customCursorActive: boolean,
    hasSound: boolean,
    user: IPartialUserItem | null,
    userEmail: string | null,
    preparePromotionsForReview?: (email: string) => Promise<ICheckoutPromotionReviewResult>,
    finishPromotionsForReview?: (payment: Stripe.PaymentIntent) => Promise<void>,
    userCartItems: ICartItem[] | undefined,
    bookInvitesInOrderCount: number,
    currentSessionId: string | null,
    checkpoint: string | undefined,
    fetchedStoreItems: boolean,
    physicalItemsInCart: string[],
    stripePaymentIntentId: string | undefined,
    stripeClientSecret: string | undefined,
    stripeCustomerId: string | undefined,
    stripeTaxCalculationId: string | undefined,
    savedAddresses: IAddress[],
    savedStripePaymentMethods: Stripe.PaymentMethod[],
    customerStreetOne: string,
    addressIsValid: boolean,
    customerBillingStreetOne: string,
    billingAddressIsValid: boolean,
    shippingRates: Map<string, IShippoRateObject>,
    selectedShippingRateID: string | undefined,
    selectedBillingAddressType: IRadioItem | undefined,
    orderCost?: {
        subtotal: number,
        shipping?: number,
        tax?: number,
        total?: number,
    },
    stripePaymentDetailsCompleted: boolean,
    stripePaymentSucceeded: boolean,
    hasComplimentaryBookInvite: boolean,
    // Runtime harnesses can seed an already-validated anonymous checkout email
    // so review-stage conversion/payment flows can be exercised directly.
    initialValidatedAnonymousCheckoutEmail?: string | null,
    // Runtime harnesses can seed derived shipping methods when mounting
    // directly onto the shipping checkpoint.
    initialShippingMethodsForRuntimeHarness?: IRadioItem[],
    snackbarData: ISnackbarItem,
    stripeSucceededPaymentIntent: Stripe.PaymentIntent | undefined,
    stripePaymentMethod: Stripe.PaymentMethod | undefined,
    orderSaleItem: ISaleItem | undefined,
    changeCheckoutSessionStatus: (status: CHECKOUT_SESSION_STATUS_TYPE) => void,
    setOrderCost: React.Dispatch<React.SetStateAction<IOrderCost | undefined>>,
    setStripeTaxCalculationId: React.Dispatch<React.SetStateAction<string | undefined>>,
    setCustomerStreetOne: React.Dispatch<React.SetStateAction<string>>,
    setAddressIsValid: React.Dispatch<React.SetStateAction<boolean>>,
    setCustomerBillingStreetOne: React.Dispatch<React.SetStateAction<string>>,
    setBillingAddressIsValid: React.Dispatch<React.SetStateAction<boolean>>,
    setShippingRates: React.Dispatch<React.SetStateAction<Map<string, IShippoRateObject>>>,
    setSelectedShippingRateID: React.Dispatch<React.SetStateAction<string | undefined>>,
    setSelectedBillingAddressType: React.Dispatch<React.SetStateAction<IRadioItem | undefined>>,
    setStripePaymentDetailsCompleted: React.Dispatch<React.SetStateAction<boolean>>,
    setCustomerDetailsCompleted: React.Dispatch<React.SetStateAction<boolean>>,
    setHadComplimentaryBookInviteForCheckoutFlow: React.Dispatch<React.SetStateAction<boolean>>,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    setIsUserProfileDialogExpanded: (isExpanded: boolean) => void,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setCursorIsHidden: React.Dispatch<React.SetStateAction<boolean>>,
}

function CheckoutCheckpoint({
    customCursorActive,
    hasSound,
    user,
    userEmail,
    preparePromotionsForReview,
    finishPromotionsForReview,
    userCartItems,
    bookInvitesInOrderCount,
    currentSessionId,
    checkpoint,
    fetchedStoreItems,
    physicalItemsInCart,
    savedAddresses,
    savedStripePaymentMethods,
    stripePaymentIntentId,
    stripeClientSecret,
    stripeCustomerId,
    stripeTaxCalculationId,
    customerStreetOne,
    addressIsValid,
    customerBillingStreetOne,
    billingAddressIsValid,
    shippingRates,
    selectedShippingRateID,
    selectedBillingAddressType,
    orderCost,
    stripePaymentDetailsCompleted,
    stripePaymentSucceeded,
    hasComplimentaryBookInvite,
    initialValidatedAnonymousCheckoutEmail = null,
    initialShippingMethodsForRuntimeHarness,
    snackbarData,
    stripeSucceededPaymentIntent,
    stripePaymentMethod,
    orderSaleItem,
    changeCheckoutSessionStatus,
    setOrderCost,
    setStripeTaxCalculationId,
    setCustomerStreetOne,
    setAddressIsValid,
    setCustomerBillingStreetOne,
    setBillingAddressIsValid,
    setShippingRates,
    setSelectedShippingRateID,
    setSelectedBillingAddressType,
    setStripePaymentDetailsCompleted,
    setCustomerDetailsCompleted,
    setHadComplimentaryBookInviteForCheckoutFlow,
    setSnackbarData,
    setIsUserProfileDialogExpanded,
    setInputFocused,
    onCursorEnter,
    onCursorLeave,
    setCursorIsHidden,
}: Props): JSX.Element {
    // ===== General Constants =====

    // Snackbar Message when user hasn't entered a first name
    const SNACKBAR_MESSAGE_NO_FIRST_NAME_ERROR = 'Please provide a first name.';
    // Snackbar Message when user hasn't entered an email
    const SNACKBAR_MESSAGE_NO_EMAIL_ERROR = 'Please provide an email address.';
    // Snackbar Message when user hasn't entered a valid email
    const SNACKBAR_MESSAGE_INVALID_EMAIL_ERROR = 'Please provide a valid email address.';
    // Snackbar Message when checkout email already belongs to an existing account
    const SNACKBAR_MESSAGE_EMAIL_IN_USE_ERROR = 'An account already exists with this email. Please sign in to continue or provide another email.';
    // Snackbar Message when user hasn't entered a last name
    const SNACKBAR_MESSAGE_NO_LAST_NAME_ERROR = 'Please provide a last name.';
    // Snackbar Message when user hasn't selected a country
    const SNACKBAR_MESSAGE_NO_COUNTRY_ERROR = 'Please select a country.';
    // Snackbar Message when user hasn't selected a state
    const SNACKBAR_MESSAGE_NO_STATE_ERROR = 'Please select a state/province.';
    // Snackbar Message when user hasn't entered a city
    const SNACKBAR_MESSAGE_NO_CITY_ERROR = 'Please provide a city.';
    // Snackbar Message when user hasn't entered a zip code
    const SNACKBAR_MESSAGE_NO_ZIP_CODE_ERROR = 'Please provide a zip code.';
    // Snackbar Message when user hasn't entered a street address
    const SNACKBAR_MESSAGE_NO_STREET_ONE_ERROR = 'Please provide a street address.';
    // Snackbar Message when user hasn't entered a phone number
    const SNACKBAR_MESSAGE_NO_PHONE_NUMBER_ERROR = 'Please provide a phone number.';
    // Snackbar Message when user hasn't entered a valid phone number
    const SNACKBAR_MESSAGE_INVALID_PHONE_NUMBER_ERROR = 'Please provide a valid phone number for the selected country.';
    // Snackbar Message when user hasn't entered valid mailing address: specific error
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_MAILING_ADDRESS_ERROR_MESSAGE = (error: string): string => `Encountered an error validating your mailing address: ${error}`;
    // Snackbar Message when user hasn't entered valid mailing address: generic error
    const SNACKBAR_MESSAGE_MAILING_ADDRESS_ERROR = 'Encountered an error validating your mailing address. Please provide a valid mailing address.';
    // Snackbar Message when encounter error fetching shipping rates
    const SNACKBAR_MESSAGE_SHIPPING_RATE_ERROR_MESSAGE = (error: string): string => `Encountered an error fetching shipping rates: ${error}.`;
    // Snackbar Message when rates information i
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_SHIPPING_NO_RATE_ERROR = `Unable to proceed. Shipping rates information cannot be accessed. Please contact the verascope team for support at: ${
        SUPPORT_EMAIL_ADDRESS
    }.`;
    // Snackbar Message when user hasn't selected shipping method
    const SNACKBAR_MESSAGE_SELECT_VALID_SHIPPING_METHOD_ERROR = 'Please select a valid shipping method.';
    //
    const SNACKBAR_MESSAGE_BILLING_NO_ADDRESS_ERROR = 'Please select a billing adress option.';
    // Snackbar Message when user hasn't entered a first name
    const SNACKBAR_MESSAGE_NO_BILLING_FIRST_NAME_ERROR = 'Please provide a first name.';
    // Snackbar Message when user hasn't entered a last name
    const SNACKBAR_MESSAGE_NO_BILLING_LAST_NAME_ERROR = 'Please provide a last name.';
    // Snackbar Message when user hasn't selected a billing country
    const SNACKBAR_MESSAGE_NO_BILLING_COUNTRY_ERROR = 'Please select a country.';
    // Snackbar Message when user hasn't selected a billing state
    const SNACKBAR_MESSAGE_NO_BILLING_STATE_ERROR = 'Please select a state/province.';
    // Snackbar Message when user hasn't entered a billing city
    const SNACKBAR_MESSAGE_NO_BILLING_CITY_ERROR = 'Please provide a city.';
    // Snackbar Message when user hasn't entered a billing zip code
    const SNACKBAR_MESSAGE_NO_BILLING_ZIP_CODE_ERROR = 'Please provide a zip code.';
    // Snackbar Message when user hasn't entered a billing street address
    const SNACKBAR_MESSAGE_NO_BILLING_STREET_ONE_ERROR = 'Please provide a street address.';
    // Snackbar Message when user hasn't entered a billing phone number
    const SNACKBAR_MESSAGE_NO_BILLING_PHONE_NUMBER_ERROR = 'Please provide a billing phone number.';
    // Snackbar Message when user hasn't entered a valid billing phone number
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_INVALID_BILLING_PHONE_NUMBER_ERROR = 'Please provide a valid billing phone number for the selected country.';
    // Snackbar Message when user hasn't entered valid billing address: specific error
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_BILLING_ADDRESS_ERROR_MESSAGE = (error: string): string => `Encountered an error validating your billing address: ${error}`;
    // Snackbar Message when Stripe Payment Element validation failed
    const SNACKBAR_MESSAGE_PAYMENT_DETAILS_ERROR_MESSAGE = (error: string): string => `Payment details validation error: ${error}`;
    // Snackbar Message when Stripe Payment Element processing failed: specific error
    const SNACKBAR_MESSAGE_PAYMENT_ERROR_MESSAGE = (error: string): string => `Payment failed. ${error}`;
    // Snackbar Message when Stripe Payment Element processing failed: generic error
    const SNACKBAR_MESSAGE_GENERIC_PAYMENT_ERROR = 'Payment failed. Please try again.';
    // Snackbar Message when Stripe tax calculation rejects the submitted address payload.
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_TAX_ADDRESS_NOT_USABLE_ERROR = 'We could not use this address to calculate tax. Please check the address (street, city, state/province, postal code, and country) and try again.';
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_TAX_INPUT_INVALID_ERROR = 'We could not calculate tax because some checkout details are invalid. Please refresh and try again.';
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_TAX_SERVICE_UNAVAILABLE_ERROR = 'Tax calculation is temporarily unavailable. Please try again later.';
    // Temporary feature flag to bypass Shippo-dependent checkout paths.
    const SHIPPO_DISABLED = (process.env.REACT_APP_DISABLE_SHIPPO || 'false').toLowerCase() === 'true';
    // Snackbar Message when user hasn't entered valid billing address: generic error
    const SNACKBAR_MESSAGE_BILLING_ADDRESS_ERROR = 'Encountered an error validating your billing address. Please provide a valid billing address.';
    // Snackbar Message when user hasn't entered valid billing address: generic error
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_REVIEW_ERROR = 'Unable to proceed. Lost connection with payment processing service. Please refresh the page and try again.';
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_PAYMENT_ALREADY_PROCESSED = 'Your payment is already being processed. We are redirecting you to confirmation.';
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_PAYMENT_STATUS_STALE = 'Unable to finalize checkout details because this payment has already moved to another status. Please refresh and try again.';
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_CHECKOUT_MODE_ERROR = `Unable to complete purchase. Encountered an error checking out. Please contact the verascope team for support at: ${
        SUPPORT_EMAIL_ADDRESS
    }.`;
    // Snackbar Message when Stripe.js is not ready for billing/review actions.
    const SNACKBAR_MESSAGE_STRIPE_NOT_READY_ERROR = 'Payment service is not ready yet. Please wait a moment and try again.';
    // Snackbar Message when shipping checkpoint payment-intent update fails.
    const SNACKBAR_MESSAGE_SHIPPING_UPDATE_ERROR = `Unable to save shipping selection right now. Please contact verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar Message when billing checkpoint payment-intent update fails.
    const SNACKBAR_MESSAGE_BILLING_UPDATE_ERROR = `Unable to save billing details right now. Please contact verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar Message when review pre-confirm payment-intent update fails.
    const SNACKBAR_MESSAGE_REVIEW_PRECONFIRM_UPDATE_ERROR = `Unable to prepare payment confirmation. Please contact verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar Message when review saved-method payment-intent update fails.
    const SNACKBAR_MESSAGE_REVIEW_SAVED_METHOD_UPDATE_ERROR = `Unable to apply your saved payment method for checkout. Please contact verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar Message when checkout account cannot be verified before charging payment method.
    const SNACKBAR_MESSAGE_REVIEW_ACCOUNT_VERIFICATION_ERROR = (
        'Unable to verify your account before charging your payment method. '
        + `Please sign in again or contact verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`
    );
    // Snackbar Message when no firestore IUserItem exists for checkout auth user.
    const SNACKBAR_MESSAGE_REVIEW_ACCOUNT_REQUIRED_ERROR = (
        'We could not find an active verascope account for this checkout. '
        + 'Please sign in again before completing payment.'
    );
    // Snackbar Message when social sign-in account is missing its firestore IUserItem.
    const SNACKBAR_MESSAGE_REVIEW_SOCIAL_ACCOUNT_REQUIRED_ERROR = (
        'Your social sign-in is missing its verascope account profile. '
        + 'Please sign out, sign in again, and then retry checkout.'
    );
    // Snackbar Message when unable to create account during checkout
    const SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_ERROR = 'Unable to create your account from checkout details. Please verify your email and try again.';
    // Snackbar Message when checkout email verification cannot be completed for conversion.
    const SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_PRECHECK_ERROR = `Unable to verify this email right now. Please contact verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar Message when anonymous checkout conversion cannot continue due to session/auth state.
    const SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_SESSION_ERROR = 'Your checkout session changed while creating your account. Please refresh and try again.';
    // Snackbar Message when anonymous checkout data cannot be found during conversion.
    const SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_USER_NOT_FOUND_ERROR = (
        'We could not find your guest checkout session while creating your account. '
        + `Please contact verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`
    );
    // Snackbar Message when conversion request arrives without an authenticated checkout session.
    const SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_UNAUTHENTICATED_ERROR = (
        'Your checkout session expired while creating your account. Please refresh and try again.'
    );
    // Snackbar Message when account creation succeeded but the follow-up sign-in/setup step failed.
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_SIGN_IN_FALLBACK_ERROR = `Your account was created, but we could not finish signing you in. Please contact verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Book Invite recipient eligibility message when recipient already has active web-book access.
    const BOOK_INVITE_RECIPIENT_HAS_ACCESS_ERROR_PREFIX = 'These recipients already have active web book invites:';
    const BOOK_INVITE_RECIPIENT_HAS_ACCESS_ERROR_SUFFIX = 'Remove them from book invites to continue.';
    // Book Invite recipient eligibility message when server-side verification fails.
    const BOOK_INVITE_RECIPIENT_ELIGIBILITY_LOOKUP_ERROR = [
        'We couldn\'t verify recipient eligibility for book invites right now.',
        `Please contact the verascope team for support at: ${SUPPORT_EMAIL_ADDRESS}`,
    ].join(' ');
    // Book Invite recipient eligibility message when server validates malformed recipient emails.
    const BOOK_INVITE_RECIPIENT_ELIGIBILITY_INVALID_EMAIL_ERROR = (
        'One or more recipient emails are invalid. Correct the red invite cards, then try again.'
    );
    const BOOK_INVITE_RECIPIENT_ELIGIBILITY_MISSING_EMAILS_ERROR = (
        'Recipient emails are required to create book invites.'
    );
    const BOOK_INVITE_RECIPIENT_ELIGIBILITY_TOO_MANY_EMAILS_ERROR = (
        'Too many recipient emails submitted.'
    );
    const BOOK_INVITE_RECIPIENT_ELIGIBILITY_TOO_MANY_EMAILS_WITH_LIMIT_ERROR = (
        maxRecipientEmails: number,
    ): string => `Too many recipient emails submitted. Limit is ${maxRecipientEmails}.`;
    const BOOK_INVITE_RECIPIENT_INPUT_INVALID_EMAIL_ERROR = (
        'Please enter a valid recipient email.'
    );
    const BOOK_INVITE_RECIPIENT_INPUT_ASSIGNED_TO_SELF_ERROR = (
        'This email is already assigned to you. Uncheck "Keep 1 Book Invite for Me" or use another email.'
    );
    const BOOK_INVITE_RECIPIENT_INPUT_DUPLICATE_EMAIL_ERROR = (
        'This recipient email is already added.'
    );
    const BOOK_INVITE_RECIPIENT_INPUT_NO_MORE_ASSIGNMENTS_ERROR = (
        'No more Book Invites available for book invites in this order.'
    );
    const BOOK_INVITE_RECIPIENT_INPUT_SELF_ASSIGNMENT_EXHAUSTED_ERROR = (
        'All book invites are currently assigned. Remove one recipient to keep an invite for yourself.'
    );
    const BOOK_INVITE_RECIPIENT_INPUT_SELF_EMAIL_CONFLICT_ERROR = (
        'Your email is already covered by "Keep 1 Book Invite for Me". Remove it from book invites.'
    );
    const ACTION_BUTTON_MESSAGE_ELLIPSIS_INTERVAL_DURATION = 1000;
    // Avoid accepting shortcode-like phone numbers in checkout contact details.
    const CHECKOUT_MIN_PHONE_NSN_LENGTH = 8;
    const CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH = 10 * BODY_FONT_SIZE;
    const CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT = 1 * BODY_FONT_SIZE;
    const CONFIRMATION_PRICE_PLACEHOLDER_MAX_WIDTH = 7 * BODY_FONT_SIZE;

    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();

    // ===== Stripe =====

    const stripe = useStripe();
    const elements = useElements();

    // ===== Refs =====

    const checkpointFormContainerRef = useRef<HTMLDivElement>(null);
    const nextBookInviteIdRef = useRef<number>(0);
    const bookInviteSectionRef = useRef<HTMLDivElement>(null);
    const lastAutosavedCheckoutEmailRef = useRef<string>('');
    const checkoutEmailAutosaveInFlightEmailRef = useRef<string>('');

    // ----- Sound Clips
    const swooshInClip = useLazyAudioRef({
        src: SwooshIn,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const swooshOutClip = useLazyAudioRef({
        src: SwooshOut,
        volume: DEFAULT_AUDIO_VOLUME,
    });

    // ===== State =====

    // ----- General
    // Indicates whether action button is loading
    const [actionButtonIsLoading, setActionButtonIsLoading] = useState<boolean>(false);
    // Stores a message to present on action button when loading
    const [actionButtonLoadingMessage, setActionButtonLoadingMessage] = useState<string>('');
    // Animates action button loading text when loading
    const [actionButtonMessageEllipsis, setActionButtonMessageEllipsis] = useState<string>('...');

    // ----- Info
    // Stores selected saved address
    const [selectedSavedAddress, setSelectedSavedAddress] = useState<IDropdownItem | undefined>(undefined);
    // Stores checkout email for users without an authenticated email
    const [checkoutEmail, setCheckoutEmail] = useState<string>(userEmail || auth.currentUser?.email || '');
    // Stores a list of countries
    const [countries, setCountries] = useState<IDropdownItem[]>([]);
    // Stores selected country
    const [customerCountry, setCustomerCountry] = useState<IDropdownItem | undefined>(undefined);
    // Stores first name of customer
    const [customerFirstName, setCustomerFirstName] = useState<string>('');
    // Stores last name of customer
    const [customerLastName, setCustomerLastName] = useState<string>('');
    // Stores company of customer
    const [customerCompany, setCustomerCompany] = useState<string>('');
    // Stores street address line two of customer
    const [customerStreetTwo, setCustomerStreetTwo] = useState<string>('');
    // Stores city of customer
    const [customerCity, setCustomerCity] = useState<string>('');
    // Stores a list of states
    const [states, setStates] = useState<IDropdownItem[]>([]);
    // Stores state of customer
    const [customerState, setCustomerState] = useState<IDropdownItem | undefined>(undefined);
    // Stores zip code of customer
    const [customerZipCode, setCustomerZipCode] = useState<string>('');
    // Stores phone of customer
    const [customerPhone, setCustomerPhone] = useState<string>('');
    // Indicates whether first name is not provided
    const [firstNameNotProvided, setFirstNameNotProvided] = useState<boolean>(false);
    // Indicates whether email is not provided
    const [emailNotProvided, setEmailNotProvided] = useState<boolean>(false);
    // Indicates whether email is invalid
    const [emailInvalid, setEmailInvalid] = useState<boolean>(false);
    // Stores the normalized anonymous checkout email that has already been validated as unused.
    const [validatedAnonymousCheckoutEmail, setValidatedAnonymousCheckoutEmail] = useState<string | null>(
        initialValidatedAnonymousCheckoutEmail,
    );
    // Indicates whether last name is not provided
    const [lastNameNotProvided, setLastNameNotProvided] = useState<boolean>(false);
    // Indicates whether country is not selected
    const [countryNotSelected, setCountryNotSelected] = useState<boolean>(false);
    // Indicates whether state is not selected
    const [stateNotSelected, setStateNotSelected] = useState<boolean>(false);
    // Indicates whether city is not provided
    const [cityNotProvided, setCityNotProvided] = useState<boolean>(false);
    // Indicates whether zip code is not provided
    const [zipCodeNotProvided, setZipCodeNotProvided] = useState<boolean>(false);
    // Indicates whether street one is not provided
    const [streetOneNotProvided, setStreetOneNotProvided] = useState<boolean>(false);
    // Indicates whether phone number is not provided
    const [phoneNumberNotProvided, setPhoneNumberNotProvided] = useState<boolean>(false);

    // ----- Shipping Method
    // Stores a list of shipping methods
    const [shippingMethods, setShippingMethods] = useState<Map<string, IRadioItem>>(() => new Map(
        (initialShippingMethodsForRuntimeHarness || []).map((item) => [
            item.value,
            item,
        ]),
    ));

    // ----- Billing
    // Stores selected saved stripe payment method
    const [selectedSavedStripePaymentMethod, setSelectedSavedStripePaymentMethod] = useState<IDropdownItem | undefined>(undefined);
    // Stores stripe payment method
    const [stripePaymentMethodType, setStripePaymentMethodType] = useState<string | undefined>(undefined);
    // Indicates whether stripe is handling payment
    const [stripePaymentIsLoading, setStripePaymentIsLoading] = useState<boolean>(false);
    // Stores selected saved billing address
    const [selectedSavedBillingAddress, setSelectedSavedBillingAddress] = useState<IDropdownItem | undefined>(undefined);
    // Stores selected billing country
    const [customerBillingCountry, setCustomerBillingCountry] = useState<IDropdownItem | undefined>(undefined);
    // Stores first name of billing customer
    const [customerBillingFirstName, setCustomerBillingFirstName] = useState<string>('');
    // Stores last name of billing customer
    const [customerBillingLastName, setCustomerBillingLastName] = useState<string>('');
    // Stores company of billing customer
    const [customerBillingCompany, setCustomerBillingCompany] = useState<string>('');
    // Stores street address line two of billing customer
    const [customerBillingStreetTwo, setCustomerBillingStreetTwo] = useState<string>('');
    // Stores city of billing customer
    const [customerBillingCity, setCustomerBillingCity] = useState<string>('');
    // Stores a list of billing states
    const [billingStates, setBillingStates] = useState<IDropdownItem[]>([]);
    // Stores state of billing customer
    const [customerBillingState, setCustomerBillingState] = useState<IDropdownItem | undefined>(undefined);
    // Stores zip code of billing customer
    const [customerBillingZipCode, setCustomerBillingZipCode] = useState<string>('');
    // Stores phone of billing customer
    const [customerBillingPhone, setCustomerBillingPhone] = useState<string>('');
    // Indicates whether billing first name is not provided
    const [billingFirstNameNotProvided, setBillingFirstNameNotProvided] = useState<boolean>(false);
    // Indicates whether billing last name is not provided
    const [billingLastNameNotProvided, setBillingLastNameNotProvided] = useState<boolean>(false);
    // Indicates whether billing country is not selected
    const [billingCountryNotSelected, setBillingCountryNotSelected] = useState<boolean>(false);
    // Indicates whether billing state is not selected
    const [billingStateNotSelected, setBillingStateNotSelected] = useState<boolean>(false);
    // Indicates whether billing city is not provided
    const [billingCityNotProvided, setBillingCityNotProvided] = useState<boolean>(false);
    // Indicates whether billing zip code is not provided
    const [billingZipCodeNotProvided, setBillingZipCodeNotProvided] = useState<boolean>(false);
    // Indicates whether billing street one is not provided
    const [billingStreetOneNotProvided, setBillingStreetOneNotProvided] = useState<boolean>(false);
    // Indicates whether billing phone number is not provided
    const [billingPhoneNumberNotProvided, setBillingPhoneNumberNotProvided] = useState<boolean>(false);
    // Indicates whether customer wants to subscribe to mailing list
    const [subscribeToMailingList, setSubscribeToMailingList] = useState<boolean>(false);
    // Indicates whether customer wants to save payment method
    const [savePaymentMethod, setSavePaymentMethod] = useState<boolean>(false);
    // Indicates whether Book Invite section is expanded.
    const [bookInviteSectionExpanded, setBookInviteSectionExpanded] = useState<boolean>(false);
    // Indicates whether one Book Invite is reserved for the buyer.
    const [assignOneBookInviteToSelf, setAssignOneBookInviteToSelf] = useState<boolean>(true);
    // Indicates whether checkout should collect gift recipients right now.
    const [assignBookInviteGiftsNow, setAssignBookInviteGiftsNow] = useState<boolean>(false);
    // Stores recipient email input for chip creation.
    const [bookInviteRecipientInput, setBookInviteRecipientInput] = useState<string>('');
    // Bumps to force remount when recipient input value is rewritten programmatically.
    const [bookInviteRecipientInputRenderKey, setBookInviteRecipientInputRenderKey] = useState<number>(0);
    // Stores validation copy for recipient chip input.
    const [bookInviteRecipientInputError, setBookInviteRecipientInputError] = useState<string>('');
    // Stores eligibility errors for recipient emails that already have active web-book access.
    const [bookInviteRecipientEligibilityError, setBookInviteRecipientEligibilityError] = useState<string>('');
    // Stores recipient emails flagged by backend eligibility validation as malformed.
    const [bookInviteRecipientEligibilityInvalidEmails, setBookInviteRecipientEligibilityInvalidEmails] = useState<string[]>([]);
    // Stores whether the checkout email currently has active web-book access.
    const [buyerHasActiveWebBookAccess, setBuyerHasActiveWebBookAccess] = useState<boolean>(false);
    // Stores draft gift recipients entered in checkout.
    const [bookInvites, setBookInvites] = useState<IBookInviteWithLocalId[]>([]);
    // Stores invite id currently showing email preview.
    const [bookInvitePreviewInviteId, setBookInvitePreviewInviteId] = useState<string | null>(null);
    const [bookInvitePreviews, setBookInvitePreviews] = useState<Record<string, IBookInvitePreview>>({});
    const [bookInvitePreviewLoadingIds, setBookInvitePreviewLoadingIds] = useState<Record<string, boolean>>({});
    const [bookInvitePreviewOpenedIds, setBookInvitePreviewOpenedIds] = useState<Set<string>>(
        () => new Set<string>(),
    );
    const userMailingListHistory = user?.mailingListSubscription?.history;
    const userIsSubscribedToMailingList = !!(
        userMailingListHistory
        && userMailingListHistory.length > 0
        && userMailingListHistory[userMailingListHistory.length - 1].subscribed
    );
    const userIsAnonymous = !!auth.currentUser?.isAnonymous
        || user?.authenticationType === AUTHENTICATION_TYPE.anonymous;

    // ===== Methods =====

    const getCheckoutEmail = (): string | undefined => {
        const email = checkoutEmail.trim() || userEmail || auth.currentUser?.email || '';
        return email.length > 0 ? email : undefined;
    };
    const normalizeEmail = (email: string): string => email.trim().toLowerCase();
    /**
     * Reads the email Stripe will use for checkout receipts and fulfillment.
     */
    const getPaymentIntentCheckoutEmail = (
        paymentIntent?: Stripe.PaymentIntent,
    ): string | undefined => {
        const paymentIntentEmail = (
            paymentIntent?.metadata?.userEmail
            || paymentIntent?.receipt_email
            || ''
        ).trim();
        return paymentIntentEmail.length > 0 ? paymentIntentEmail : undefined;
    };
    const parseBookInviteRecipientEligibilityErrorDetails = (
        details?: unknown,
    ): IBookInviteRecipientEligibilityInvalidArgumentDetails | undefined => {
        if (
            details == null
            || typeof details !== 'object'
            || Array.isArray(details)
        ) {
            return undefined;
        }
        return details as IBookInviteRecipientEligibilityInvalidArgumentDetails;
    };
    const getInvalidRecipientEmailsFromEligibilityError = (
        details?: unknown,
    ): string[] => {
        const parsedDetails = parseBookInviteRecipientEligibilityErrorDetails(details);
        const invalidEmailsFromDetails = parsedDetails?.invalidEmails;
        if (Array.isArray(invalidEmailsFromDetails)) {
            return Array.from(new Set(
                invalidEmailsFromDetails
                    .map((item: string) => normalizeEmail(item))
                    .filter((item: string) => item.length > 0),
            ));
        }
        return [];
    };
    const getInvalidArgumentEligibilityMessage = (
        details?: unknown,
    ): string => {
        const parsedDetails = parseBookInviteRecipientEligibilityErrorDetails(details);
        if (
            parsedDetails?.reason
            === BOOK_INVITE_RECIPIENT_ELIGIBILITY_INVALID_ARGUMENT_REASON.invalidRecipientEmails
        ) {
            return BOOK_INVITE_RECIPIENT_ELIGIBILITY_INVALID_EMAIL_ERROR;
        }
        if (
            parsedDetails?.reason
            === BOOK_INVITE_RECIPIENT_ELIGIBILITY_INVALID_ARGUMENT_REASON.missingRecipientEmails
        ) {
            return BOOK_INVITE_RECIPIENT_ELIGIBILITY_MISSING_EMAILS_ERROR;
        }
        if (
            parsedDetails?.reason
            === BOOK_INVITE_RECIPIENT_ELIGIBILITY_INVALID_ARGUMENT_REASON.tooManyRecipientEmails
        ) {
            if (typeof parsedDetails.maxRecipientEmails === 'number') {
                return BOOK_INVITE_RECIPIENT_ELIGIBILITY_TOO_MANY_EMAILS_WITH_LIMIT_ERROR(
                    parsedDetails.maxRecipientEmails,
                );
            }
            return BOOK_INVITE_RECIPIENT_ELIGIBILITY_TOO_MANY_EMAILS_ERROR;
        }
        return BOOK_INVITE_RECIPIENT_ELIGIBILITY_INVALID_EMAIL_ERROR;
    };
    const shouldRenderBookInviteSectionOnInfo = (
        physicalItemsInCart.length > 0
        && bookInvitesInOrderCount > 0
    );
    const shouldRenderBookInviteSectionOnBilling = (
        physicalItemsInCart.length === 0
        && bookInvitesInOrderCount > 0
    );
    const checkoutLocationState = location.state as ICheckoutLocationState | null;
    const shouldFocusBookInvitesSectionFromNavigation = !!checkoutLocationState?.focusBookInvitesSection;
    const effectiveAssignOneBookInviteToSelf = (
        assignOneBookInviteToSelf
        && !buyerHasActiveWebBookAccess
    );
    const maxInviteAssignments = Math.max(
        0,
        bookInvitesInOrderCount - (effectiveAssignOneBookInviteToSelf ? 1 : 0),
    );
    const remainingInviteAssignments = Math.max(
        0,
        maxInviteAssignments - bookInvites.length,
    );
    const selfAssignmentUsesOnlyInvite = (
        bookInvitesInOrderCount === 1
        && effectiveAssignOneBookInviteToSelf
    );
    const activeBookInviteCount = assignBookInviteGiftsNow
        ? bookInvites.length
        : 0;
    const bookInvitePayload = useMemo(() => (
        assignBookInviteGiftsNow
            ? bookInvites.map((invite) => ({
                recipientEmail: invite.recipientEmail,
                recipientName: invite.recipientName,
                inviteMessage: invite.inviteMessage,
                previewOpened: bookInvitePreviewOpenedIds.has(invite.id),
            }))
            : []
    ), [
        assignBookInviteGiftsNow,
        bookInvites,
        bookInvitePreviewOpenedIds,
    ]);
    const assignedBookInviteCount = (
        (effectiveAssignOneBookInviteToSelf ? 1 : 0) + activeBookInviteCount
    );
    const hadBookInviteAtCheckout = (
        hasComplimentaryBookInvite
        || bookInvitesInOrderCount > 0
    );
    const checkoutEmailForBookInvite = getCheckoutEmail();
    const normalizedCheckoutEmail = checkoutEmailForBookInvite
        ? normalizeEmail(checkoutEmailForBookInvite)
        : '';
    useEffect(() => {
        let cancelled = false;
        const email = normalizedCheckoutEmail;
        if (!validateEmail(email)) {
            setBuyerHasActiveWebBookAccess(false);
            return () => {
                cancelled = true;
            };
        }
        const timeoutId = window.setTimeout(async () => {
            try {
                const eligibilityItems = await getBookInviteRecipientEligibility({
                    test: isTestMode(),
                    emails: [email],
                });
                if (cancelled) return;
                const eligibilityItem = eligibilityItems.find(
                    (item: IBookInviteRecipientEligibilityItem) => item.normalizedEmail === email,
                ) || eligibilityItems[0];
                setBuyerHasActiveWebBookAccess(!!eligibilityItem?.hasActiveWebBookAccess);
            } catch {
                if (!cancelled) {
                    setBuyerHasActiveWebBookAccess(false);
                }
            }
        }, 300);
        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [normalizedCheckoutEmail]);
    const normalizedBookInviteRecipientInput = normalizeEmail(bookInviteRecipientInput);
    const gifterDisplayName = useMemo(() => {
        const billingFullName = `${customerBillingFirstName || ''} ${customerBillingLastName || ''}`.trim();
        if (billingFullName.length > 0) return billingFullName;
        const fullName = `${customerFirstName || ''} ${customerLastName || ''}`.trim();
        if (fullName.length > 0) return fullName;
        if (user?.authenticationType !== AUTHENTICATION_TYPE.anonymous) {
            const userFullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
            if (userFullName.length > 0) return userFullName;
        }
        const fallbackEmail = normalizeEmail(checkoutEmailForBookInvite || '');
        if (fallbackEmail.length > 0) {
            const inferred = inferNameFromEmail(fallbackEmail);
            const inferredName = `${inferred.firstName || ''} ${inferred.lastName || ''}`.trim();
            if (inferredName.length > 0) return inferredName;
        }
        return 'Someone';
    }, [
        customerBillingFirstName,
        customerBillingLastName,
        customerFirstName,
        customerLastName,
        user?.authenticationType,
        user?.firstName,
        user?.lastName,
        checkoutEmailForBookInvite,
    ]);
    const buildLocalBookInvitePreview = useCallback((invite: IBookInviteWithLocalId): IBookInvitePreview => (
        BUILD_BOOK_INVITE_PREVIEW({
            recipientEmail: invite.recipientEmail,
            recipientName: invite.recipientName,
            inviteMessage: invite.inviteMessage,
            gifterDisplayName,
            gifterEmail: checkoutEmailForBookInvite,
        })
    ), [
        gifterDisplayName,
        checkoutEmailForBookInvite,
    ]);
    const getDisplayBookInvitePreview = useCallback((invite: IBookInviteWithLocalId): IBookInvitePreview => (
        bookInvitePreviews[invite.id] || buildLocalBookInvitePreview(invite)
    ), [bookInvitePreviews, buildLocalBookInvitePreview]);
    const fetchBookInvitePreview = async (invite: IBookInviteWithLocalId): Promise<void> => {
        if (bookInvitePreviewLoadingIds[invite.id]) return;
        setBookInvitePreviewLoadingIds((previous) => ({
            ...previous,
            [invite.id]: true,
        }));
        try {
            const preview = await getBookInvitePreview({
                recipientEmail: invite.recipientEmail,
                recipientName: invite.recipientName,
                inviteMessage: invite.inviteMessage,
                gifterDisplayName,
                gifterEmail: checkoutEmailForBookInvite,
            });
            setBookInvitePreviews((previous) => ({
                ...previous,
                [invite.id]: preview,
            }));
        } finally {
            setBookInvitePreviewLoadingIds((previous) => ({
                ...previous,
                [invite.id]: false,
            }));
        }
    };
    const recipientInputContainsDelimiters = (
        bookInviteRecipientInput.includes(',')
        || bookInviteRecipientInput.includes('\n')
    );
    const recipientInputInvalid = (
        !recipientInputContainsDelimiters
        && bookInviteRecipientInput.trim().length > 0
        && !validateEmail(normalizedBookInviteRecipientInput)
    );
    const hasOwnEmailBookInviteConflict = (
        assignBookInviteGiftsNow
        && effectiveAssignOneBookInviteToSelf
        && normalizedCheckoutEmail.length > 0
        && bookInvites.some((invite) => (
            normalizeEmail(invite.recipientEmail) === normalizedCheckoutEmail
        ))
    );
    const bookInviteInvalidRecipientEmailSet = useMemo(() => (
        new Set(bookInviteRecipientEligibilityInvalidEmails.map(normalizeEmail))
    ), [bookInviteRecipientEligibilityInvalidEmails]);
    let bookInviteValidationMessage = '';
    if (assignBookInviteGiftsNow) {
        bookInviteValidationMessage = bookInviteRecipientInputError.length > 0
            ? bookInviteRecipientInputError
            : bookInviteRecipientEligibilityError;
    }
    if (assignBookInviteGiftsNow && hasOwnEmailBookInviteConflict) {
        bookInviteValidationMessage = BOOK_INVITE_RECIPIENT_INPUT_SELF_EMAIL_CONFLICT_ERROR;
    } else if (
        assignBookInviteGiftsNow
        && bookInviteValidationMessage.length === 0
        && recipientInputInvalid
    ) {
        bookInviteValidationMessage = BOOK_INVITE_RECIPIENT_INPUT_INVALID_EMAIL_ERROR;
    }

    const getBookInviteRecipientEmailValidationError = (
        normalizedRecipientEmail: string,
        recipientInvites: IBookInviteWithLocalId[],
    ): string | undefined => {
        if (!validateEmail(normalizedRecipientEmail)) {
            return BOOK_INVITE_RECIPIENT_INPUT_INVALID_EMAIL_ERROR;
        }
        if (
            effectiveAssignOneBookInviteToSelf
            && normalizedCheckoutEmail.length > 0
            && normalizedRecipientEmail === normalizedCheckoutEmail
        ) {
            return BOOK_INVITE_RECIPIENT_INPUT_ASSIGNED_TO_SELF_ERROR;
        }
        if (
            recipientInvites.some((invite) => (
                normalizeEmail(invite.recipientEmail) === normalizedRecipientEmail
            ))
        ) {
            return BOOK_INVITE_RECIPIENT_INPUT_DUPLICATE_EMAIL_ERROR;
        }
        if (recipientInvites.length >= maxInviteAssignments) {
            return BOOK_INVITE_RECIPIENT_INPUT_NO_MORE_ASSIGNMENTS_ERROR;
        }

        return undefined;
    };

    const addBookInvitesByEmail = (
        rawEmails: string[],
    ): {
        addedCount: number,
        rejectedEmails: string[],
    } => {
        const nextInvites: IBookInviteWithLocalId[] = [...bookInvites];
        let addedCount = 0;
        let firstError = '';
        const rejectedEmails: string[] = [];

        rawEmails.forEach((rawEmail: string) => {
            const trimmedRawEmail = rawEmail.trim();
            const normalizedRecipientEmail = normalizeEmail(trimmedRawEmail);
            if (normalizedRecipientEmail.length === 0) return;

            const validationError = getBookInviteRecipientEmailValidationError(
                normalizedRecipientEmail,
                nextInvites,
            );
            if (validationError) {
                if (firstError.length === 0) {
                    firstError = validationError;
                }
                rejectedEmails.push(trimmedRawEmail);
                return;
            }

            nextBookInviteIdRef.current += 1;
            nextInvites.push({
                id: `book-invite-gift-${nextBookInviteIdRef.current}`,
                recipientEmail: normalizedRecipientEmail,
                recipientName: '',
                inviteMessage: '',
            });
            addedCount += 1;
        });

        if (addedCount > 0) {
            setBookInvites(nextInvites);
        }
        setBookInviteRecipientInputError(firstError);
        if (bookInviteRecipientEligibilityError.length > 0) {
            setBookInviteRecipientEligibilityError('');
        }
        if (bookInviteRecipientEligibilityInvalidEmails.length > 0) {
            setBookInviteRecipientEligibilityInvalidEmails([]);
        }

        return {
            addedCount,
            rejectedEmails,
        };
    };

    const addBookInviteByEmail = (
        rawEmail: string,
    ): boolean => {
        const normalizedRecipientEmail = normalizeEmail(rawEmail);
        if (normalizedRecipientEmail.length === 0) return false;

        return addBookInvitesByEmail([normalizedRecipientEmail]).addedCount > 0;
    };

    const canAddBookInviteRecipientFromInput = (
        !recipientInputContainsDelimiters
        && remainingInviteAssignments > 0
        && bookInviteRecipientInput.trim().length > 0
        && getBookInviteRecipientEmailValidationError(
            normalizedBookInviteRecipientInput,
            bookInvites,
        ) == null
    );

    const onAddBookInviteRecipientFromInput = (): void => {
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickAddGiftRecipientButton,
                sessionId: currentSessionId,
            });
        }
        const trimmedInput = bookInviteRecipientInput.trim();
        if (trimmedInput.length === 0) return;
        const added = addBookInviteByEmail(trimmedInput);
        if (added) {
            setBookInviteRecipientInput('');
            setBookInviteRecipientInputRenderKey((previous: number) => previous + 1);
        }
    };

    const onBookInviteRecipientInputChange = (value: string): void => {
        if (bookInviteRecipientEligibilityError.length > 0) {
            setBookInviteRecipientEligibilityError('');
        }
        if (bookInviteRecipientEligibilityInvalidEmails.length > 0) {
            setBookInviteRecipientEligibilityInvalidEmails([]);
        }
        if (value.includes(',') || value.includes('\n')) {
            const parsedValues = value
                .split(/[\n,]/)
                .map((entry) => entry.trim())
                .filter((entry) => entry.length > 0);

            if (parsedValues.length > 0) {
                const validEmails: string[] = [];
                const invalidEmails: string[] = [];

                parsedValues.forEach((email) => {
                    const normalizedEmail = normalizeEmail(email);
                    if (validateEmail(normalizedEmail)) {
                        validEmails.push(normalizedEmail);
                    } else {
                        invalidEmails.push(email);
                    }
                });

                if (validEmails.length > 0) {
                    addBookInvitesByEmail(validEmails);
                } else if (bookInviteRecipientInputError.length > 0) {
                    setBookInviteRecipientInputError('');
                }

                setBookInviteRecipientInput(invalidEmails.join(', '));
                setBookInviteRecipientInputRenderKey((previous: number) => previous + 1);
            } else if (bookInviteRecipientInputError.length > 0) {
                setBookInviteRecipientInputError('');
                setBookInviteRecipientInput('');
                setBookInviteRecipientInputRenderKey((previous: number) => previous + 1);
            } else {
                setBookInviteRecipientInput('');
                setBookInviteRecipientInputRenderKey((previous: number) => previous + 1);
            }
            return;
        }

        if (bookInviteRecipientInputError.length > 0) {
            setBookInviteRecipientInputError('');
        }
        setBookInviteRecipientInput(value);
    };

    const onRemoveBookInvite = (inviteId: string): void => {
        setBookInvites(bookInvites.filter((invite) => invite.id !== inviteId));
        if (bookInvitePreviewInviteId === inviteId) {
            setBookInvitePreviewInviteId(null);
        }
        setBookInvitePreviews((previous) => {
            const next = { ...previous };
            delete next[inviteId];
            return next;
        });
        setBookInvitePreviewLoadingIds((previous) => {
            const next = { ...previous };
            delete next[inviteId];
            return next;
        });
        if (bookInviteRecipientInputError.length > 0) {
            setBookInviteRecipientInputError('');
        }
        if (bookInviteRecipientEligibilityError.length > 0) {
            setBookInviteRecipientEligibilityError('');
        }
        if (bookInviteRecipientEligibilityInvalidEmails.length > 0) {
            setBookInviteRecipientEligibilityInvalidEmails([]);
        }
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.removeGiftRecipient,
                sessionId: currentSessionId,
            });
        }
    };

    const onBookInviteFieldChange = (
        inviteId: string,
        key: BOOK_INVITE_FIELD_KEY,
        value: string,
    ): void => {
        setBookInvites(bookInvites.map((invite) => (
            invite.id === inviteId
                ? {
                    ...invite,
                    [key]: value,
                }
                : invite
        )));
        // Keep preview reactive to local edits immediately.
        setBookInvitePreviews((previous) => {
            const targetInvite = bookInvites.find((invite) => invite.id === inviteId);
            if (!targetInvite) return previous;
            return {
                ...previous,
                [inviteId]: buildLocalBookInvitePreview({
                    ...targetInvite,
                    [key]: value,
                }),
            };
        });
    };

    const onBookInviteSectionToggle = (): void => {
        setBookInviteSectionExpanded(!bookInviteSectionExpanded);
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.toggleAssignGiftWebBookInvites,
                sessionId: currentSessionId,
            });
        }
    };
    const onToggleBookInvitePreview = (invite: IBookInviteWithLocalId): void => {
        const nextVisible = bookInvitePreviewInviteId !== invite.id;
        setBookInvitePreviewInviteId((currentId) => (currentId === invite.id ? null : invite.id));
        if (nextVisible) {
            if (
                currentSessionId
                && !bookInvitePreviewOpenedIds.has(invite.id)
            ) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.viewCheckoutBookInviteCreateEmailPreview,
                    sessionId: currentSessionId,
                    payload: {
                        inviteId: invite.id,
                    },
                });
                setBookInvitePreviewOpenedIds((previous) => {
                    const next = new Set(previous);
                    next.add(invite.id);
                    return next;
                });
            }
            fetchBookInvitePreview(invite).catch(() => undefined);
        }
    };
    useEffect(() => {
        setBookInvitePreviewOpenedIds(new Set<string>());
    }, [currentSessionId]);
    useEffect(() => {
        // Rehydrate cached previews when gifter identity changes.
        if (Object.keys(bookInvitePreviews).length === 0) return;
        setBookInvitePreviews((previous) => {
            const next = { ...previous };
            bookInvites.forEach((invite) => {
                if (next[invite.id]) {
                    next[invite.id] = buildLocalBookInvitePreview(invite);
                }
            });
            return next;
        });
    }, [
        gifterDisplayName,
        bookInvites,
        buildLocalBookInvitePreview,
    ]);
    useEffect(() => {
        if (
            buyerHasActiveWebBookAccess
            && assignOneBookInviteToSelf
        ) {
            setAssignOneBookInviteToSelf(false);
        }
    }, [
        buyerHasActiveWebBookAccess,
        assignOneBookInviteToSelf,
    ]);
    const onAssignOneBookInviteToSelfToggle = (checked: boolean): void => {
        if (buyerHasActiveWebBookAccess) {
            setAssignOneBookInviteToSelf(false);
            return;
        }
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickAssignOneBookInviteToSelfToggle,
                sessionId: currentSessionId,
            });
        }
        if (
            checked
            && activeBookInviteCount >= bookInvitesInOrderCount
        ) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: BOOK_INVITE_RECIPIENT_INPUT_SELF_ASSIGNMENT_EXHAUSTED_ERROR,
                icon: PixelCartIcon,
                hasFailure: true,
            });
            return;
        }

        setAssignOneBookInviteToSelf(checked);
        if (bookInviteRecipientInputError.length > 0) {
            setBookInviteRecipientInputError('');
        }
        if (bookInviteRecipientEligibilityError.length > 0) {
            setBookInviteRecipientEligibilityError('');
        }
        if (bookInviteRecipientEligibilityInvalidEmails.length > 0) {
            setBookInviteRecipientEligibilityInvalidEmails([]);
        }
    };
    const onAssignOthersNow = (): void => {
        setAssignBookInviteGiftsNow(true);
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickAssignOthersNowButton,
                sessionId: currentSessionId,
            });
        }
    };
    const onDoThisLater = (): void => {
        setAssignBookInviteGiftsNow(false);
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickDoThisLaterButton,
                sessionId: currentSessionId,
            });
        }
    };
    const getStateFieldLabel = (countryCode?: string): string => {
        if (!countryCode) return 'State / Province';
        return COUNTRY_SUBDIVISION_LABEL_BY_CODE2[countryCode] || 'State / Province';
    };
    const parsePhoneForCountry = (
        rawPhone: string,
        countryCode?: string,
    ): ReturnType<typeof parsePhoneNumberFromString> => {
        const normalizedPhone = rawPhone.trim();
        const phoneWithPrefix = normalizedPhone.length > 2 && normalizedPhone.substring(0, 2) === '00'
            ? `+${normalizedPhone.substring(2)}`
            : normalizedPhone;
        if (phoneWithPrefix.length === 0) {
            return undefined;
        }
        const hasInternationalPrefix = phoneWithPrefix.startsWith('+');
        let parsedPhone: ReturnType<typeof parsePhoneNumberFromString>;
        if (hasInternationalPrefix) {
            parsedPhone = parsePhoneNumberFromString(phoneWithPrefix);
        } else if (countryCode) {
            parsedPhone = parsePhoneNumberFromString(phoneWithPrefix, countryCode as CountryCode);
        } else {
            parsedPhone = parsePhoneNumberFromString(phoneWithPrefix);
        }
        if (
            parsedPhone
            && countryCode
            && parsedPhone.country
            && parsedPhone.country !== countryCode
        ) {
            return undefined;
        }
        return parsedPhone;
    };
    const isCheckoutPhoneLengthValid = (
        parsedPhone: ReturnType<typeof parsePhoneNumberFromString>,
    ): boolean => !!parsedPhone && parsedPhone.nationalNumber.length >= CHECKOUT_MIN_PHONE_NSN_LENGTH;
    const isPhoneValidForCountry = (
        rawPhone: string,
        countryCode?: string,
    ): boolean => {
        if (!countryCode) return false;
        const parsedPhone = parsePhoneForCountry(rawPhone, countryCode);
        return !!parsedPhone && parsedPhone.isValid() && isCheckoutPhoneLengthValid(parsedPhone);
    };
    const getFormattedPhoneForCountry = (
        rawPhone: string,
        countryCode?: string,
    ): string | undefined => {
        if (!countryCode) return undefined;
        const parsedPhone = parsePhoneForCountry(rawPhone, countryCode);
        return parsedPhone && parsedPhone.isValid() && isCheckoutPhoneLengthValid(parsedPhone)
            ? parsedPhone.number
            : undefined;
    };
    const isCheckoutEmailUnused = useCallback(async (email: string): Promise<boolean> => {
        if (!auth.currentUser?.isAnonymous) {
            return true;
        }
        return isEmailUnusedByOtherUsers({
            test: isTestMode(),
            email,
        });
    }, []);
    useEffect(() => {
        lastAutosavedCheckoutEmailRef.current = '';
        checkoutEmailAutosaveInFlightEmailRef.current = '';
    }, [stripePaymentIntentId]);
    useEffect(() => {
        let cancelled = false;
        const email = normalizedCheckoutEmail;
        const checkoutAlreadyHasAuthenticatedEmail = !!(userEmail || auth.currentUser?.email);
        if (
            checkoutAlreadyHasAuthenticatedEmail
            || !stripePaymentIntentId
            || !email
            || !validateEmail(email)
            || email === lastAutosavedCheckoutEmailRef.current
            || email === checkoutEmailAutosaveInFlightEmailRef.current
        ) {
            return () => {
                cancelled = true;
            };
        }

        /**
         * Saves an anonymous checkout email as soon as the current typed value is
         * valid and unused, so abandonment follow-ups can use the existing session.
         */
        const autosaveCheckoutEmail = async (): Promise<void> => {
            checkoutEmailAutosaveInFlightEmailRef.current = email;
            try {
                const emailIsUnused = email === validatedAnonymousCheckoutEmail
                    || await isCheckoutEmailUnused(email);
                if (cancelled) return;
                if (!emailIsUnused) {
                    if (validatedAnonymousCheckoutEmail === email) {
                        setValidatedAnonymousCheckoutEmail(null);
                    }
                    return;
                }
                if (auth.currentUser?.isAnonymous && validatedAnonymousCheckoutEmail !== email) {
                    setValidatedAnonymousCheckoutEmail(email);
                }
                await stripeUpdatePaymentIntent({
                    test: isTestMode(),
                    paymentIntentId: stripePaymentIntentId,
                    updateMode: STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.lightweight,
                    userEmail: email,
                });
                if (!cancelled) {
                    lastAutosavedCheckoutEmailRef.current = email;
                }
            } catch (error) {
                if (cancelled || !currentSessionId) return;
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: `Checkout email autosave failed: ${(error as Error).message}`,
                    },
                });
            } finally {
                if (checkoutEmailAutosaveInFlightEmailRef.current === email) {
                    checkoutEmailAutosaveInFlightEmailRef.current = '';
                }
            }
        };

        autosaveCheckoutEmail();
        return () => {
            cancelled = true;
        };
    }, [
        currentSessionId,
        isCheckoutEmailUnused,
        normalizedCheckoutEmail,
        stripePaymentIntentId,
        userEmail,
        validatedAnonymousCheckoutEmail,
    ]);
    const validateBookInviteGiftRecipientsForCheckout = async (): Promise<boolean> => {
        if (!assignBookInviteGiftsNow || bookInvites.length === 0) {
            if (bookInviteRecipientEligibilityError.length > 0) {
                setBookInviteRecipientEligibilityError('');
            }
            if (bookInviteRecipientEligibilityInvalidEmails.length > 0) {
                setBookInviteRecipientEligibilityInvalidEmails([]);
            }
            return true;
        }

        const uniqueRecipientEmails = Array.from(new Set(
            bookInvites
                .map((invite) => normalizeEmail(invite.recipientEmail))
                .filter((email) => validateEmail(email)),
        ));
        if (uniqueRecipientEmails.length === 0) {
            if (bookInviteRecipientEligibilityError.length > 0) {
                setBookInviteRecipientEligibilityError('');
            }
            if (bookInviteRecipientEligibilityInvalidEmails.length > 0) {
                setBookInviteRecipientEligibilityInvalidEmails([]);
            }
            return true;
        }

        setActionButtonIsLoading(true);
        setActionButtonLoadingMessage('Checking gift recipients');
        try {
            const eligibilityItems = await getBookInviteRecipientEligibility({
                test: isTestMode(),
                emails: uniqueRecipientEmails,
            });
            const ineligibleRecipientEmails = eligibilityItems
                .filter((item: IBookInviteRecipientEligibilityItem) => item.hasActiveWebBookAccess)
                .map((item: IBookInviteRecipientEligibilityItem) => item.normalizedEmail);
            if (ineligibleRecipientEmails.length > 0) {
                setAssignBookInviteGiftsNow(true);
                setBookInviteSectionExpanded(true);
                if (bookInviteRecipientEligibilityInvalidEmails.length > 0) {
                    setBookInviteRecipientEligibilityInvalidEmails([]);
                }
                setBookInviteRecipientEligibilityError(
                    `${BOOK_INVITE_RECIPIENT_HAS_ACCESS_ERROR_PREFIX} ${
                        ineligibleRecipientEmails.join(', ')
                    }. ${BOOK_INVITE_RECIPIENT_HAS_ACCESS_ERROR_SUFFIX}`,
                );
                return false;
            }

            if (bookInviteRecipientEligibilityError.length > 0) {
                setBookInviteRecipientEligibilityError('');
            }
            if (bookInviteRecipientEligibilityInvalidEmails.length > 0) {
                setBookInviteRecipientEligibilityInvalidEmails([]);
            }
            return true;
        } catch (error) {
            const eligibilityError = error as {
                code?: string,
                message?: string,
                details?: unknown,
            };
            const invalidArgumentError = eligibilityError?.code === FIREBASE_CALLABLE_ERROR_CODE.invalidArgument;
            const invalidRecipientEmails = invalidArgumentError
                ? getInvalidRecipientEmailsFromEligibilityError(
                    eligibilityError?.details,
                )
                : [];
            const parsedInvalidArgumentMessage = getInvalidArgumentEligibilityMessage(
                eligibilityError?.details,
            );
            setAssignBookInviteGiftsNow(true);
            setBookInviteSectionExpanded(true);
            setBookInviteRecipientEligibilityInvalidEmails(invalidRecipientEmails);
            setBookInviteRecipientEligibilityError(
                invalidArgumentError
                    ? parsedInvalidArgumentMessage
                    : BOOK_INVITE_RECIPIENT_ELIGIBILITY_LOOKUP_ERROR,
            );
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: `Book invite recipient eligibility check failed: ${eligibilityError?.message || String(error)}`,
                    },
                });
            }
            return false;
        } finally {
            setActionButtonIsLoading(false);
            setActionButtonLoadingMessage('');
        }
    };
    const paymentMethodSupportsSave = (paymentMethodType?: string): boolean => (
        !!paymentMethodType && (
            Object.values(STRIPE_PAYMENT_METHOD_TYPES_SUPPORTING_SAVE) as string[]
        ).includes(paymentMethodType)
    );
    // PaymentElement doesn't provide a method type until the user interacts with it.
    // Keep the "save payment method" checkbox visible in that initial state.
    const savePaymentMethodAllowedBySelection = (
        !selectedSavedStripePaymentMethod
        && (
            stripePaymentMethodType == null
            || paymentMethodSupportsSave(stripePaymentMethodType)
        )
    );
    // Final guard: even if the checkbox was previously selected, do not persist/save
    // when the active method type doesn't support Stripe's save_payment_method flow.
    const effectiveSavePaymentMethod = (
        savePaymentMethod && savePaymentMethodAllowedBySelection
    );

    const onActionButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.checkoutActionButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onActionButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onBackButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.checkoutBackButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onBackButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onLinkEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.linkButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onTermsOfUseLinkEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.termsOfUseButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onLinkLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onOpenBookInvitesProfile = (): void => {
        const nextSearchParams = new URLSearchParams(location.search);
        nextSearchParams.set(BOOK_INVITE_PROFILE_QUERY_PARAM_OPEN_PROFILE, 'true');
        nextSearchParams.set(
            BOOK_INVITE_PROFILE_QUERY_PARAM_PANEL,
            BOOK_INVITE_PROFILE_PANEL_BOOK_INVITES,
        );
        setIsUserProfileDialogExpanded(true);
        navigate({
            pathname: location.pathname,
            search: `?${nextSearchParams.toString()}`,
            hash: location.hash,
        }, {
            replace: true,
        });
    };

    const onSelectSavedAddress = (address: IDropdownItem | undefined): void => {
        setSelectedSavedAddress(address);

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.selectSavedMailingAddress,
                sessionId: currentSessionId,
            });
        }

        if (address) {
            // Auto-populate address form
            const selectedAddress = savedAddresses[parseInt(address.value, 10)];
            setCustomerFirstName(selectedAddress.firstName);
            setCustomerLastName(selectedAddress.lastName);
            if (selectedAddress.company) {
                setCustomerCompany(selectedAddress.company);
            } else if (customerCompany.length > 0) {
                setCustomerCompany('');
            }
            setCustomerStreetOne(selectedAddress.street1);
            if (selectedAddress.street2 && selectedAddress.street2.length > 0) {
                setCustomerStreetTwo(selectedAddress.street2);
            } else if (customerStreetTwo.length > 0) {
                setCustomerStreetTwo('');
            }
            setCustomerCity(selectedAddress.city);
            // Find country
            const countryItem = countriesJSON
                .find((item: any) => item.code2 === selectedAddress.country) as ICountry;
            setCustomerCountry({
                value: countryItem.code2,
                name: countryItem.name,
            });
            const stateArr: IDropdownItem[] = (countryItem.states as ICountryState[]).map((state: ICountryState) => ({
                value: state.code,
                name: state.name,
            }));
            setStates(stateArr);
            // Find and select state if the saved address has one.
            if (selectedAddress.state) {
                const stateMap: Map<string, IDropdownItem> = new Map(
                    stateArr.map((state: IDropdownItem) => ([
                        state.value,
                        state,
                    ])),
                );
                setCustomerState(stateMap.get(selectedAddress.state));
            } else if (customerState != null) {
                setCustomerState(undefined);
            }
            setCustomerZipCode(selectedAddress.zip);
            if (selectedAddress.phone) setCustomerPhone(selectedAddress.phone);
        } else {
            setCustomerFirstName('');
            setCustomerLastName('');
            setCustomerCompany('');
            setCustomerStreetOne('');
            setCustomerStreetTwo('');
            setCustomerCity('');
            setCustomerState(undefined);
            setCustomerCountry(undefined);
            setStates([]);
            setCustomerZipCode('');
            setCustomerPhone('');
        }
    };

    const onSelectSavedStripePaymentMethod = (paymentMethod: IDropdownItem | undefined): void => {
        setSelectedSavedStripePaymentMethod(paymentMethod);
        setStripePaymentDetailsCompleted(!!paymentMethod);

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.selectSavedStripePaymentMethod,
                sessionId: currentSessionId,
            });
        }
    };

    const onSelectCountry = (country: IDropdownItem | undefined): void => {
        setCustomerCountry(country);

        if (country) {
            // Find country
            const countryItem = countriesJSON
                .find((item: any) => item.code2 === country.value) as ICountry;

            // Change country states
            const stateArr: IDropdownItem[] = (countryItem.states as ICountryState[]).map((state: ICountryState) => ({
                value: state.code,
                name: state.name,
            }));
            setStates(stateArr);
            setCustomerState(undefined);
        }
    };

    const onSelectState = (state: IDropdownItem | undefined): void => {
        setCustomerState(state);
    };

    const onSelectSavedBillingAddress = (address: IDropdownItem | undefined): void => {
        setSelectedSavedBillingAddress(address);

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.selectSavedBillingAddress,
                sessionId: currentSessionId,
            });
        }

        if (address) {
            // Auto-populate address form
            const selectedAddress = savedAddresses[parseInt(address.value, 10)];
            setCustomerBillingFirstName(selectedAddress.firstName);
            setCustomerBillingLastName(selectedAddress.lastName);
            if (selectedAddress.company) {
                setCustomerBillingCompany(selectedAddress.company);
            } else if (customerBillingCompany.length > 0) {
                setCustomerBillingCompany('');
            }
            setCustomerBillingStreetOne(selectedAddress.street1);
            if (selectedAddress.street2 && selectedAddress.street2.length > 0) {
                setCustomerBillingStreetTwo(selectedAddress.street2);
            } else if (customerBillingStreetTwo.length > 0) {
                setCustomerBillingStreetTwo('');
            }
            setCustomerBillingCity(selectedAddress.city);
            // Find country
            const countryItem = countriesJSON
                .find((item: any) => item.code2 === selectedAddress.country) as ICountry;
            setCustomerBillingCountry({
                value: countryItem.code2,
                name: countryItem.name,
            });
            const stateArr: IDropdownItem[] = (countryItem.states as ICountryState[]).map((state: ICountryState) => ({
                value: state.code,
                name: state.name,
            }));
            setBillingStates(stateArr);
            // Find and select state if the saved address has one.
            if (selectedAddress.state) {
                const stateMap: Map<string, IDropdownItem> = new Map(
                    stateArr.map((state: IDropdownItem) => ([
                        state.value,
                        state,
                    ])),
                );
                setCustomerBillingState(stateMap.get(selectedAddress.state));
            } else {
                setCustomerBillingState(undefined);
            }
            setCustomerBillingZipCode(selectedAddress.zip);
            if (selectedAddress.phone) setCustomerBillingPhone(selectedAddress.phone);
        } else {
            setCustomerBillingFirstName('');
            setCustomerBillingLastName('');
            setCustomerBillingCompany('');
            setCustomerBillingStreetOne('');
            setCustomerBillingStreetTwo('');
            setCustomerBillingCity('');
            setCustomerBillingState(undefined);
            setCustomerBillingCountry(undefined);
            setBillingStates([]);
            setCustomerBillingZipCode('');
            setCustomerBillingPhone('');
        }
    };

    const onSelectBillingCountry = (country: IDropdownItem | undefined): void => {
        setCustomerBillingCountry(country);

        if (country) {
            // Find country
            const countryItem = countriesJSON
                .find((item: any) => item.code2 === country.value) as ICountry;

            // Change country states
            const stateArr: IDropdownItem[] = (countryItem.states as ICountryState[]).map((state: ICountryState) => ({
                value: state.code,
                name: state.name,
            }));
            setBillingStates(stateArr);
            setCustomerBillingState(undefined);
        }
    };

    const onSelectBillingState = (state: IDropdownItem | undefined): void => {
        setCustomerBillingState(state);
    };

    const onReviewEditButtonClick = (
        newCheckpoint: CHECKOUT_CHECKPOINT,
        e: React.MouseEvent,
        options?: {
            focusBookInvitesSection?: boolean,
        },
    ): void => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickEditCheckoutDetailsButton,
                sessionId: currentSessionId,
                payload: {
                    to: newCheckpoint,
                },
            });
        }
        navigate(
            `/${PAGE_ROUTE.checkout}/${newCheckpoint}`,
            {
                state: {
                    prevPath: location.pathname,
                    ...(options?.focusBookInvitesSection
                        ? {
                            focusBookInvitesSection: true,
                        }
                        : {}),
                },
            },
        );
    };
    const onReviewBookInvitesEditButtonClick = (e: React.MouseEvent): void => {
        const inviteCheckpoint = shouldRenderBookInviteSectionOnInfo
            ? CHECKOUT_CHECKPOINT.info
            : CHECKOUT_CHECKPOINT.billing;
        onReviewEditButtonClick(
            inviteCheckpoint,
            e,
            {
                focusBookInvitesSection: true,
            },
        );
    };

    const handleActionButtonMessageEllipsisText = (): void => {
        const currentEllipsis = actionButtonMessageEllipsis;
        const currentNumDots = currentEllipsis.split('.').length - 1;
        let updatedNumDots;
        if (currentNumDots === 3) {
            updatedNumDots = 0;
        } else {
            updatedNumDots = currentNumDots + 1;
        }

        setActionButtonMessageEllipsis(`${'.'.repeat(updatedNumDots)}`);
    };

    useEffect(() => {
        if (!shouldFocusBookInvitesSectionFromNavigation) return;
        const shouldFocusOnCurrentCheckpoint = (
            (checkpoint === CHECKOUT_CHECKPOINT.info && shouldRenderBookInviteSectionOnInfo)
            || (checkpoint === CHECKOUT_CHECKPOINT.billing && shouldRenderBookInviteSectionOnBilling)
        );
        if (!shouldFocusOnCurrentCheckpoint) return;

        setBookInviteSectionExpanded(true);
        const timeoutId = window.setTimeout(() => {
            bookInviteSectionRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [
        shouldFocusBookInvitesSectionFromNavigation,
        checkpoint,
        shouldRenderBookInviteSectionOnInfo,
        shouldRenderBookInviteSectionOnBilling,
    ]);

    const onBackButtonClick = (): void => {
        // Play Sound
        if (hasSound && swooshOutClip.current) {
            swooshOutClip.current.pause();
            swooshOutClip.current.currentTime = 0;
            playAudio(swooshOutClip.current);
        }

        let url: string | undefined;
        let toCheckpoint: CHECKOUT_CHECKPOINT | undefined;
        switch (checkpoint) {
        case CHECKOUT_CHECKPOINT.info:
            url = `/${PAGE_ROUTE.cart}`;
            break;
        case CHECKOUT_CHECKPOINT.shipping:
            url =  `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.info}`;
            toCheckpoint = CHECKOUT_CHECKPOINT.info;
            break;
        case CHECKOUT_CHECKPOINT.billing:
            if (physicalItemsInCart.length > 0) {
                url = `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.shipping}`;
                toCheckpoint = CHECKOUT_CHECKPOINT.shipping;
            } else {
                url = `/${PAGE_ROUTE.cart}`;
            }
            break;
        case CHECKOUT_CHECKPOINT.review:
            url = `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.billing}`;
            toCheckpoint = CHECKOUT_CHECKPOINT.billing;
            break;
        default:
            // CHECKOUT_CHECKPOINT.confirmation
            // Do nothing
            // Should not allow to go back
        }
        if (
            currentSessionId
            && toCheckpoint
        ) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickCheckoutBackButton,
                sessionId: currentSessionId,
                payload: {
                    to: toCheckpoint,
                },
            });
        }
        if (url) {
            navigate(
                url,
                {
                    state: {
                        prevPath: location.pathname,
                    },
                },
            );
        }
    };

    useEffect(() => {
        const email = userEmail || auth.currentUser?.email || '';
        if (email.length > 0 && checkoutEmail !== email) {
            setCheckoutEmail(email);
        }
    }, [
        checkoutEmail,
        userEmail,
        user?.id,
    ]);

    const getTaxCalculationErrorReason = (
        error: unknown,
    ): TAX_CALCULATION_ERROR_REASON | undefined => {
        const err = error as {
            code?: string,
            message?: string,
            details?: {
                reason?: string,
            },
        };
        const message = `${err?.message || ''}`.toLowerCase();
        if (
            err?.details?.reason === TAX_CALCULATION_ERROR_REASON.taxAddressInvalid
            || (
                err?.code === FIREBASE_CALLABLE_ERROR_CODE.invalidArgument
                && message.includes('checkout/tax-address-invalid')
            )
        ) {
            return TAX_CALCULATION_ERROR_REASON.taxAddressInvalid;
        }
        if (err?.details?.reason === TAX_CALCULATION_ERROR_REASON.taxInputInvalid) {
            return TAX_CALCULATION_ERROR_REASON.taxInputInvalid;
        }
        if (err?.details?.reason === TAX_CALCULATION_ERROR_REASON.taxServiceUnavailable) {
            return TAX_CALCULATION_ERROR_REASON.taxServiceUnavailable;
        }
        return undefined;
    };
    const showTaxCalculationErrorSnackbar = (
        reason: TAX_CALCULATION_ERROR_REASON,
    ): void => {
        let message = SNACKBAR_MESSAGE_TAX_ADDRESS_NOT_USABLE_ERROR;
        let failureTelemetryType = FAILURE_TELEMETRY_TYPE.userCorrectable;
        if (reason === TAX_CALCULATION_ERROR_REASON.taxInputInvalid) {
            message = SNACKBAR_MESSAGE_TAX_INPUT_INVALID_ERROR;
        } else if (reason === TAX_CALCULATION_ERROR_REASON.taxServiceUnavailable) {
            message = SNACKBAR_MESSAGE_TAX_SERVICE_UNAVAILABLE_ERROR;
            failureTelemetryType = FAILURE_TELEMETRY_TYPE.unexpectedError;
        }
        setSnackbarData({
            visible: true,
            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            text: message,
            icon: PixelCartIcon,
            hasFailure: true,
            failureTelemetryType,
        });
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.error,
                sessionId: currentSessionId,
                payload: {
                    error: message,
                    failureTelemetryType,
                },
            });
        }
    };
    /**
     * Uses failure styling for user-correctable checkout guidance without
     * escalating those messages as crashes in the daily digest.
     */
    const showUserCorrectableFailureSnackbar = ({
        text,
        duration = DEFAULT_SNACKBAR_VISIBLE_DURATION,
        icon = PixelCartIcon,
    }: {
        text: string,
        duration?: number,
        icon?: string,
    }): void => {
        setSnackbarData({
            visible: true,
            duration,
            text,
            icon,
            hasFailure: true,
            failureTelemetryType: FAILURE_TELEMETRY_TYPE.userCorrectable,
        });
    };
    const handleCheckoutPaymentIntentUpdateOutcome = (data: {
        snackbarText: string,
        hasFailure: boolean,
        stopStripePaymentLoading?: boolean,
        stopActionButtonLoading?: boolean,
        navigateToConfirmation?: boolean,
        userActionError?: string,
    }): void => {
        // Shared terminal UI path for both success-recovery and failure outcomes
        // after payment-intent update attempts.
        const {
            snackbarText,
            hasFailure,
            stopStripePaymentLoading,
            stopActionButtonLoading,
            navigateToConfirmation,
            userActionError,
        } = data;
        if (stopStripePaymentLoading) {
            setStripePaymentIsLoading(false);
        }
        if (stopActionButtonLoading) {
            setActionButtonIsLoading(false);
        }
        setActionButtonLoadingMessage('');
        setSnackbarData({
            visible: true,
            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            text: snackbarText,
            icon: PixelCartIcon,
            hasFailure,
        });
        if (userActionError && currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.error,
                sessionId: currentSessionId,
                payload: {
                    error: userActionError,
                },
            });
        }
        if (navigateToConfirmation) {
            navigate(
                `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.confirmation}`,
                {
                    state: {
                        prevPath: location.pathname,
                    },
                },
            );
        }
    };
    const handleCheckoutPaymentIntentUpdateError = (data: {
        error: unknown,
        snackbarText: string,
        userActionContext: string,
        stopStripePaymentLoading?: boolean,
        stopActionButtonLoading?: boolean,
        skipTaxErrorMapping?: boolean,
    }): CHECKOUT_PAYMENT_INTENT_UPDATE_ERROR_RESULT => {
        const {
            error,
            snackbarText,
            userActionContext,
            stopStripePaymentLoading,
            stopActionButtonLoading,
            skipTaxErrorMapping,
        } = data;
        const checkoutUpdateError = error as {
            code?: string,
            details?: unknown,
        };
        if (checkoutUpdateError?.code === FIREBASE_CALLABLE_ERROR_CODE.invalidArgument) {
            const parsedInvalidArgumentMessage = getInvalidArgumentEligibilityMessage(
                checkoutUpdateError?.details,
            );
            const invalidRecipientEmails = getInvalidRecipientEmailsFromEligibilityError(
                checkoutUpdateError?.details,
            );
            if (
                invalidRecipientEmails.length > 0
                || parsedInvalidArgumentMessage !== BOOK_INVITE_RECIPIENT_ELIGIBILITY_INVALID_EMAIL_ERROR
            ) {
                setAssignBookInviteGiftsNow(true);
                setBookInviteSectionExpanded(true);
                setBookInviteRecipientInputError('');
                setBookInviteRecipientEligibilityInvalidEmails(invalidRecipientEmails);
                setBookInviteRecipientEligibilityError(parsedInvalidArgumentMessage);
                if (stopStripePaymentLoading) {
                    setStripePaymentIsLoading(false);
                }
                if (stopActionButtonLoading) {
                    setActionButtonIsLoading(false);
                }
                setActionButtonLoadingMessage('');
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: `${userActionContext}: ${parsedInvalidArgumentMessage}${
                                invalidRecipientEmails.length > 0
                                    ? ` (invalid recipient emails: ${invalidRecipientEmails.join(', ')})`
                                    : ''
                            }`,
                            failureTelemetryType: FAILURE_TELEMETRY_TYPE.userCorrectable,
                        },
                    });
                }
                return CHECKOUT_PAYMENT_INTENT_UPDATE_ERROR_RESULT.genericErrorHandled;
            }
        }
        if (!skipTaxErrorMapping) {
            const taxErrorReason = getTaxCalculationErrorReason(error);
            if (taxErrorReason) {
                if (stopStripePaymentLoading) {
                    setStripePaymentIsLoading(false);
                }
                if (stopActionButtonLoading) {
                    setActionButtonIsLoading(false);
                }
                setActionButtonLoadingMessage('');
                showTaxCalculationErrorSnackbar(taxErrorReason);
                return CHECKOUT_PAYMENT_INTENT_UPDATE_ERROR_RESULT.taxErrorHandled;
            }
        }
        const message = error instanceof Error
            ? error.message
            : String(error);
        handleCheckoutPaymentIntentUpdateOutcome({
            snackbarText,
            hasFailure: true,
            stopStripePaymentLoading,
            stopActionButtonLoading,
            userActionError: `${userActionContext}: ${message}`,
        });
        return CHECKOUT_PAYMENT_INTENT_UPDATE_ERROR_RESULT.genericErrorHandled;
    };
    const reconcileImmutableStatusSkippedUpdate = async (data: {
        paymentIntentId: string,
        checkpointLabel: CHECKOUT_CHECKPOINT,
        stopStripePaymentLoading?: boolean,
        stopActionButtonLoading?: boolean,
    }): Promise<void> => {
        const {
            paymentIntentId,
            checkpointLabel,
            stopStripePaymentLoading,
            stopActionButtonLoading,
        } = data;
        try {
            const latestPaymentIntent = await stripeGetPaymentIntent({
                test: isTestMode(),
                paymentIntentId,
            });
            if (
                latestPaymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.succeeded
                || latestPaymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.processing
            ) {
                handleCheckoutPaymentIntentUpdateOutcome({
                    snackbarText: SNACKBAR_MESSAGE_PAYMENT_ALREADY_PROCESSED,
                    hasFailure: false,
                    stopStripePaymentLoading,
                    stopActionButtonLoading,
                    navigateToConfirmation: true,
                });
                return;
            }
            handleCheckoutPaymentIntentUpdateError({
                // eslint-disable-next-line max-len
                error: new Error(`Checkout update skipped for immutable status; latest status=${latestPaymentIntent.status}.`),
                snackbarText: SNACKBAR_MESSAGE_PAYMENT_STATUS_STALE,
                // eslint-disable-next-line max-len
                userActionContext: `Checkout payment intent immutable status reconciliation failed in ${checkpointLabel}`,
                stopStripePaymentLoading,
                stopActionButtonLoading,
                skipTaxErrorMapping: true,
            });
        } catch (error) {
            handleCheckoutPaymentIntentUpdateError({
                error,
                snackbarText: SNACKBAR_MESSAGE_PAYMENT_STATUS_STALE,
                userActionContext: (
                    // eslint-disable-next-line max-len
                    `Unable to reconcile payment intent status after immutable update skip in ${checkpointLabel}`
                ),
                stopStripePaymentLoading,
                stopActionButtonLoading,
                skipTaxErrorMapping: true,
            });
        }
    };
    const onActionButtonClickLatest = useLatest({
        preparePromotionsForReview,
        finishPromotionsForReview,
        snackbarData,
        stripeTaxCalculationId,
        changeCheckoutSessionStatus,
    });
    const actionButtonClickInFlightRef = useRef(false);
    /* eslint-disable indent */
    const onActionButtonClick = async (): Promise<void> => {
        if (actionButtonClickInFlightRef.current) return;
        actionButtonClickInFlightRef.current = true;
        let shippoMailingAddressValidation: Address | null;
        let shippoBillingAddressValidation: Address | null;
        // let stripePaymentError: { error: Stripe.StripeError };
        try {
            switch (checkpoint) {
        case CHECKOUT_CHECKPOINT.info:
            if (physicalItemsInCart.length === 0) {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: `There are no items in your cart. Mailing address view should not be presented. Please contact the verascope team for support at: ${
                                SUPPORT_EMAIL_ADDRESS
                            }.`,
                        },
                    });
                }
                throw Error(
                    // eslint-disable-next-line max-len
                    `There are no items in your cart. Mailing address view should not be presented. Please contact the verascope team for support at: ${
                        SUPPORT_EMAIL_ADDRESS
                    }.`,
                );
            }

            // eslint-disable-next-line no-case-declarations
            const infoCheckoutEmailValue = getCheckoutEmail();
            if (!infoCheckoutEmailValue) {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_NO_EMAIL_ERROR,
                });
                if (!emailNotProvided) setEmailNotProvided(true);
                if (emailInvalid) setEmailInvalid(false);
                break;
            } else {
                if (emailNotProvided) setEmailNotProvided(false);
                if (!validateEmail(infoCheckoutEmailValue)) {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_INVALID_EMAIL_ERROR,
                    });
                    if (!emailInvalid) setEmailInvalid(true);
                    break;
                } else if (emailInvalid) {
                    setEmailInvalid(false);
                }
            }
            if (auth.currentUser?.isAnonymous) {
                let emailIsUnused = false;
                const normalizedInfoCheckoutEmail = normalizeEmail(infoCheckoutEmailValue);
                if (normalizedInfoCheckoutEmail === validatedAnonymousCheckoutEmail) {
                    emailIsUnused = true;
                } else {
                    setActionButtonIsLoading(true);
                    setActionButtonLoadingMessage('Validating email');
                    try {
                        emailIsUnused = await isCheckoutEmailUnused(infoCheckoutEmailValue);
                        if (!emailIsUnused) {
                            showUserCorrectableFailureSnackbar({
                                text: SNACKBAR_MESSAGE_EMAIL_IN_USE_ERROR,
                            });
                            setIsUserProfileDialogExpanded(true);
                        } else {
                            setValidatedAnonymousCheckoutEmail(normalizedInfoCheckoutEmail);
                        }
                    } catch (error) {
                        setSnackbarData({
                            visible: true,
                            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                            text: SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_ERROR,
                            icon: PixelCartIcon,
                            hasFailure: true,
                        });
                        if (currentSessionId) {
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.error,
                                sessionId: currentSessionId,
                                payload: {
                                    // eslint-disable-next-line max-len
                                    error: `Checkout email availability check failed in ${CHECKOUT_CHECKPOINT.info}: ${(error as Error).message}`,
                                },
                            });
                        }
                    } finally {
                        setActionButtonIsLoading(false);
                        setActionButtonLoadingMessage('');
                    }
                }
                if (!emailIsUnused) break;
            }
            if (!await validateBookInviteGiftRecipientsForCheckout()) {
                break;
            }

            // Validate Country
            if (!customerCountry) {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_NO_COUNTRY_ERROR,
                });
                if (!countryNotSelected) setCountryNotSelected(true);
                break;
            } else if (countryNotSelected) {
                setCountryNotSelected(false);
            }

            // Validate First Name
            if (customerFirstName.length > 0) {
                if (firstNameNotProvided) setFirstNameNotProvided(false);
            } else {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_NO_FIRST_NAME_ERROR,
                });
                if (!firstNameNotProvided) setFirstNameNotProvided(true);
                break;
            }

            // Validate Last Name
            if (customerLastName.length > 0) {
                if (lastNameNotProvided) setLastNameNotProvided(false);
            } else {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_NO_LAST_NAME_ERROR,
                });
                if (!lastNameNotProvided) setLastNameNotProvided(true);
                break;
            }

            // Validate State
            // eslint-disable-next-line no-case-declarations
            const countryItem = countriesJSON
                .find((item: any) => item.code2 === customerCountry.value) as ICountry;

            if (customerCountry
                && !customerState
                && countryItem.states.length > 0
            ) {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_NO_STATE_ERROR,
                });
                if (!stateNotSelected) setStateNotSelected(true);
                break;
            } else if (stateNotSelected) {
                setStateNotSelected(false);
            }

            // Validate City
            if (customerCity.length > 0) {
                if (cityNotProvided) setCityNotProvided(false);
            } else {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_NO_CITY_ERROR,
                });
                if (!cityNotProvided) setCityNotProvided(true);
                break;
            }

            // Validate Zip Code
            if (customerZipCode.length > 0) {
                if (zipCodeNotProvided) setZipCodeNotProvided(false);
            } else {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_NO_ZIP_CODE_ERROR,
                });
                if (!zipCodeNotProvided) setZipCodeNotProvided(true);
                break;
            }

            // Validate Street One
            if (customerStreetOne.length > 0) {
                if (streetOneNotProvided) setStreetOneNotProvided(false);
            } else {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_NO_STREET_ONE_ERROR,
                });
                if (!streetOneNotProvided) setStreetOneNotProvided(true);
                break;
            }

            // Validate Phone Number
            if (customerPhone.length > 0) {
                if (phoneNumberNotProvided) setPhoneNumberNotProvided(false);
            } else {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_NO_PHONE_NUMBER_ERROR,
                });
                if (!phoneNumberNotProvided) setPhoneNumberNotProvided(true);
                break;
            }
            if (!isPhoneValidForCountry(customerPhone, customerCountry?.value)) {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_INVALID_PHONE_NUMBER_ERROR,
                });
                break;
            }

            // Validate mailing address
            try {
                // IMPORTANT: Validation doesn't work for international addresses in testing
                // In live, international address validation is $0.06/address
                const addressObj: IShippoAddressObject = {
                    name: `${customerFirstName} ${customerLastName}`,
                    street1: customerStreetOne,
                    city: customerCity,
                    zip: customerZipCode,
                    country: customerCountry.value,
                    validate: true,
                };
                if (customerCompany.length > 0) addressObj.company = customerCompany;
                if (customerStreetTwo.length > 0) addressObj.street2 = customerStreetTwo;
                if (customerState) addressObj.state = customerState.value;
                const formattedCustomerPhone = getFormattedPhoneForCountry(
                    customerPhone,
                    customerCountry.value,
                );
                if (formattedCustomerPhone) {
                    addressObj.phone = formattedCustomerPhone;
                }
                if (infoCheckoutEmailValue) addressObj.email = infoCheckoutEmailValue;
                setActionButtonIsLoading(true);
                setActionButtonLoadingMessage('Validating address');
                if ((isTestMode()) && typeof window !== 'undefined') {
                    console.info('[shipping] shippo disabled', SHIPPO_DISABLED);
                }
                if (SHIPPO_DISABLED) {
                    shippoMailingAddressValidation = {
                        ...addressObj,
                        validationResults: {
                            isValid: true,
                        },
                    } as Address;
                } else {
                    shippoMailingAddressValidation = await shippoAddressValidation({
                        test: isTestMode(),
                        address: addressObj,
                    });
                    if ((isTestMode()) && typeof window !== 'undefined') {
                        console.info('[shipping] mailing address validation [in]', addressObj);
                        console.info('[shipping] mailing address validation [out]', shippoMailingAddressValidation);
                    }
                }
            } catch (e) {
                setActionButtonIsLoading(false);
                setActionButtonLoadingMessage('');
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_MAILING_ADDRESS_ERROR_MESSAGE((e as any).toString()),
                    icon: PixelCartIcon,
                    hasFailure: true,
                });
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: SNACKBAR_MESSAGE_MAILING_ADDRESS_ERROR_MESSAGE((e as any).toString()),
                        },
                    });
                }
                break;
            }

            if (
                stripePaymentIntentId
                && shippoMailingAddressValidation
                && shippoMailingAddressValidation.validationResults?.isValid
            ) {
                try {
                    if (!addressIsValid) setAddressIsValid(true);
                    // Update mailing address
                    const mailingAddress: IAddress = {
                        firstName: customerFirstName,
                        lastName: customerLastName,
                        street1: customerStreetOne,
                        city: customerCity,
                        zip: customerZipCode,
                        country: customerCountry.value,
                    };
                    if (customerCompany.length > 0) mailingAddress.company = customerCompany;
                    if (customerStreetTwo.length > 0) mailingAddress.street2 = customerStreetTwo;
                    if (customerState) mailingAddress.state = customerState.value;
                    const formattedCustomerPhone = getFormattedPhoneForCountry(
                        customerPhone,
                        customerCountry.value,
                    );
                    if (formattedCustomerPhone) {
                        mailingAddress.phone = formattedCustomerPhone;
                    }
                    // Update address based on Shippo
                    if (
                        shippoMailingAddressValidation.street1 != null
                        && customerStreetOne !== shippoMailingAddressValidation.street1
                    ) {
                        setCustomerStreetOne(shippoMailingAddressValidation.street1);
                        mailingAddress.street1 = shippoMailingAddressValidation.street1;
                    }
                    if (
                        customerStreetTwo.length > 0
                        && customerStreetTwo !== shippoMailingAddressValidation.street2
                    ) {
                        setCustomerStreetTwo(shippoMailingAddressValidation.street2!);
                        mailingAddress.street2 = shippoMailingAddressValidation.street2;
                        if (mailingAddress.street2 === '') delete mailingAddress.street2;
                    }
                    if (
                        shippoMailingAddressValidation.city != null
                        && customerCity !== shippoMailingAddressValidation.city
                    ) {
                        setCustomerCity(shippoMailingAddressValidation.city);
                        mailingAddress.city = shippoMailingAddressValidation.city;
                    }
                    if (
                        shippoMailingAddressValidation.zip
                        && customerZipCode !== shippoMailingAddressValidation.zip
                    ) {
                        setCustomerZipCode(shippoMailingAddressValidation.zip);
                        mailingAddress.zip = shippoMailingAddressValidation.zip;
                    }
                    if (
                        shippoMailingAddressValidation.phone
                        && shippoMailingAddressValidation.phone.length > 0
                        && customerPhone !== shippoMailingAddressValidation.phone
                    ) {
                        setCustomerPhone(shippoMailingAddressValidation.phone);
                        mailingAddress.phone = shippoMailingAddressValidation.phone;
                    } else if (
                        shippoMailingAddressValidation.phone
                        && shippoMailingAddressValidation.phone.length > 0
                        && mailingAddress.phone !== shippoMailingAddressValidation.phone
                    ) {
                        mailingAddress.phone = shippoMailingAddressValidation.phone;
                    }

                    if (stripePaymentIntentId) {
                        // Update Payment Intent
                        stripeUpdatePaymentIntent({
                            test: isTestMode(),
                            paymentIntentId: stripePaymentIntentId,
                            updateMode: STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.lightweight,
                            userEmail: infoCheckoutEmailValue,
                            mailingAddress,
                        });

                        // Change checkout status
                        changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.mailingInfoCollected);
                    }
                    if ((isTestMode()) && typeof window !== 'undefined') {
                        console.info('[shipping] shippo disabled', SHIPPO_DISABLED);
                    }
                    if (SHIPPO_DISABLED) {
                        const fallbackRate: IShippoRateObject = {
                            id: SHIPPO_DISABLED_FALLBACK_RATE_ID,
                            timestamp: Date.now(),
                            attributes: [],
                            amount: '0',
                            currency: 'USD',
                            provider: 'Shipping',
                            serviceName: 'Standard',
                            estimatedDays: undefined,
                            durationTerms: 'Shipping fee will be confirmed after purchase.',
                        };
                        const ratesMap: Map<string, IShippoRateObject> = new Map([
                            [fallbackRate.id, fallbackRate],
                        ]);
                        const shippingMethodMap: Map<string, IRadioItem> = new Map([
                            [fallbackRate.id, {
                                value: fallbackRate.id,
                                title: 'Standard Shipping',
                                subtitle: fallbackRate.durationTerms,
                                detail: '$0.00',
                            }],
                        ]);
                        setShippingRates(ratesMap);
                        setShippingMethods(shippingMethodMap);
                        setSelectedShippingRateID(fallbackRate.id);
                        setActionButtonIsLoading(false);
                        setActionButtonLoadingMessage('');
                    } else {
                        // Get shipping rates
                        const addressTo: IShippoAddressObject = {
                            state: shippoMailingAddressValidation.state,
                            zip: customerZipCode,
                            country: customerCountry.value,
                        };
                        if (shippoMailingAddressValidation.name) {
                            addressTo.name = shippoMailingAddressValidation.name;
                        }
                        if (shippoMailingAddressValidation.street1) {
                            addressTo.street1 = shippoMailingAddressValidation.street1;
                        }
                        if (shippoMailingAddressValidation.city) {
                            addressTo.city = shippoMailingAddressValidation.city;
                        }
                        if (
                            shippoMailingAddressValidation.company
                            && shippoMailingAddressValidation.company.length > 0
                        ) {
                            addressTo.company = shippoMailingAddressValidation.company;
                        }
                        if (
                            shippoMailingAddressValidation.street2
                            && shippoMailingAddressValidation.street2.length > 0
                        ) {
                            addressTo.street2 = shippoMailingAddressValidation.street2;
                        }
                        if (
                            shippoMailingAddressValidation.street3
                            && shippoMailingAddressValidation.street3.length > 0
                        ) {
                            addressTo.street3 = shippoMailingAddressValidation.street3;
                        }
                        if (
                            shippoMailingAddressValidation.zip
                            && shippoMailingAddressValidation.zip.length > 0
                        ) {
                            addressTo.zip = shippoMailingAddressValidation.zip;
                        }
                        if (
                            shippoMailingAddressValidation.country
                            && shippoMailingAddressValidation.country.length > 0
                        ) {
                            addressTo.country = shippoMailingAddressValidation.country;
                        }
                        if (
                            shippoMailingAddressValidation.phone
                            && shippoMailingAddressValidation.phone.length > 0
                        ) {
                            addressTo.phone = shippoMailingAddressValidation.phone;
                        }
                        if (
                            shippoMailingAddressValidation.email
                            && shippoMailingAddressValidation.email.length > 0
                        ) {
                            addressTo.email = shippoMailingAddressValidation.email;
                        }
                        setActionButtonLoadingMessage('Fetching shipment rates');
                        const shipment = await shippoCreateShipment({
                            test: isTestMode(),
                            paymentIntentId: stripePaymentIntentId,
                            addressTo,
                            physicalItemsInCart,
                            internationalShipment: !!userCartItems
                                && fetchedStoreItems
                                && customerCountry.value !== 'US',
                        });
                        setActionButtonIsLoading(false);
                        setActionButtonLoadingMessage('');

                        // Store shipping rates
                        const ratesMap: Map<string, IShippoRateObject> = new Map();
                        const { rates } = shipment;
                        rates.forEach((rate: Rate) => {
                            const shippoRate: IShippoRateObject = {
                                id: rate.objectId,
                                // we don't use rate.objectCreated.getTime() because Date objects
                                // can't be represented in JSON, so we lose data
                                timestamp: Date.now(),
                                attributes: rate.attributes,
                                amount: rate.amount,
                                currency: rate.currency,
                                provider: rate.provider,
                                serviceName: rate.servicelevel.name,
                                estimatedDays: rate.estimatedDays,
                                durationTerms: rate.durationTerms,
                            };
                            ratesMap.set(rate.objectId, shippoRate);
                        });
                        const shippoRates = Array.from(ratesMap.values());
                        setShippingRates(ratesMap);
                        const rateList: IRadioItem[] = shippoRates.map((rate: IShippoRateObject) => {
                            const estimatedDaysLabel = rate.estimatedDays != null
                                ? `${rate.estimatedDays} day${rate.estimatedDays === 1 ? '' : 's'}`
                                : 'delivery estimate unavailable';
                            const durationTermsLabel = rate.durationTerms || 'No additional shipping details available.';
                            const radioItem: IRadioItem = {
                                value: rate.id,
                                title: `${
                                    rate.provider
                                } ${
                                    rate.serviceName || 'Standard'
                                } (${
                                    estimatedDaysLabel
                                })`,
                                subtitle: durationTermsLabel,
                                detail: `$${rate.amount}`, // in dollars
                            };
                            if (rate.attributes.length > 0) {
                                const [rateAttribute] = rate.attributes;
                                switch (rateAttribute) {
                                case SHIPPO_RATE_ATTRIBUTE_TYPE.fastest:
                                    radioItem.annotation = 'Fastest';
                                    break;
                                case SHIPPO_RATE_ATTRIBUTE_TYPE.bestValue:
                                    radioItem.annotation = 'Best Value';
                                    break;
                                default:
                                    // SHIPPO_RATE_ATTRIBUTE_TYPE.cheapest
                                    radioItem.annotation = 'Cheapest';
                                    break;
                                }
                            }
                            return radioItem;
                        });
                        const shippingMethodMap: Map<string, IRadioItem> = new Map();
                        rateList.forEach((rate: IRadioItem) => {
                            shippingMethodMap.set(rate.value, rate);
                        });
                        setShippingMethods(shippingMethodMap);
                    }

                    // Play Sound
                    if (hasSound && swooshInClip.current) {
                        swooshInClip.current.pause();
                        swooshInClip.current.currentTime = 0;
                        playAudio(swooshInClip.current);
                    }

                    if (currentSessionId) {
                        // Record user action
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.clickCheckoutActionButton,
                            sessionId: currentSessionId,
                            payload: {
                                to: CHECKOUT_CHECKPOINT.shipping,
                            },
                        });
                    }

                    // Update to have shippo address details
                    navigate(
                        `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.shipping}`,
                        {
                            state: {
                                prevPath: location.pathname,
                            },
                        },
                    );

                    if (checkpointFormContainerRef.current) {
                        checkpointFormContainerRef.current.scrollTop = 0;
                    }

                    if (snackbarData.visible) {
                        setSnackbarData({
                            visible: false,
                            text: '',
                        });
                    }
                } catch (e) {
                    setActionButtonIsLoading(false);
                    setActionButtonLoadingMessage('');
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_SHIPPING_RATE_ERROR_MESSAGE((e as any).toString()),
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: SNACKBAR_MESSAGE_SHIPPING_RATE_ERROR_MESSAGE((e as any).toString()),
                            },
                        });
                    }
                    break;
                }
            } else {
                setActionButtonIsLoading(false);
                setActionButtonLoadingMessage('');
                if (addressIsValid) setAddressIsValid(false);
                if (
                    shippoMailingAddressValidation
                    && shippoMailingAddressValidation.validationResults?.messages
                    && shippoMailingAddressValidation.validationResults.messages[0].text
                ) {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_MAILING_ADDRESS_ERROR_MESSAGE(
                            shippoMailingAddressValidation.validationResults.messages[0].text,
                        ),
                    });
                } else {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_MAILING_ADDRESS_ERROR,
                    });
                }
            }
            break;
        case CHECKOUT_CHECKPOINT.shipping:
            if (selectedShippingRateID) {
                // Update Payment Intent
                const shippingRate = shippingRates.get(selectedShippingRateID) as IShippoRateObject;
                if (stripePaymentIntentId && shippingRate) {
                    const shippingCheckoutEmailValue = getCheckoutEmail();
                    setActionButtonIsLoading(true);
                    setActionButtonLoadingMessage('Selecting rate');

                    // Update stripe
                    let updatedPaymentIntent: Stripe.PaymentIntent;
                    try {
                        updatedPaymentIntent = await stripeUpdatePaymentIntent({
                            test: isTestMode(),
                            paymentIntentId: stripePaymentIntentId,
                            updateMode: STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.final,
                            shippingRate,
                            userEmail: shippingCheckoutEmailValue,
                        });
                    } catch (error) {
                        setActionButtonIsLoading(false);
                        handleCheckoutPaymentIntentUpdateError({
                            error,
                            snackbarText: SNACKBAR_MESSAGE_SHIPPING_UPDATE_ERROR,
                            // eslint-disable-next-line max-len
                            userActionContext: `Checkout payment intent update failed in ${CHECKOUT_CHECKPOINT.shipping}`,
                        });
                        break;
                    }
                    // Change checkout status
                    changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.shippingMethodSelected);

                    // Update order cost
                    if (
                        updatedPaymentIntent.metadata
                        && updatedPaymentIntent.metadata.taxCalculationId
                        && updatedPaymentIntent.metadata.taxCalculationId !== stripeTaxCalculationId
                    ) {
                        setActionButtonLoadingMessage('Calculating taxes');
                        const taxCalculationLineItems = await stripeGetTaxCalculationLineItems({
                            test: isTestMode(),
                            taxCalculationId: updatedPaymentIntent.metadata.taxCalculationId,
                        });
                        const subtotal = taxCalculationLineItems.reduce((
                            sum: number,
                            lineItem: Stripe.Tax.CalculationLineItem,
                        ) => (
                            sum + (lineItem.quantity * lineItem.amount)
                        ), 0);
                        const shipping = parseFloat(shippingRate.amount) * CENTS_IN_A_DOLLAR;
                        let tax = taxCalculationLineItems.reduce((
                            sum: number,
                            lineItem: Stripe.Tax.CalculationLineItem,
                        ) => (
                            sum + (lineItem.quantity * lineItem.amount_tax)
                        ), 0);
                        if (updatedPaymentIntent.metadata.shippingTax) {
                            tax += parseInt(updatedPaymentIntent.metadata.shippingTax, 10);
                        }
                        const total = !!subtotal
                            && (
                                shipping != null
                                || physicalItemsInCart.length === 0
                            )
                            && tax != null
                            ? subtotal + (shipping || 0) + tax
                            : undefined;
                        setOrderCost({
                            subtotal,
                            shipping,
                            tax,
                            total,
                        });
                        setStripeTaxCalculationId(updatedPaymentIntent.metadata.taxCalculationId);
                    }
                } else {
                    setActionButtonIsLoading(false);
                    setActionButtonLoadingMessage('');
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_SHIPPING_NO_RATE_ERROR,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    break;
                }

                setActionButtonIsLoading(false);
                setActionButtonLoadingMessage('');

                // Play Sound
                if (hasSound && swooshInClip.current) {
                    swooshInClip.current.pause();
                    swooshInClip.current.currentTime = 0;
                    playAudio(swooshInClip.current);
                }

                if (currentSessionId) {
                    // Record user action
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.clickCheckoutActionButton,
                        sessionId: currentSessionId,
                        payload: {
                            to: CHECKOUT_CHECKPOINT.billing,
                        },
                    });
                }

                navigate(
                    `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.billing}`,
                    {
                        state: {
                            prevPath: location.pathname,
                        },
                    },
                );

                if (checkpointFormContainerRef.current) {
                    checkpointFormContainerRef.current.scrollTop = 0;
                }

                if (snackbarData.visible) {
                    setSnackbarData({
                        visible: false,
                        text: '',
                    });
                }
            } else {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_SELECT_VALID_SHIPPING_METHOD_ERROR,
                });
            }
            break;
        case CHECKOUT_CHECKPOINT.billing:
            if (!selectedBillingAddressType) {
                showUserCorrectableFailureSnackbar({
                    text: SNACKBAR_MESSAGE_BILLING_NO_ADDRESS_ERROR,
                });
                break;
            }

            // Validate ownership only when billing is the first checkpoint that captured
            // the anonymous checkout email, or when the email changed since last validation.
            // eslint-disable-next-line no-case-declarations
            const billingCheckoutEmailValue = getCheckoutEmail();
            if (billingCheckoutEmailValue && auth.currentUser?.isAnonymous) {
                let emailIsUnused = false;
                const normalizedBillingCheckoutEmail = normalizeEmail(billingCheckoutEmailValue);
                if (normalizedBillingCheckoutEmail === validatedAnonymousCheckoutEmail) {
                    emailIsUnused = true;
                } else {
                    setActionButtonIsLoading(true);
                    setActionButtonLoadingMessage('Validating email');
                    try {
                        emailIsUnused = await isCheckoutEmailUnused(billingCheckoutEmailValue);
                        if (!emailIsUnused) {
                            showUserCorrectableFailureSnackbar({
                                text: SNACKBAR_MESSAGE_EMAIL_IN_USE_ERROR,
                            });
                            setIsUserProfileDialogExpanded(true);
                        } else {
                            setValidatedAnonymousCheckoutEmail(normalizedBillingCheckoutEmail);
                        }
                    } catch (error) {
                        setSnackbarData({
                            visible: true,
                            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                            text: SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_ERROR,
                            icon: PixelCartIcon,
                            hasFailure: true,
                        });
                        if (currentSessionId) {
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.error,
                                sessionId: currentSessionId,
                                payload: {
                                    // eslint-disable-next-line max-len
                                    error: `Checkout email availability check failed in ${CHECKOUT_CHECKPOINT.billing}: ${(error as Error).message}`,
                                },
                            });
                        }
                    } finally {
                        setActionButtonIsLoading(false);
                        setActionButtonLoadingMessage('');
                    }
                }
                if (!emailIsUnused) break;
            }
            if (!await validateBookInviteGiftRecipientsForCheckout()) {
                break;
            }
            if (
                physicalItemsInCart.length === 0
                || (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                )
            ) {
                // Validate Billing Country
                if (!customerBillingCountry) {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_NO_BILLING_COUNTRY_ERROR,
                    });
                    if (!billingCountryNotSelected) setBillingCountryNotSelected(true);
                    break;
                } else if (billingCountryNotSelected) {
                    setBillingCountryNotSelected(false);
                }

                // Validate Billing First Name
                if (customerBillingFirstName.length > 0) {
                    if (billingFirstNameNotProvided) setBillingFirstNameNotProvided(false);
                } else {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_NO_BILLING_FIRST_NAME_ERROR,
                    });
                    if (!billingFirstNameNotProvided) setBillingFirstNameNotProvided(true);
                    break;
                }

                // Validate Billing Last Name
                if (customerBillingLastName.length > 0) {
                    if (billingLastNameNotProvided) setBillingLastNameNotProvided(false);
                } else {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_NO_BILLING_LAST_NAME_ERROR,
                    });
                    if (!billingLastNameNotProvided) setBillingLastNameNotProvided(true);
                    break;
                }

                // Validate Billing State
                // eslint-disable-next-line no-case-declarations
                const billingCountryItem = countriesJSON
                    .find((item: any) => item.code2 === customerBillingCountry.value) as ICountry;

                if (customerBillingCountry
                    && !customerBillingState
                    && billingCountryItem.states.length > 0
                ) {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_NO_BILLING_STATE_ERROR,
                    });
                    if (!billingStateNotSelected) setBillingStateNotSelected(true);
                    break;
                } else if (billingStateNotSelected) {
                    setBillingStateNotSelected(false);
                }

                // Validate Billing City
                if (customerBillingCity.length > 0) {
                    if (billingCityNotProvided) setBillingCityNotProvided(false);
                } else {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_NO_BILLING_CITY_ERROR,
                    });
                    if (!billingCityNotProvided) setBillingCityNotProvided(true);
                    break;
                }

                // Validate Billing Zip Code
                if (customerBillingZipCode.length > 0) {
                    if (billingZipCodeNotProvided) setBillingZipCodeNotProvided(false);
                } else {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_NO_BILLING_ZIP_CODE_ERROR,
                    });
                    if (!billingZipCodeNotProvided) setBillingZipCodeNotProvided(true);
                    break;
                }

                // Validate Billing Street One
                if (customerBillingStreetOne.length > 0) {
                    if (billingStreetOneNotProvided) setBillingStreetOneNotProvided(false);
                } else {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_NO_BILLING_STREET_ONE_ERROR,
                    });
                    if (!billingStreetOneNotProvided) setBillingStreetOneNotProvided(true);
                    break;
                }

                // Validate Billing Phone Number
                if (customerBillingPhone.length > 0) {
                    if (phoneNumberNotProvided) setBillingPhoneNumberNotProvided(false);
                } else {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_NO_BILLING_PHONE_NUMBER_ERROR,
                    });
                    if (!billingPhoneNumberNotProvided) setBillingPhoneNumberNotProvided(true);
                    break;
                }
                if (!isPhoneValidForCountry(customerBillingPhone, customerBillingCountry?.value)) {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_INVALID_BILLING_PHONE_NUMBER_ERROR,
                    });
                    break;
                }
            }

            // Validate billing address
            try {
                // IMPORTANT: Validation doesn't work for international addresses in testing
                // In live, international address validation is $0.06/address
                const addressObj: IShippoAddressObject = {
                    name: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? `${customerBillingFirstName} ${customerBillingLastName}`
                        : `${customerFirstName} ${customerLastName}`,
                    street1: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? customerBillingStreetOne
                        : customerStreetOne,
                    city: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? customerBillingCity
                        : customerCity,
                    zip: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? customerBillingZipCode
                        : customerZipCode,
                    country: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? customerBillingCountry!.value
                        : customerCountry!.value,
                    validate: true,
                };
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.same
                    && customerCompany.length > 0
                ) {
                    addressObj.company = customerCompany;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && customerBillingCompany.length > 0
                ) {
                    addressObj.company = customerBillingCompany;
                }
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.same
                    && customerStreetTwo.length > 0
                ) {
                    addressObj.street2 = customerStreetTwo;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && customerBillingStreetTwo.length > 0
                ) {
                    addressObj.street2 = customerBillingStreetTwo;
                }
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.same
                    && customerState
                ) {
                    addressObj.state = customerState.value;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && customerBillingState
                ) {
                    addressObj.state = customerBillingState.value;
                }
                const formattedCustomerPhone = getFormattedPhoneForCountry(
                    customerPhone,
                    customerCountry?.value,
                );
                const formattedBillingPhone = getFormattedPhoneForCountry(
                    customerBillingPhone,
                    customerBillingCountry?.value,
                );
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.same
                    && formattedCustomerPhone
                ) {
                    addressObj.phone = formattedCustomerPhone;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && formattedBillingPhone
                ) {
                    addressObj.phone = formattedBillingPhone;
                }
                const billingAddressCheckoutEmailValue = getCheckoutEmail();
                if (billingAddressCheckoutEmailValue) addressObj.email = billingAddressCheckoutEmailValue;
                setActionButtonIsLoading(true);
                setActionButtonLoadingMessage('Validating address');
                if ((isTestMode()) && typeof window !== 'undefined') {
                    console.info('[shipping] shippo disabled', SHIPPO_DISABLED);
                }
                if (SHIPPO_DISABLED) {
                    shippoBillingAddressValidation = {
                        ...addressObj,
                        validationResults: {
                            isValid: true,
                        },
                    } as Address;
                } else {
                    shippoBillingAddressValidation = await shippoAddressValidation({
                        test: isTestMode(),
                        address: addressObj,
                    });
                    if ((isTestMode()) && typeof window !== 'undefined') {
                        console.info('[shipping] billing address validation [in]', addressObj);
                        console.info('[shipping] billing address validation [out]', shippoBillingAddressValidation);
                    }
                }
            } catch (e) {
                setActionButtonIsLoading(false);
                setActionButtonLoadingMessage('');
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_BILLING_ADDRESS_ERROR_MESSAGE((e as any).toString()),
                    icon: PixelCartIcon,
                    hasFailure: true,
                });
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: SNACKBAR_MESSAGE_BILLING_ADDRESS_ERROR_MESSAGE((e as any).toString()),
                        },
                    });
                }
                break;
            }

            if (
                shippoBillingAddressValidation
                && shippoBillingAddressValidation.validationResults?.isValid
            ) {
                if (!billingAddressIsValid) setBillingAddressIsValid(true);

                // Update billing address
                const billingAddress: IAddress = {
                    firstName: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? customerBillingFirstName
                        : customerFirstName,
                    lastName: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? customerBillingLastName
                        : customerLastName,
                    street1: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? customerBillingStreetOne
                        : customerStreetOne,
                    city: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? customerBillingCity
                        : customerCity,
                    zip: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? customerBillingZipCode
                        : customerZipCode,
                    country: selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                        ? customerBillingCountry!.value
                        : customerCountry!.value,
                };
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.same
                    && customerCompany.length > 0
                ) {
                    billingAddress.company = customerCompany;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && customerBillingCompany.length > 0
                ) {
                    billingAddress.company = customerBillingCompany;
                }
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.same
                    && customerStreetTwo.length > 0
                ) {
                    billingAddress.street2 = customerStreetTwo;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && customerBillingStreetTwo.length > 0
                ) {
                    billingAddress.street2 = customerBillingStreetTwo;
                }
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.same
                    && customerState
                ) {
                    billingAddress.state = customerState.value;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && customerBillingState
                ) {
                    billingAddress.state = customerBillingState.value;
                }
                const formattedCustomerPhone = getFormattedPhoneForCountry(
                    customerPhone,
                    customerCountry?.value,
                );
                const formattedBillingPhone = getFormattedPhoneForCountry(
                    customerBillingPhone,
                    customerBillingCountry?.value,
                );
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.same
                    && formattedCustomerPhone
                ) {
                    billingAddress.phone = formattedCustomerPhone;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && formattedBillingPhone
                ) {
                    billingAddress.phone = formattedBillingPhone;
                }
                // Update address based on Shippo
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && shippoBillingAddressValidation.street1 != null
                    && customerBillingStreetOne !== shippoBillingAddressValidation.street1
                ) {
                    setCustomerBillingStreetOne(shippoBillingAddressValidation.street1);
                    billingAddress.street1 = shippoBillingAddressValidation.street1;
                }
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.same
                    && customerStreetTwo.length > 0
                    && customerStreetTwo !== shippoBillingAddressValidation.street2
                ) {
                    billingAddress.street2 = shippoBillingAddressValidation.street2;
                    if (billingAddress.street2 === '') delete billingAddress.street2;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && customerBillingStreetTwo.length > 0
                    && customerBillingStreetTwo !== shippoBillingAddressValidation.street2
                ) {
                    setCustomerBillingStreetTwo(shippoBillingAddressValidation.street2!);
                    billingAddress.street2 = shippoBillingAddressValidation.street2;
                    if (billingAddress.street2 === '') delete billingAddress.street2;
                }
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && shippoBillingAddressValidation.city != null
                    && customerBillingCity !== shippoBillingAddressValidation.city
                ) {
                    setCustomerBillingCity(shippoBillingAddressValidation.city);
                    billingAddress.city = shippoBillingAddressValidation.city;
                }
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && shippoBillingAddressValidation.zip
                    && customerBillingZipCode !== shippoBillingAddressValidation.zip
                ) {
                    setCustomerBillingZipCode(shippoBillingAddressValidation.zip);
                    billingAddress.zip = shippoBillingAddressValidation.zip;
                }
                if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && shippoBillingAddressValidation.phone
                    && shippoBillingAddressValidation.phone.length > 0
                    && customerBillingPhone !== shippoBillingAddressValidation.phone
                ) {
                    setCustomerBillingPhone(shippoBillingAddressValidation.phone);
                    billingAddress.phone = shippoBillingAddressValidation.phone;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.different
                    && shippoBillingAddressValidation.phone
                    && shippoBillingAddressValidation.phone.length > 0
                    && billingAddress.phone !== shippoBillingAddressValidation.phone
                ) {
                    billingAddress.phone = shippoBillingAddressValidation.phone;
                } else if (
                    selectedBillingAddressType
                    && selectedBillingAddressType.value ===  BILLING_ADDRESS_TYPE.same
                    && shippoBillingAddressValidation.phone
                    && shippoBillingAddressValidation.phone.length > 0
                    && billingAddress.phone !== shippoBillingAddressValidation.phone
                ) {
                    billingAddress.phone = shippoBillingAddressValidation.phone;
                }

                if (stripePaymentIntentId) {
                    // Validate payment details / check for payment method errors
                    if (!stripe || !elements) {
                        // Stripe.js hasn't yet loaded.
                        // Make sure to disable form submission until Stripe.js has loaded.
                        setActionButtonIsLoading(false);
                        setActionButtonLoadingMessage('');
                        setSnackbarData({
                            visible: true,
                            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                            text: SNACKBAR_MESSAGE_STRIPE_NOT_READY_ERROR,
                            icon: PixelCartIcon,
                            hasFailure: true,
                        });
                        return;
                    }
                    setActionButtonLoadingMessage('Validating payment method');
                    if (!selectedSavedStripePaymentMethod) {
                        const paymentDetailsValidation = await elements.submit();
                        if (paymentDetailsValidation.error?.message) {
                            setActionButtonIsLoading(false);
                            setActionButtonLoadingMessage('');
                            setSnackbarData({
                                visible: true,
                                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                                text: SNACKBAR_MESSAGE_PAYMENT_DETAILS_ERROR_MESSAGE(paymentDetailsValidation.error.message),
                                icon: PixelCartIcon,
                                hasFailure: true,
                            });
                            break;
                        }
                    }

                    // Update Payment Intent
                    let promotionReview: ICheckoutPromotionReviewResult | undefined;
                    try {
                        promotionReview = await onActionButtonClickLatest.current.preparePromotionsForReview?.(
                            getCheckoutEmail() || '',
                        );
                    } catch (error) {
                        setActionButtonIsLoading(false);
                        setActionButtonLoadingMessage('');
                        handleCheckoutPaymentIntentUpdateError({
                            error,
                            snackbarText: SNACKBAR_MESSAGE_BILLING_UPDATE_ERROR,
                            userActionContext: 'Unable to check promotion eligibility before review',
                        });
                        return;
                    }
                    await stripeUpdatePaymentIntent({
                        test: isTestMode(),
                        paymentIntentId: stripePaymentIntentId,
                        updateMode: STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.final,
                        cartItems: promotionReview?.promotionsChecked === false ? undefined : promotionReview?.cartItems,
                        promotionIds: promotionReview?.promotionIds,
                        userEmail: getCheckoutEmail(),
                        billingAddress,
                        addToMailingList: subscribeToMailingList,
                        savePaymentMethod: effectiveSavePaymentMethod,
                    }).then(async (updatedPaymentIntent) => {
                        const cur = onActionButtonClickLatest.current;
                        const updateSkippedStatusNotMutable = (
                            updatedPaymentIntent.metadata?.[
                                CHECKOUT_UPDATE_SKIPPED_STATUS_NOT_MUTABLE_METADATA_KEY
                            ] === 'true'
                        );
                        if (updateSkippedStatusNotMutable) {
                            await reconcileImmutableStatusSkippedUpdate({
                                paymentIntentId: stripePaymentIntentId,
                                checkpointLabel: CHECKOUT_CHECKPOINT.billing,
                                stopActionButtonLoading: true,
                            });
                            return;
                        }
                        if (promotionReview?.promotionsChecked) {
                            await cur.finishPromotionsForReview?.(updatedPaymentIntent);
                        }
                        // Change checkout status
                        cur.changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.paymentInfoCollected);

                        // Update order cost
                        if (
                            updatedPaymentIntent.metadata
                            && updatedPaymentIntent.metadata.taxCalculationId
                            && updatedPaymentIntent.metadata.taxCalculationId !== cur.stripeTaxCalculationId
                        ) {
                            setActionButtonLoadingMessage('Calculating taxes');
                            const taxCalculationLineItems = await stripeGetTaxCalculationLineItems({
                                test: isTestMode(),
                                taxCalculationId: updatedPaymentIntent.metadata.taxCalculationId,
                            });
                            const subtotal = taxCalculationLineItems.reduce((
                                sum: number,
                                lineItem: Stripe.Tax.CalculationLineItem,
                            ) => (
                                sum + (lineItem.quantity * lineItem.amount)
                            ), 0);
                            let shipping: number | undefined;
                            if (updatedPaymentIntent.metadata.shippingRate) {
                                const rate = JSON.parse(updatedPaymentIntent.metadata.shippingRate) as IShippoRateObject;
                                shipping = parseFloat(rate.amount) * CENTS_IN_A_DOLLAR;
                            }
                            let tax = taxCalculationLineItems.reduce((
                                sum: number,
                                lineItem: Stripe.Tax.CalculationLineItem,
                            ) => (
                                sum + (lineItem.quantity * lineItem.amount_tax)
                            ), 0);
                            if (updatedPaymentIntent.metadata.shippingTax) {
                                tax += parseInt(updatedPaymentIntent.metadata.shippingTax, 10);
                            }
                            const total = !!subtotal
                                && (
                                    shipping != null
                                    || physicalItemsInCart.length === 0
                                )
                                && tax != null
                                ? subtotal + (shipping || 0) + tax
                                : undefined;
                            setOrderCost({
                                subtotal,
                                shipping,
                                tax,
                                total,
                            });
                            setStripeTaxCalculationId(updatedPaymentIntent.metadata.taxCalculationId);
                        }
                        setActionButtonIsLoading(false);
                        setActionButtonLoadingMessage('');

                        // Play Sound
                        if (hasSound && swooshInClip.current) {
                            swooshInClip.current.pause();
                            swooshInClip.current.currentTime = 0;
                            playAudio(swooshInClip.current);
                        }

                        if (currentSessionId) {
                            // Record user action
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.clickCheckoutActionButton,
                                sessionId: currentSessionId,
                                payload: {
                                    to: CHECKOUT_CHECKPOINT.review,
                                },
                            });
                        }

                        setCustomerDetailsCompleted(true);

                        navigate(
                            `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.review}`,
                            {
                                state: {
                                    prevPath: location.pathname,
                                },
                            },
                        );

                        if (checkpointFormContainerRef.current) {
                            checkpointFormContainerRef.current.scrollTop = 0;
                        }

                        if (promotionReview?.promotionRemoved) {
                            setSnackbarData({
                                visible: true,
                                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                                text: 'We couldn’t apply the promotion code to your order. We’ve removed it and updated your total.',
                                icon: PixelCartIcon,
                                hasFailure: true,
                            });
                        } else if (cur.snackbarData.visible) {
                            setSnackbarData({ visible: false, text: '' });
                        }
                    }).catch((error) => {
                        setActionButtonIsLoading(false);
                        handleCheckoutPaymentIntentUpdateError({
                            error,
                            snackbarText: SNACKBAR_MESSAGE_BILLING_UPDATE_ERROR,
                            // eslint-disable-next-line max-len
                            userActionContext: `Checkout payment intent update failed in ${CHECKOUT_CHECKPOINT.billing}`,
                        });
                    });
                }
            } else {
                setActionButtonIsLoading(false);
                setActionButtonLoadingMessage('');
                if (billingAddressIsValid) setBillingAddressIsValid(false);
                if (
                    shippoBillingAddressValidation
                    && shippoBillingAddressValidation.validationResults?.messages
                    && shippoBillingAddressValidation.validationResults.messages[0].text
                ) {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_BILLING_ADDRESS_ERROR_MESSAGE(
                            shippoBillingAddressValidation.validationResults.messages[0].text,
                        ),
                    });
                } else {
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_BILLING_ADDRESS_ERROR,
                    });
                }
            }
            break;
        case CHECKOUT_CHECKPOINT.review:
            if (
                stripePaymentIntentId
                && checkpoint === CHECKOUT_CHECKPOINT.review
            ) {
                if (hasComplimentaryBookInvite) {
                    setHadComplimentaryBookInviteForCheckoutFlow(true);
                }
                // Change checkout status
                changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.reviewed);

                if (!stripe || !elements) {
                    // Stripe.js hasn't yet loaded.
                    // Make sure to disable form submission until Stripe.js has loaded.
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_STRIPE_NOT_READY_ERROR,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    return;
                }

                if (currentSessionId) {
                    // Record user action
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.clickCheckoutBuyButton,
                        sessionId: currentSessionId,
                    });
                }

                setStripePaymentIsLoading(true);

                // Create or convert anonymous account before payment confirmation.
                let customerEmail = getCheckoutEmail();
                if (auth.currentUser?.isAnonymous) {
                    if (!customerEmail || !validateEmail(customerEmail)) {
                        setStripePaymentIsLoading(false);
                        setActionButtonLoadingMessage('');
                        setSnackbarData({
                            visible: true,
                            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                            text: !customerEmail
                                ? SNACKBAR_MESSAGE_NO_EMAIL_ERROR
                                : SNACKBAR_MESSAGE_INVALID_EMAIL_ERROR,
                            icon: PixelCartIcon,
                            hasFailure: true,
                        });
                        return;
                    }
                    if (normalizeEmail(customerEmail) !== validatedAnonymousCheckoutEmail) {
                        setSnackbarData({
                            visible: true,
                            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                            text: SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_ERROR,
                            icon: PixelCartIcon,
                            hasFailure: true,
                        });
                        setStripePaymentIsLoading(false);
                        return;
                    }

                    try {
                        setActionButtonLoadingMessage('Creating verascope account');
                        const checkoutAccountFirstName = (
                            selectedBillingAddressType
                            && selectedBillingAddressType.value === BILLING_ADDRESS_TYPE.different
                                ? customerBillingFirstName
                                : customerFirstName
                        ).trim();
                        const checkoutAccountLastName = (
                            selectedBillingAddressType
                            && selectedBillingAddressType.value === BILLING_ADDRESS_TYPE.different
                                ? customerBillingLastName
                                : customerLastName
                        ).trim();
                        const conversionOldUser = user
                            ? {
                                ...user,
                                firstName: checkoutAccountFirstName || user.firstName,
                                lastName: checkoutAccountLastName || user.lastName,
                            }
                            : undefined;
                        const conversionOldUserCart = userCartItems
                            ? [...userCartItems]
                            : [];
                        let conversionOldUserEmail: string | undefined;
                        let conversionOldUserSessions: string[] | undefined;
                        let conversionOldUserUpdateTimestamps: number[] | undefined;
                        if (conversionOldUser) {
                            try {
                                const existingUserEmailSessionsAndUpdateTimestamps = await getUserEmailSessionItemsAndUpdateTimestamps({
                                    test: isTestMode(),
                                });
                                conversionOldUserEmail = existingUserEmailSessionsAndUpdateTimestamps.email || undefined;
                                conversionOldUserSessions = existingUserEmailSessionsAndUpdateTimestamps.sessions;
                                conversionOldUserUpdateTimestamps = existingUserEmailSessionsAndUpdateTimestamps.updated;
                            } catch (error) {
                                // best effort only; migration setup tolerates missing optional fields
                                if (currentSessionId) {
                                    recordUserAction({
                                        test: isTestMode(),
                                        type: USER_ACTION_TYPE.error,
                                        sessionId: currentSessionId,
                                        payload: {
                                            error: `Checkout email conversion failed to fetch optional user email-session context: ${(error as Error)?.message || String(error)}`,
                                            source: 'checkout-email-conversion-context-fetch',
                                            checkpoint: CHECKOUT_CHECKPOINT.review,
                                        },
                                    });
                                }
                            }
                        }
                        const conversionResult = await authenticateWithEmailConversion({
                            test: isTestMode(),
                            hasSound,
                            email: customerEmail,
                            oldUserData: {
                                user: conversionOldUser,
                                email: conversionOldUserEmail,
                                oldUserCart: conversionOldUserCart,
                                userSessions: conversionOldUserSessions,
                                userUpdateTimestamps: conversionOldUserUpdateTimestamps,
                            },
                            errorMessages: {
                                fallback: SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_ERROR,
                                emailInUse: SNACKBAR_MESSAGE_EMAIL_IN_USE_ERROR,
                                precheckUnavailable: SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_PRECHECK_ERROR,
                                invalidEmail: SNACKBAR_MESSAGE_INVALID_EMAIL_ERROR,
                                notAnonymous: SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_SESSION_ERROR,
                                userNotFound: SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_USER_NOT_FOUND_ERROR,
                                unauthenticated: SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_UNAUTHENTICATED_ERROR,
                                signInFallback: SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_SIGN_IN_FALLBACK_ERROR,
                            },
                        });
                        customerEmail = conversionResult.email;
                        setCheckoutEmail(conversionResult.email);
                    } catch (error) {
                        const conversionErrorMessage = (error as { message?: string } | undefined)?.message
                            || SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_ERROR;
                        setStripePaymentIsLoading(false);
                        setActionButtonLoadingMessage('');
                        setSnackbarData({
                            visible: true,
                            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                            text: conversionErrorMessage,
                            icon: PixelCartIcon,
                            hasFailure: true,
                        });
                        if (currentSessionId) {
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.error,
                                sessionId: currentSessionId,
                                payload: {
                                    error: `Guest checkout account conversion failed: ${(error as Error).message}`,
                                },
                            });
                        }
                        return;
                    }
                }

                const authUserForCheckout = auth.currentUser;
                if (!authUserForCheckout || authUserForCheckout.isAnonymous) {
                    setStripePaymentIsLoading(false);
                    setActionButtonLoadingMessage('');
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_CREATE_CHECKOUT_ACCOUNT_USER_NOT_FOUND_ERROR,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: 'Checkout blocked before payment confirmation because authenticated user session is missing.',
                                checkpoint: CHECKOUT_CHECKPOINT.review,
                            },
                        });
                    }
                    return;
                }

                let verifiedCheckoutUser: IPartialUserItem | null = null;
                try {
                    verifiedCheckoutUser = await getUser({
                        test: isTestMode(),
                        userId: authUserForCheckout.uid,
                    });
                } catch (error) {
                    const accountVerificationError = (error as Error)?.message || String(error);
                    setStripePaymentIsLoading(false);
                    setActionButtonLoadingMessage('');
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_REVIEW_ACCOUNT_VERIFICATION_ERROR,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: [
                                    'Checkout failed to verify user account before payment confirmation:',
                                    accountVerificationError,
                                ].join(' '),
                                checkpoint: CHECKOUT_CHECKPOINT.review,
                            },
                        });
                    }
                    return;
                }
                if (!verifiedCheckoutUser) {
                    const hasLinkedSocialProvider = (authUserForCheckout.providerData || []).some((provider) => (
                        !!provider.providerId
                        && provider.providerId !== FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.email
                        && provider.providerId !== FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.anonymous
                    ));
                    setStripePaymentIsLoading(false);
                    setActionButtonLoadingMessage('');
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: hasLinkedSocialProvider
                            ? SNACKBAR_MESSAGE_REVIEW_SOCIAL_ACCOUNT_REQUIRED_ERROR
                            : SNACKBAR_MESSAGE_REVIEW_ACCOUNT_REQUIRED_ERROR,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: [
                                    'Checkout blocked before payment confirmation because firestore user account is missing for auth uid:',
                                    authUserForCheckout.uid,
                                ].join(' '),
                                checkpoint: CHECKOUT_CHECKPOINT.review,
                                hasLinkedSocialProvider,
                                providerIds: (authUserForCheckout.providerData || []).map((provider) => provider.providerId || ''),
                            },
                        });
                    }
                    return;
                }

                const shouldCreateCustomerProfile = (
                    !!verifiedCheckoutUser
                    && !stripeCustomerId
                    && !!customerEmail
                    && effectiveSavePaymentMethod
                    && !selectedSavedStripePaymentMethod
                );
                let customerIdForPaymentIntent = stripeCustomerId;
                if (shouldCreateCustomerProfile && verifiedCheckoutUser && customerEmail) {
                    setActionButtonLoadingMessage('Generating customer profile');
                    const inferredName = inferNameFromEmail(customerEmail);
                    const inferredCustomerName = `${inferredName.firstName || ''} ${inferredName.lastName || ''}`.trim();
                    const customerName = `${verifiedCheckoutUser.firstName || ''} ${verifiedCheckoutUser.lastName || ''}`.trim()
                        || inferredCustomerName
                        || customerEmail;
                    const customer = await stripeCreateCustomer({
                        test: isTestMode(),
                        email: customerEmail,
                        name: customerName,
                    });
                    customerIdForPaymentIntent = customer.id;
                    // Change in firestore
                    await updateUserStripeCustomerId({
                        test: isTestMode(),
                        stripeCustomerId: customer.id,
                    });
                }
                let finalCheckoutUpdatePrepared = false;
                let preConfirmPaymentIntent: Stripe.PaymentIntent;
                let finalUpdateAttemptPaymentIntent: Stripe.PaymentIntent | undefined;
                try {
                    setActionButtonLoadingMessage('Finalizing checkout details');
                    preConfirmPaymentIntent = await stripeUpdatePaymentIntent({
                        test: isTestMode(),
                        paymentIntentId: stripePaymentIntentId,
                        updateMode: selectedSavedStripePaymentMethod
                            ? STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.lightweight
                            : STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.final,
                        hadComplimentaryBookInviteAtCheckout: hadBookInviteAtCheckout,
                        bookInviteAssignOneToSelf: effectiveAssignOneBookInviteToSelf,
                        bookInvites: bookInvitePayload,
                        ...(customerEmail ? {
                            userEmail: customerEmail,
                        } : {}),
                        ...(customerIdForPaymentIntent ? {
                            customerId: customerIdForPaymentIntent,
                        } : {}),
                    });
                    finalUpdateAttemptPaymentIntent = preConfirmPaymentIntent;
                } catch (error) {
                    handleCheckoutPaymentIntentUpdateError({
                        error,
                        snackbarText: SNACKBAR_MESSAGE_REVIEW_PRECONFIRM_UPDATE_ERROR,
                        // eslint-disable-next-line max-len
                        userActionContext: `Checkout payment intent pre-confirm update failed in ${CHECKOUT_CHECKPOINT.review}`,
                        stopStripePaymentLoading: true,
                    });
                    break;
                }
                if (
                    preConfirmPaymentIntent.metadata
                    && preConfirmPaymentIntent.metadata.checkoutUpdateMode
                    === STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.final
                ) {
                    finalCheckoutUpdatePrepared = true;
                }

                // Confirm Payment
                setActionButtonLoadingMessage('Confirming payment');
                const confirmParams: ConfirmPaymentData = {
                    return_url: `${window.location.origin}/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.confirmation}`,
                    save_payment_method: effectiveSavePaymentMethod,
                };
                const selectedBillingIsSame = selectedBillingAddressType
                    && selectedBillingAddressType.value === BILLING_ADDRESS_TYPE.same;
                const confirmFirstName = selectedBillingIsSame
                    ? customerFirstName
                    : customerBillingFirstName;
                const confirmLastName = selectedBillingIsSame
                    ? customerLastName
                    : customerBillingLastName;
                const confirmStreetOne = selectedBillingIsSame
                    ? customerStreetOne
                    : customerBillingStreetOne;
                const confirmStreetTwo = selectedBillingIsSame
                    ? customerStreetTwo
                    : customerBillingStreetTwo;
                const confirmCity = selectedBillingIsSame
                    ? customerCity
                    : customerBillingCity;
                const confirmState = selectedBillingIsSame
                    ? customerState?.value
                    : customerBillingState?.value;
                const confirmZip = selectedBillingIsSame
                    ? customerZipCode
                    : customerBillingZipCode;
                const confirmCountry = selectedBillingIsSame
                    ? customerCountry?.value
                    : customerBillingCountry?.value;
                const confirmPhoneRaw = selectedBillingIsSame
                    ? customerPhone
                    : customerBillingPhone;
                const confirmPhone = getFormattedPhoneForCountry(confirmPhoneRaw, confirmCountry);
                if (
                    !selectedSavedStripePaymentMethod
                    && confirmFirstName.length > 0
                    && confirmLastName.length > 0
                    && confirmStreetOne.length > 0
                    && confirmCity.length > 0
                    && confirmZip.length > 0
                    && confirmCountry
                ) {
                    confirmParams.payment_method_data = {
                        billing_details: {
                            name: `${confirmFirstName} ${confirmLastName}`,
                            phone: confirmPhone,
                            address: {
                                line1: confirmStreetOne,
                                line2: confirmStreetTwo.length > 0 ? confirmStreetTwo : undefined,
                                city: confirmCity,
                                state: confirmState || undefined,
                                postal_code: confirmZip,
                                country: confirmCountry,
                            },
                        },
                    };
                }
                let confirmBillingAddress: IAddress | undefined;
                if (
                    confirmFirstName.length > 0
                    && confirmLastName.length > 0
                    && confirmStreetOne.length > 0
                    && confirmCity.length > 0
                    && confirmZip.length > 0
                    && confirmCountry
                ) {
                    confirmBillingAddress = {
                        firstName: confirmFirstName,
                        lastName: confirmLastName,
                        street1: confirmStreetOne,
                        city: confirmCity,
                        zip: confirmZip,
                        country: confirmCountry,
                    };
                    if (confirmStreetTwo.length > 0) {
                        confirmBillingAddress.street2 = confirmStreetTwo;
                    }
                    if (confirmState) {
                        confirmBillingAddress.state = confirmState;
                    }
                    if (confirmPhone) {
                        confirmBillingAddress.phone = confirmPhone;
                    }
                }
                if (selectedSavedStripePaymentMethod) {
                    let savedMethodPaymentIntent: Stripe.PaymentIntent;
                    try {
                        savedMethodPaymentIntent = await stripeUpdatePaymentIntent({
                            test: isTestMode(),
                            paymentIntentId: stripePaymentIntentId,
                            updateMode: STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.final,
                            paymentMethodId: selectedSavedStripePaymentMethod.value,
                            userEmail: customerEmail,
                            hadComplimentaryBookInviteAtCheckout: hadBookInviteAtCheckout,
                            bookInviteAssignOneToSelf: effectiveAssignOneBookInviteToSelf,
                            bookInvites: bookInvitePayload,
                            ...(confirmBillingAddress ? {
                                billingAddress: confirmBillingAddress,
                            } : {}),
                            ...(stripeCustomerId ? {
                                customerId: stripeCustomerId,
                            } : {}),
                        });
                        finalUpdateAttemptPaymentIntent = savedMethodPaymentIntent;
                    } catch (error) {
                        handleCheckoutPaymentIntentUpdateError({
                            error,
                            snackbarText: SNACKBAR_MESSAGE_REVIEW_SAVED_METHOD_UPDATE_ERROR,
                            // eslint-disable-next-line max-len
                            userActionContext: `Checkout payment intent saved-method update failed in ${CHECKOUT_CHECKPOINT.review}`,
                            stopStripePaymentLoading: true,
                        });
                        break;
                    }
                    if (
                        savedMethodPaymentIntent.metadata
                        && savedMethodPaymentIntent.metadata.checkoutUpdateMode
                        === STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.final
                    ) {
                        finalCheckoutUpdatePrepared = true;
                    }
                }
                if (!finalCheckoutUpdatePrepared) {
                    const updateSkippedStatusNotMutable = (
                        finalUpdateAttemptPaymentIntent?.metadata?.[
                            CHECKOUT_UPDATE_SKIPPED_STATUS_NOT_MUTABLE_METADATA_KEY
                        ] === 'true'
                    );
                    if (updateSkippedStatusNotMutable) {
                        await reconcileImmutableStatusSkippedUpdate({
                            paymentIntentId: stripePaymentIntentId,
                            checkpointLabel: CHECKOUT_CHECKPOINT.review,
                            stopStripePaymentLoading: true,
                        });
                        break;
                    }
                    setStripePaymentIsLoading(false);
                    setActionButtonLoadingMessage('');
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_CHECKOUT_MODE_ERROR,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: 'Checkout confirmation blocked because final payment intent update was not applied.',
                            },
                        });
                    }
                    break;
                }
                if (!getPaymentIntentCheckoutEmail(finalUpdateAttemptPaymentIntent)) {
                    setStripePaymentIsLoading(false);
                    setActionButtonLoadingMessage('');
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_NO_EMAIL_ERROR,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: 'Checkout confirmation blocked because payment intent is missing checkout email.',
                                checkpoint: CHECKOUT_CHECKPOINT.review,
                            },
                        });
                    }
                    navigate(
                        `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.billing}`,
                        {
                            state: {
                                prevPath: location.pathname,
                            },
                        },
                    );
                    if (checkpointFormContainerRef.current) {
                        checkpointFormContainerRef.current.scrollTop = 0;
                    }
                    break;
                }
                let stripePaymentError: StripeError | undefined;
                if (
                    selectedSavedStripePaymentMethod
                    && stripeClientSecret
                ) {
                    confirmParams.payment_method = selectedSavedStripePaymentMethod.value;
                    const { error } = await stripe.confirmPayment({
                        clientSecret: stripeClientSecret,
                        confirmParams,
                    });
                    stripePaymentError = error;
                } else if (
                    !selectedSavedStripePaymentMethod
                ) {
                    const { error } = await stripe.confirmPayment({
                        elements,
                        confirmParams,
                    });
                    stripePaymentError = error;
                } else {
                    setStripePaymentIsLoading(false);
                    setActionButtonLoadingMessage('');
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_GENERIC_PAYMENT_ERROR,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    break;
                }

                // This point will only be reached if there is an immediate error when
                // confirming the payment. Otherwise, customer will be redirected to
                // `return_url`.
                if (stripePaymentError) {
                    setStripePaymentIsLoading(false);
                    setActionButtonLoadingMessage('');
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: stripePaymentError.message
                            ? SNACKBAR_MESSAGE_PAYMENT_ERROR_MESSAGE(stripePaymentError.message)
                            : SNACKBAR_MESSAGE_GENERIC_PAYMENT_ERROR,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    switch (stripePaymentError.type) {
                    case STRIPE_PAYMENT_INTENT_ERROR_TYPE.cardError:
                        // Change checkout status
                        changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.cardError);
                        break;
                    case STRIPE_PAYMENT_INTENT_ERROR_TYPE.apiError:
                        // Change checkout status
                        changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.apiError);
                        break;
                    case STRIPE_PAYMENT_INTENT_ERROR_TYPE.idempotencyError:
                        // Change checkout status
                        changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.idempotencyError);
                        break;
                    case STRIPE_PAYMENT_INTENT_ERROR_TYPE.invalidRequestError:
                        // Change checkout status
                        changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.invalidRequestError);
                        break;
                    case STRIPE_PAYMENT_INTENT_ERROR_TYPE.validationError:
                        // Change checkout status
                        changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.validationError);
                        break;
                    default:
                        // Change checkout status
                        changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.unknownPaymentError);
                    }
                }
            } else {
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_REVIEW_ERROR,
                    icon: PixelCartIcon,
                    hasFailure: true,
                });
            }
            break;
        default:
            // CHECKOUT_CHECKPOINT.confirmation
            // Do nothing
            // Should not allow to go forward
        }
        } finally {
            actionButtonClickInFlightRef.current = false;
        }
    };
    /* eslint-enable indent */

    const onStripePaymentElementEnter = (): void => {
        setCursorIsHidden(true);
    };

    const onStripePaymentElementLeave = (): void => {
        setCursorIsHidden(false);
    };

    const onStripePaymentElementChange = (e: StripePaymentElementChangeEvent): void => {
        const paymentMethodType = e.value;
        if (paymentMethodType) {
            setStripePaymentMethodType(paymentMethodType.type);
            if (!paymentMethodSupportsSave(paymentMethodType.type) && savePaymentMethod) {
                setSavePaymentMethod(false);
            }
            if (currentSessionId) {
                // Record user action
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.selectCheckoutPaymentMethod,
                    sessionId: currentSessionId,
                    payload: {
                        type: paymentMethodType,
                    },
                });
            }
        }
        if (
            e.complete
            && !stripePaymentDetailsCompleted
        ) {
            setStripePaymentDetailsCompleted(true);
            if (currentSessionId) {
                // Record user action
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.completeCheckoutPaymentDetails,
                    sessionId: currentSessionId,
                });
            }
        }
    };
    useEffect(() => {
        if (!savePaymentMethodAllowedBySelection && savePaymentMethod) {
            setSavePaymentMethod(false);
        }
    }, [
        savePaymentMethodAllowedBySelection,
        savePaymentMethod,
    ]);

    // ===== Side Effects =====

    /**
     * Fetches and saves list of countries
     */
    useEffect(() => {
        const countryArr: IDropdownItem[] = (countriesJSON as ICountry[]).map((country: ICountry) => ({
            value: country.code2,
            name: country.name,
        }));
        setCountries(countryArr);
    }, []);

    /**
     * Manages Action Button Loading Message Ellipsis Changes
     */
    useInterval(() => {
        handleActionButtonMessageEllipsisText();
    }, actionButtonIsLoading || stripePaymentIsLoading ? ACTION_BUTTON_MESSAGE_ELLIPSIS_INTERVAL_DURATION : null);

    /**
     * Redirects to different checkpoint based on value of payment success
     */
    useEffect(() => {
        if (
            checkpoint === CHECKOUT_CHECKPOINT.review
            && selectedBillingAddressType
            && billingAddressIsValid
            && (
                physicalItemsInCart.length === 0
                || (
                    addressIsValid
                    && selectedShippingRateID
                )
            ) && stripePaymentSucceeded
        ) {
            navigate(
                `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.confirmation}`,
                {
                    state: {
                        prevPath: location.pathname,
                    },
                },
            );
        } else if (
            checkpoint === CHECKOUT_CHECKPOINT.review
            && selectedBillingAddressType
            && billingAddressIsValid
            && (
                physicalItemsInCart.length === 0
                || (
                    addressIsValid
                    && selectedShippingRateID
                )
            ) && !stripePaymentDetailsCompleted
            && !stripePaymentSucceeded
        ) {
            navigate(
                `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.billing}`,
                {
                    state: {
                        prevPath: location.pathname,
                    },
                },
            );
        }
    }, [
        checkpoint,
        stripePaymentSucceeded,
    ]);

    // ===== Memoization =====

    const checkpointTitle = useMemo(() => {
        switch (checkpoint) {
        case CHECKOUT_CHECKPOINT.info:
            return 'Shipping Address';
        case CHECKOUT_CHECKPOINT.shipping:
            return 'Shipping Method';
        case CHECKOUT_CHECKPOINT.billing:
            return 'Billing';
        case CHECKOUT_CHECKPOINT.review:
            return 'Confirm Shipping and Billing Details';
        default:
            // CHECKOUT_CHECKPOINT.confirmation
            return 'Order complete! Check your email for further details.';
        }
    }, [checkpoint]);
    const statePlaceholderText = useMemo(
        () => getStateFieldLabel(customerCountry?.value),
        [customerCountry?.value],
    );
    const billingStatePlaceholderText = useMemo(
        () => getStateFieldLabel(customerBillingCountry?.value),
        [customerBillingCountry?.value],
    );

    const actionButtonInteractionProperties = useMemo(() => {
        const properties: Record<string, string | (
            (e: React.TouchEvent) => void) | ((e: React.MouseEvent) => void
        )> = !(actionButtonIsLoading || stripePaymentIsLoading)
            ? {
                className: HOVER_TARGET_CLASSNAME,
            } : {};

        if (
            !(
                actionButtonIsLoading
                || stripePaymentIsLoading
            ) && detectTouchDevice(document)
        ) {
            properties.onTouchStart = (e: React.TouchEvent) => onActionButtonEnter(e);
            properties.onTouchEnd = (e: React.TouchEvent) => onActionButtonLeave(e);
        } else if (
            !(
                actionButtonIsLoading
                || stripePaymentIsLoading
            ) && !detectTouchDevice(document)
        ) {
            properties.onMouseEnter = (e: React.TouchEvent) => onActionButtonEnter(e);
            properties.onMouseLeave = (e: React.TouchEvent) => onActionButtonLeave(e);
        }

        return properties;
    }, [
        actionButtonIsLoading,
        stripePaymentIsLoading,
        customCursorActive,
    ]);

    const dropdownSavedAddresses = useMemo(() => (
        savedAddresses.map((address: IAddress, index: number) => ({
            value: `${index}`,
            name: `${
                `${address.firstName} ${address.lastName}`
            }${address.streetNo
                ? `, ${address.streetNo}`
                : ''
            }, ${
                address.street1
            }${address.street2
                ? `, ${address.street2}`
                : ''
            }${address.street3
                ? `, ${address.street3}`
                : ''
            }, ${
                address.city
            }${address.state
                ? ` ${address.state}`
                : ''
            } ${
                address.zip
            }, ${
                address.country
            }`,
        }))
    ), [savedAddresses]);

    const dropdownSavedStripePaymentMethods = useMemo(() => (
        savedStripePaymentMethods.map((paymentMethod: Stripe.PaymentMethod) => {
            let name: string | undefined;
            if (
                paymentMethod.type === 'card'
                && paymentMethod.card
            ) {
                name = `${
                    capitalizeFirstLetter(paymentMethod.card.brand.replace('_', ' ').toLowerCase())
                } ending with ${
                    paymentMethod.card.last4
                }`;
            } else {
                name = capitalizeFirstLetter(paymentMethod.type.replace('_', ' ').toLowerCase());
            }

            return {
                value: paymentMethod.id,
                name,
            };
        })
    ), [savedStripePaymentMethods]);

    const stripePaymentElementOptions: StripePaymentElementOptions = {
        layout: {
            type: 'accordion',
            defaultCollapsed: false,
            radios: true,
            spacedAccordionItems: false,
        },
    };
    const bookInviteAssignmentSection = (
        <BookInviteSectionContainer
            ref={bookInviteSectionRef}
        >
            <BookInviteSectionHeaderButton
                type="button"
                customCursorActive={customCursorActive}
                className={HOVER_TARGET_CLASSNAME}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: onActionButtonEnter,
                    onMouseLeave: onActionButtonLeave,
                })}
                {...getScrollAwareTapPressEventHandlers({ onTap: onBookInviteSectionToggle })}
            >
                <BookInviteSectionHeaderTextContainer>
                    <BookInviteSectionHeaderTitle>
                        Assign / Gift Web Book Invites (Optional)
                    </BookInviteSectionHeaderTitle>
                    <BookInviteSectionHeaderSummary>
                        Book Invites in this order:
                        {' '}
                        <BookInviteSectionHeaderSummaryValue>
                            {bookInvitesInOrderCount}
                        </BookInviteSectionHeaderSummaryValue>
                    </BookInviteSectionHeaderSummary>
                </BookInviteSectionHeaderTextContainer>
                <BookInviteSectionToggleButtonContainer
                    detectTouchDevice={detectTouchDevice(document)}
                    customCursorActive={customCursorActive}
                    expanded={bookInviteSectionExpanded}
                >
                    <BookInviteSectionToggleButton />
                </BookInviteSectionToggleButtonContainer>
            </BookInviteSectionHeaderButton>
            {bookInviteSectionExpanded && (
                <BookInviteSectionBody>
                    {!buyerHasActiveWebBookAccess && (
                        <BookInviteSelfAssignmentRow>
                            <PixelCheckbox
                                checked={assignOneBookInviteToSelf}
                                text="Keep 1 Book Invite for Me"
                                customCursorActive={customCursorActive}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                onChange={onAssignOneBookInviteToSelfToggle}
                            />
                        </BookInviteSelfAssignmentRow>
                    )}
                    <BookInviteActionRow>
                        <BookInviteToggleActionButton
                            type="button"
                            active={assignBookInviteGiftsNow}
                            customCursorActive={customCursorActive}
                            className={HOVER_TARGET_CLASSNAME}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onActionButtonEnter,
                                onMouseLeave: onActionButtonLeave,
                            })}
                            {...getPressEventHandlers({ onMouseDown: onAssignOthersNow })}
                        >
                            Assign Others Now
                        </BookInviteToggleActionButton>
                        <BookInviteToggleActionButton
                            type="button"
                            active={!assignBookInviteGiftsNow}
                            customCursorActive={customCursorActive}
                            className={HOVER_TARGET_CLASSNAME}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onActionButtonEnter,
                                onMouseLeave: onActionButtonLeave,
                            })}
                            {...getPressEventHandlers({ onMouseDown: onDoThisLater })}
                        >
                            I&apos;ll do this later
                        </BookInviteToggleActionButton>
                    </BookInviteActionRow>
                    <BookInviteAssignedSummaryText>
                        {`Assigned ${assignedBookInviteCount} / ${bookInvitesInOrderCount}`}
                    </BookInviteAssignedSummaryText>
                    {assignBookInviteGiftsNow && (
                        <>
                            <BookInviteSubtext>
                                {selfAssignmentUsesOnlyInvite
                                    ? 'To assign this Book Invite to someone else, turn off "Keep 1 Book Invite for Me" above.'
                                    : 'Add recipient emails and press Enter or comma to create book invites.'}
                            </BookInviteSubtext>
                            <BookInviteChipList>
                                {bookInvites.map((invite: IBookInviteWithLocalId, inviteIndex: number) => (
                                    <BookInviteCard
                                        key={invite.id}
                                        hasError={bookInviteInvalidRecipientEmailSet.has(
                                            normalizeEmail(invite.recipientEmail),
                                        )}
                                    >
                                        <BookInviteIndex>
                                            <BookInviteIndexText>
                                                {`Invite #${inviteIndex + 1}`}
                                            </BookInviteIndexText>
                                        </BookInviteIndex>
                                        <BookInviteBody>
                                            <BookInviteChipRow
                                                hasError={bookInviteInvalidRecipientEmailSet.has(
                                                    normalizeEmail(invite.recipientEmail),
                                                )}
                                            >
                                                <BookInviteChipText>
                                                    {invite.recipientEmail}
                                                </BookInviteChipText>
                                                <BookInviteChipRemoveButton
                                                    type="button"
                                                    customCursorActive={customCursorActive}
                                                    hasError={bookInviteInvalidRecipientEmailSet.has(
                                                        normalizeEmail(invite.recipientEmail),
                                                    )}
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: onActionButtonEnter,
                                                        onMouseLeave: onActionButtonLeave,
                                                    })}
                                                    {...getPressEventHandlers({ onMouseDown: () => onRemoveBookInvite(invite.id) })}
                                                >
                                                    <ReactSVG
                                                        src={PixelCrossIcon}
                                                    />
                                                </BookInviteChipRemoveButton>
                                            </BookInviteChipRow>
                                            <InputGroupContainer
                                                stackOnMobile
                                            >
                                                <InputContainer
                                                    noMargin
                                                    paddingRight
                                                >
                                                    <BookInviteRecipientNameInputScale>
                                                        <PixelInput
                                                            customCursorActive={customCursorActive}
                                                            currentSessionId={currentSessionId}
                                                            isValid={!bookInviteInvalidRecipientEmailSet.has(
                                                                normalizeEmail(invite.recipientEmail),
                                                            )}
                                                            placeholderText="Recipient Name (Optional)"
                                                            value={invite.recipientName}
                                                            hasSound={hasSound}
                                                            onCursorEnter={onCursorEnter}
                                                            onCursorLeave={onCursorLeave}
                                                            setInputFocused={setInputFocused}
                                                            onChange={(value: string) => {
                                                                onBookInviteFieldChange(
                                                                    invite.id,
                                                                    BOOK_INVITE_FIELD_KEY.recipientName,
                                                                    value,
                                                                );
                                                            }}
                                                        />
                                                    </BookInviteRecipientNameInputScale>
                                                </InputContainer>
                                                <InputContainer
                                                    noMargin
                                                    paddingLeft
                                                >
                                                    <BookInviteSecondaryInputScale>
                                                        <PixelInput
                                                            customCursorActive={customCursorActive}
                                                            currentSessionId={currentSessionId}
                                                            isValid={!bookInviteInvalidRecipientEmailSet.has(
                                                                normalizeEmail(invite.recipientEmail),
                                                            )}
                                                            placeholderText="Invite Message (Optional)"
                                                            value={invite.inviteMessage}
                                                            hasSound={hasSound}
                                                            onCursorEnter={onCursorEnter}
                                                            onCursorLeave={onCursorLeave}
                                                            setInputFocused={setInputFocused}
                                                            onChange={(value: string) => {
                                                                onBookInviteFieldChange(
                                                                    invite.id,
                                                                    BOOK_INVITE_FIELD_KEY.inviteMessage,
                                                                    value,
                                                                );
                                                            }}
                                                        />
                                                    </BookInviteSecondaryInputScale>
                                                </InputContainer>
                                            </InputGroupContainer>
                                            <BookInviteActionsRow>
                                                <BookInviteActionButtonContainer>
                                                    <BookInviteActionButton
                                                        type="button"
                                                        $active={bookInvitePreviewInviteId === invite.id}
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: onActionButtonEnter,
                                                            onMouseLeave: onActionButtonLeave,
                                                        })}
                                                        {...getPressEventHandlers({ onMouseDown: () => onToggleBookInvitePreview(invite) })}
                                                    >
                                                        {bookInvitePreviewInviteId === invite.id ? 'Hide Preview' : 'Email Preview'}
                                                    </BookInviteActionButton>
                                                </BookInviteActionButtonContainer>
                                            </BookInviteActionsRow>
                                            {bookInvitePreviewInviteId === invite.id && (
                                                <BookInviteEmailPreviewContainer>
                                                    <BookInviteEmailPreviewHeader>
                                                        Email Preview
                                                    </BookInviteEmailPreviewHeader>
                                                    {bookInvitePreviewLoadingIds[invite.id] ? (
                                                        <BookInviteEmailPreviewLoadingContainer>
                                                            <BookInvitePreviewSpinner
                                                                src={Spinner}
                                                            />
                                                        </BookInviteEmailPreviewLoadingContainer>
                                                    ) : (
                                                        <>
                                                            <BookInviteEmailPreviewSubject>
                                                                {`Subject: ${
                                                                    getDisplayBookInvitePreview(invite).subject
                                                                    || bookInviteEmailSubjectFromGifter({
                                                                        bookName: BOOK_NAME,
                                                                    })
                                                                }`}
                                                            </BookInviteEmailPreviewSubject>
                                                            <BookInviteEmailPreviewToLine>
                                                                {getDisplayBookInvitePreview(invite).toLine || `To: ${normalizeEmail(invite.recipientEmail)}`}
                                                            </BookInviteEmailPreviewToLine>
                                                            {getDisplayBookInvitePreview(invite).ccLine && (
                                                                <BookInviteEmailPreviewToLine>
                                                                    {getDisplayBookInvitePreview(invite).ccLine}
                                                                </BookInviteEmailPreviewToLine>
                                                            )}
                                                            <BookInviteEmailPreviewBody>
                                                                {getDisplayBookInvitePreview(invite).body || 'Preview unavailable.'}
                                                            </BookInviteEmailPreviewBody>
                                                        </>
                                                    )}
                                                </BookInviteEmailPreviewContainer>
                                            )}
                                        </BookInviteBody>
                                    </BookInviteCard>
                                ))}
                            </BookInviteChipList>
                            {remainingInviteAssignments > 0 && (
                                <BookInviteRecipientInputRow>
                                    <BookInviteRecipientInputContainer
                                        addButtonVisible={canAddBookInviteRecipientFromInput}
                                    >
                                        <PixelInput
                                            key={`book-invite-recipient-input-${bookInviteRecipientInputRenderKey}`}
                                            customCursorActive={customCursorActive}
                                            currentSessionId={currentSessionId}
                                            isValid={bookInviteValidationMessage.length === 0}
                                            isEmailField
                                            placeholderText={`Recipient #${bookInvites.length + 1} Email`}
                                            value={bookInviteRecipientInput}
                                            hasSound={hasSound}
                                            onCursorEnter={onCursorEnter}
                                            onCursorLeave={onCursorLeave}
                                            setInputFocused={setInputFocused}
                                            onChange={onBookInviteRecipientInputChange}
                                            onEnter={onAddBookInviteRecipientFromInput}
                                        />
                                    </BookInviteRecipientInputContainer>
                                    {canAddBookInviteRecipientFromInput && (
                                        <BookInviteRecipientAddButton
                                            type="button"
                                            customCursorActive={customCursorActive}
                                            className={HOVER_TARGET_CLASSNAME}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onActionButtonEnter,
                                                onMouseLeave: onActionButtonLeave,
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: onAddBookInviteRecipientFromInput })}
                                        >
                                            Add
                                        </BookInviteRecipientAddButton>
                                    )}
                                </BookInviteRecipientInputRow>
                            )}
                            {bookInviteValidationMessage.length > 0 && (
                                <BookInviteValidationText
                                    hasError
                                >
                                    {bookInviteValidationMessage}
                                </BookInviteValidationText>
                            )}
                        </>
                    )}
                    {!assignBookInviteGiftsNow && (
                        <BookInviteSubtext>
                            You can assign, resend, or revoke book invites later from your
                            {' '}
                            <BookInviteSubtextLink
                                type="button"
                                customCursorActive={customCursorActive}
                                className={HOVER_TARGET_CLASSNAME}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onLinkEnter,
                                    onMouseLeave: onLinkLeave,
                                })}
                                {...getPressEventHandlers({ onMouseDown: onOpenBookInvitesProfile })}
                            >
                                Profile
                            </BookInviteSubtextLink>
                            {' '}
                            page.
                        </BookInviteSubtext>
                    )}
                </BookInviteSectionBody>
            )}
        </BookInviteSectionContainer>
    );

    // ===== Renderer =====

    const InfoCheckpoint = useMemo(() => (
        <CheckpointFormContainer
            extraPadding
            ref={checkpointFormContainerRef}
        >
            <CheckpointTitle
                noMarginTop={stripePaymentSucceeded && !!stripeSucceededPaymentIntent}
            >
                {checkpointTitle}
            </CheckpointTitle>
            {!(userEmail || auth.currentUser?.email) && (
                <InputContainer>
                    <PixelInput
                        isRequired
                        isEmailField
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        isValid={!emailNotProvided && !emailInvalid}
                        placeholderText="Email"
                        value={checkoutEmail}
                        hasSound={hasSound}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={setInputFocused}
                        onChange={(value: string) => {
                            const normalizedEmail = value.trim();
                            setCheckoutEmail(value);
                            if (
                                validatedAnonymousCheckoutEmail != null
                                && normalizedEmail.toLowerCase() !== validatedAnonymousCheckoutEmail
                            ) {
                                setValidatedAnonymousCheckoutEmail(null);
                            }
                            if (normalizedEmail.length === 0) {
                                if (emailInvalid) setEmailInvalid(false);
                                return;
                            }
                            if (emailNotProvided) setEmailNotProvided(false);
                            const validEmail = validateEmail(normalizedEmail);
                            if (validEmail && emailInvalid) {
                                setEmailInvalid(false);
                            } else if (!validEmail && !emailInvalid) {
                                setEmailInvalid(true);
                            }
                        }}
                    />
                </InputContainer>
            )}
            {savedAddresses.length > 0 && (
                <InputContainer>
                    <PixelDropdown
                        customCursorActive={customCursorActive}
                        hasSound={hasSound}
                        currentValue={selectedSavedAddress}
                        values={dropdownSavedAddresses}
                        placeholderText="Saved Address"
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        onChange={onSelectSavedAddress}
                    />
                </InputContainer>
            )}
            <InputContainer>
                <PixelDropdown
                    isRequired
                    customCursorActive={customCursorActive}
                    hasSound={hasSound}
                    isValid={!countryNotSelected && addressIsValid}
                    currentValue={customerCountry}
                    values={countries}
                    placeholderText="Country"
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    onChange={onSelectCountry}
                />
            </InputContainer>
            <InputGroupContainer
                stackOnMobile
            >
                <InputContainer
                    paddingRight
                >
                    <PixelInput
                        isRequired
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        isValid={!firstNameNotProvided && (
                            customerFirstName.length === 0
                            || (
                                validateName(customerFirstName)
                                && addressIsValid
                            )
                        )}
                        placeholderText="First Name"
                        value={customerFirstName}
                        hasSound={hasSound}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={setInputFocused}
                        onChange={(value: string) => setCustomerFirstName(capitalizeFirstLetter(value))}
                    />
                </InputContainer>
                <InputContainer
                    paddingLeft
                >
                    <PixelInput
                        isRequired
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        isValid={!lastNameNotProvided && (
                            customerLastName.length === 0
                            || (
                                validateName(customerLastName)
                                && addressIsValid
                            )
                        )}
                        placeholderText="Last Name"
                        value={customerLastName}
                        hasSound={hasSound}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={setInputFocused}
                        onChange={(value: string) => setCustomerLastName(capitalizeFirstLetter(value))}
                    />
                </InputContainer>
            </InputGroupContainer>
            <InputContainer>
                <PixelInput
                    customCursorActive={customCursorActive}
                    currentSessionId={currentSessionId}
                    isValid={customerCompany.length === 0 || (
                        validateName(customerCompany)
                        && addressIsValid
                    )}
                    placeholderText="Company"
                    value={customerCompany}
                    hasSound={hasSound}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    setInputFocused={setInputFocused}
                    onChange={(value: string) => setCustomerCompany(capitalizeFirstLetter(value))}
                />
            </InputContainer>
            <InputContainer>
                <PixelInput
                    isRequired
                    customCursorActive={customCursorActive}
                    currentSessionId={currentSessionId}
                    isValid={!streetOneNotProvided && (
                        customerStreetOne.length === 0
                        || addressIsValid
                    )}
                    placeholderText="Address"
                    value={customerStreetOne}
                    hasSound={hasSound}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    setInputFocused={setInputFocused}
                    onChange={(value: string) => setCustomerStreetOne(value)}
                />
            </InputContainer>
            <InputContainer>
                <PixelInput
                    customCursorActive={customCursorActive}
                    currentSessionId={currentSessionId}
                    isValid={customerStreetTwo.length === 0 || addressIsValid}
                    placeholderText="Apartment, Suite, Street Line 2, Etc"
                    value={customerStreetTwo}
                    hasSound={hasSound}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    setInputFocused={setInputFocused}
                    onChange={(value: string) => setCustomerStreetTwo(value)}
                />
            </InputContainer>
            <InputGroupContainer
                stackOnMobile
            >
                <InputContainer>
                    <PixelInput
                        isRequired
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        isValid={!cityNotProvided && (
                            customerCity.length === 0
                            || addressIsValid
                        )}
                        placeholderText="City"
                        value={customerCity}
                        hasSound={hasSound}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={setInputFocused}
                        onChange={(value: string) => setCustomerCity(value)}
                    />
                </InputContainer>
                {states.length > 0 && (
                    <InputContainer
                        paddingLeft
                    >
                        <PixelDropdown
                            isRequired
                            customCursorActive={customCursorActive}
                            hasSound={hasSound}
                            isValid={!stateNotSelected && addressIsValid}
                            currentValue={customerState}
                            values={states}
                            placeholderText={statePlaceholderText}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            onChange={onSelectState}
                        />
                    </InputContainer>
                )}
                <InputContainer
                    paddingLeft
                >
                    <PixelInput
                        isRequired
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        isValid={!zipCodeNotProvided && (
                            customerZipCode.length === 0
                            || addressIsValid
                        )}
                        placeholderText="Zip Code"
                        value={customerZipCode}
                        hasSound={hasSound}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={setInputFocused}
                        onChange={(value: string) => setCustomerZipCode(value)}
                    />
                </InputContainer>
            </InputGroupContainer>
            <InputContainer>
                <PixelInput
                    isRequired
                    customCursorActive={customCursorActive}
                    currentSessionId={currentSessionId}
                    isValid={!phoneNumberNotProvided && (
                        customerPhone.length === 0
                        || isPhoneValidForCountry(customerPhone, customerCountry?.value)
                    )}
                    placeholderText="Phone"
                    value={customerPhone}
                    hasSound={hasSound}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    setInputFocused={setInputFocused}
                    onChange={(value: string) => setCustomerPhone(value)}
                />
            </InputContainer>
            {shouldRenderBookInviteSectionOnInfo && bookInviteAssignmentSection}
            <ButtonsContainer>
                <BackButton
                    data-testid="checkout-back-button"
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    detectTouchDevice={detectTouchDevice(document)}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: onBackButtonEnter,
                        onMouseLeave: onBackButtonLeave,
                    })}
                    {...getPressEventHandlers({ onMouseDown: onBackButtonClick })}
                >
                    <BackButtonIconContainer>
                        <ReactSVG
                            src={PixelChevronIcon}
                        />
                    </BackButtonIconContainer>
                    <BackButtonTextContainer>
                        Cart
                    </BackButtonTextContainer>
                </BackButton>
                <ActionButton
                    data-testid="checkout-action-button"
                    customCursorActive={customCursorActive}
                    noHover={actionButtonIsLoading || stripePaymentIsLoading}
                    detectTouchDevice={detectTouchDevice(document)}
                    {...(actionButtonInteractionProperties)}
                    {...(!(actionButtonIsLoading || stripePaymentIsLoading || !stripe || !elements)
                        ? {
                            onMouseDown: onActionButtonClick,
                        } : {})}
                >
                    {actionButtonIsLoading || stripePaymentIsLoading || !stripe || !elements
                        ? (
                            <ActionButtonLoadingContainer>
                                <SpinnerContainer
                                    src={Spinner}
                                    alt="spinner"
                                />
                                {actionButtonLoadingMessage.length > 0 && (
                                    <ActionButtonLoadingMessageText>
                                        {`${actionButtonLoadingMessage}${actionButtonMessageEllipsis}`}
                                    </ActionButtonLoadingMessageText>
                                )}
                            </ActionButtonLoadingContainer>
                        )
                        : 'Shipping'}
                </ActionButton>
            </ButtonsContainer>
        </CheckpointFormContainer>
    ), [
        customCursorActive,
        currentSessionId,
        checkpointFormContainerRef.current,
        hasSound,
        checkpointTitle,
        savedAddresses,
        snackbarData,
        selectedSavedAddress,
        dropdownSavedAddresses,
        checkoutEmail,
        emailNotProvided,
        emailInvalid,
        validatedAnonymousCheckoutEmail,
        userEmail,
        user?.id,
        countryNotSelected,
        customerCountry,
        countries,
        addressIsValid,
        firstNameNotProvided,
        customerFirstName,
        lastNameNotProvided,
        customerLastName,
        customerCompany,
        streetOneNotProvided,
        customerStreetOne,
        customerStreetTwo,
        cityNotProvided,
        customerCity,
        stateNotSelected,
        customerState,
        states,
        zipCodeNotProvided,
        customerZipCode,
        phoneNumberNotProvided,
        customerPhone,
        actionButtonIsLoading,
        actionButtonLoadingMessage,
        actionButtonMessageEllipsis,
        stripePaymentIsLoading,
        actionButtonInteractionProperties,
        stripePaymentSucceeded,
        stripeSucceededPaymentIntent,
        shouldRenderBookInviteSectionOnInfo,
        bookInviteAssignmentSection,
        stripe,
        elements,
    ]);

    const ShippingCheckpoint = useMemo(() => (
        <CheckpointFormContainer
            extraPadding
            ref={checkpointFormContainerRef}
        >
            <CheckpointTitle
                noMarginTop={stripePaymentSucceeded && !!stripeSucceededPaymentIntent}
            >
                {checkpointTitle}
            </CheckpointTitle>
            <InputContainer>
                <PixelRadioInput
                    customCursorActive={customCursorActive}
                    items={Array.from(shippingMethods.values())}
                    selected={selectedShippingRateID
                        ? shippingMethods.get(selectedShippingRateID)
                        : undefined}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    onChange={(value: IRadioItem) => setSelectedShippingRateID(value.value)}
                />
            </InputContainer>
            <ButtonsContainer>
                <BackButton
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    detectTouchDevice={detectTouchDevice(document)}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: onBackButtonEnter,
                        onMouseLeave: onBackButtonLeave,
                    })}
                    {...getPressEventHandlers({ onMouseDown: onBackButtonClick })}
                >
                    <BackButtonIconContainer>
                        <ReactSVG
                            src={PixelChevronIcon}
                        />
                    </BackButtonIconContainer>
                    <BackButtonTextContainer>
                        Info
                    </BackButtonTextContainer>
                </BackButton>
                <ActionButton
                    customCursorActive={customCursorActive}
                    noHover={actionButtonIsLoading || stripePaymentIsLoading}
                    detectTouchDevice={detectTouchDevice(document)}
                    {...(actionButtonInteractionProperties)}
                    {...(!(actionButtonIsLoading || stripePaymentIsLoading || !stripe || !elements)
                        ? {
                            onMouseDown: onActionButtonClick,
                        } : {})}
                >
                    {actionButtonIsLoading || stripePaymentIsLoading || !stripe || !elements
                        ? (
                            <ActionButtonLoadingContainer>
                                <SpinnerContainer
                                    src={Spinner}
                                    alt="spinner"
                                />
                                {actionButtonLoadingMessage.length > 0 && (
                                    <ActionButtonLoadingMessageText>
                                        {`${actionButtonLoadingMessage}${actionButtonMessageEllipsis}`}
                                    </ActionButtonLoadingMessageText>
                                )}
                            </ActionButtonLoadingContainer>
                        )
                        : 'Billing'}
                </ActionButton>
            </ButtonsContainer>
        </CheckpointFormContainer>
    ), [
        customCursorActive,
        checkpointFormContainerRef.current,
        checkpointTitle,
        shippingMethods,
        selectedShippingRateID,
        actionButtonIsLoading,
        actionButtonLoadingMessage,
        actionButtonMessageEllipsis,
        stripePaymentIsLoading,
        actionButtonInteractionProperties,
        stripePaymentSucceeded,
        stripeSucceededPaymentIntent,
        stripe,
        elements,
    ]);

    const BillingAndReviewCheckpoint = useMemo(() => (
        <CheckpointFormContainer
            extraPadding
            ref={checkpointFormContainerRef}
        >
            <CheckpointTitle
                noMarginTop={stripePaymentSucceeded && !!stripeSucceededPaymentIntent}
            >
                {checkpointTitle}
            </CheckpointTitle>
            <BillingContainer
                visible={checkpoint === CHECKOUT_CHECKPOINT.billing}
            >
                <CheckpointSubtitle>
                    Secure payment
                </CheckpointSubtitle>
                {!(userEmail || auth.currentUser?.email) && (
                    <InputContainer>
                        <PixelInput
                            isRequired
                            isEmailField
                            customCursorActive={customCursorActive}
                            currentSessionId={currentSessionId}
                            isValid={!emailNotProvided && !emailInvalid}
                            placeholderText="Email"
                            value={checkoutEmail}
                            hasSound={hasSound}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setInputFocused={setInputFocused}
                            onChange={(value: string) => {
                                const normalizedEmail = value.trim();
                                setCheckoutEmail(value);
                                if (
                                    validatedAnonymousCheckoutEmail != null
                                    && normalizedEmail.toLowerCase() !== validatedAnonymousCheckoutEmail
                                ) {
                                    setValidatedAnonymousCheckoutEmail(null);
                                }
                                if (normalizedEmail.length === 0) {
                                    if (emailInvalid) setEmailInvalid(false);
                                    return;
                                }
                                if (emailNotProvided) setEmailNotProvided(false);
                                const validEmail = validateEmail(normalizedEmail);
                                if (validEmail && emailInvalid) {
                                    setEmailInvalid(false);
                                } else if (!validEmail && !emailInvalid) {
                                    setEmailInvalid(true);
                                }
                            }}
                        />
                    </InputContainer>
                )}
                {savedStripePaymentMethods.length > 0 && (
                    <InputContainer>
                        <PixelDropdown
                            customCursorActive={customCursorActive}
                            hasSound={hasSound}
                            currentValue={selectedSavedStripePaymentMethod}
                            values={dropdownSavedStripePaymentMethods}
                            placeholderText="Saved Payment Methods"
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            onChange={onSelectSavedStripePaymentMethod}
                        />
                    </InputContainer>
                )}
                <PaymentElementContainer
                    visible={!selectedSavedStripePaymentMethod}
                >
                    <PaymentWidgetContainer
                        className={HOVER_TARGET_CLASSNAME}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onStripePaymentElementEnter,
                            onMouseLeave: onStripePaymentElementLeave,
                        })}
                    >
                        <PaymentElement
                            options={stripePaymentElementOptions}
                            onChange={(e) => onStripePaymentElementChange(e)}
                        />
                    </PaymentWidgetContainer>
                    {savePaymentMethodAllowedBySelection && (
                        <SavePaymentMethodContainer>
                            <PixelCheckbox
                                checked={savePaymentMethod}
                                text="Save payment method for future purchases"
                                customCursorActive={customCursorActive}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                onChange={(checked) => setSavePaymentMethod(checked)}
                            />
                        </SavePaymentMethodContainer>
                    )}
                    <PaymentTrustText>
                        Payments are processed by Stripe using industry-standard encryption (TLS).
                    </PaymentTrustText>
                </PaymentElementContainer>
                <CheckpointTitle
                    largeMarginTop
                >
                    Billing Address
                </CheckpointTitle>
                <CheckpointSubtitle>
                    {physicalItemsInCart.length === 0
                        ? 'Provide an address that matches your card or billing method for tax purposes'
                        : 'Select the address that matches your card or billing method'}
                </CheckpointSubtitle>
                {physicalItemsInCart.length > 0
                && (
                    <InputContainer
                        noMargin
                    >
                        <PixelRadioInput
                            customCursorActive={customCursorActive}
                            items={[
                                {
                                    title: 'Same as shipping address',
                                    value: BILLING_ADDRESS_TYPE.same,
                                },
                                {
                                    title: 'Use a different billing address',
                                    value: BILLING_ADDRESS_TYPE.different,
                                },
                            ]}
                            selected={selectedBillingAddressType}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            onChange={(value: IRadioItem) => {
                                setSelectedBillingAddressType(value);
                            }}
                        />
                    </InputContainer>
                )}
                {(
                    physicalItemsInCart.length === 0
                    || (
                        selectedBillingAddressType
                        && selectedBillingAddressType.value === BILLING_ADDRESS_TYPE.different
                    )
                ) && (
                    <BillingAddressContainer
                        physicalItemsInCart={physicalItemsInCart.length > 0}
                    >
                        {savedAddresses.length > 0 && (
                            <InputContainer>
                                <PixelDropdown
                                    customCursorActive={customCursorActive}
                                    hasSound={hasSound}
                                    currentValue={selectedSavedBillingAddress}
                                    values={dropdownSavedAddresses}
                                    placeholderText="Saved Address"
                                    onCursorEnter={onCursorEnter}
                                    onCursorLeave={onCursorLeave}
                                    onChange={onSelectSavedBillingAddress}
                                />
                            </InputContainer>
                        )}
                        <InputContainer>
                            <PixelDropdown
                                isRequired
                                customCursorActive={customCursorActive}
                                hasSound={hasSound}
                                isValid={!billingCountryNotSelected && billingAddressIsValid}
                                currentValue={customerBillingCountry}
                                values={countries}
                                placeholderText="Country"
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                onChange={onSelectBillingCountry}
                            />
                        </InputContainer>
                        <InputGroupContainer
                            stackOnMobile
                        >
                            <InputContainer
                                paddingRight
                            >
                                <PixelInput
                                    isRequired
                                    customCursorActive={customCursorActive}
                                    currentSessionId={currentSessionId}
                                    isValid={!billingFirstNameNotProvided && (
                                        customerBillingFirstName.length === 0
                                        || (
                                            validateName(customerBillingFirstName)
                                            && billingAddressIsValid
                                        )
                                    )}
                                    placeholderText="First Name"
                                    value={customerBillingFirstName}
                                    hasSound={hasSound}
                                    onCursorEnter={onCursorEnter}
                                    onCursorLeave={onCursorLeave}
                                    setInputFocused={setInputFocused}
                                    onChange={(value: string) => setCustomerBillingFirstName(capitalizeFirstLetter(value))}
                                />
                            </InputContainer>
                            <InputContainer
                                paddingLeft
                            >
                                <PixelInput
                                    isRequired
                                    customCursorActive={customCursorActive}
                                    currentSessionId={currentSessionId}
                                    isValid={!billingLastNameNotProvided && (
                                        customerBillingLastName.length === 0
                                        || (
                                            validateName(customerBillingLastName)
                                            && billingAddressIsValid
                                        )
                                    )}
                                    placeholderText="Last Name"
                                    value={customerBillingLastName}
                                    hasSound={hasSound}
                                    onCursorEnter={onCursorEnter}
                                    onCursorLeave={onCursorLeave}
                                    setInputFocused={setInputFocused}
                                    onChange={(value: string) => setCustomerBillingLastName(capitalizeFirstLetter(value))}
                                />
                            </InputContainer>
                        </InputGroupContainer>
                        <InputContainer>
                            <PixelInput
                                customCursorActive={customCursorActive}
                                currentSessionId={currentSessionId}
                                isValid={customerBillingCompany.length === 0 || (
                                    validateName(customerBillingCompany)
                                    && billingAddressIsValid
                                )}
                                placeholderText="Company"
                                value={customerBillingCompany}
                                hasSound={hasSound}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                setInputFocused={setInputFocused}
                                onChange={(value: string) => setCustomerBillingCompany(capitalizeFirstLetter(value))}
                            />
                        </InputContainer>
                        <InputContainer>
                            <PixelInput
                                isRequired
                                customCursorActive={customCursorActive}
                                currentSessionId={currentSessionId}
                                isValid={!billingStreetOneNotProvided && (
                                    customerBillingStreetOne.length === 0
                                    || billingAddressIsValid
                                )}
                                placeholderText="Address"
                                value={customerBillingStreetOne}
                                hasSound={hasSound}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                setInputFocused={setInputFocused}
                                onChange={(value: string) => setCustomerBillingStreetOne(value)}
                            />
                        </InputContainer>
                        <InputContainer>
                            <PixelInput
                                customCursorActive={customCursorActive}
                                currentSessionId={currentSessionId}
                                isValid={customerBillingStreetTwo.length === 0 || billingAddressIsValid}
                                placeholderText="Apartment, Suite, Street Line 2, Etc"
                                value={customerBillingStreetTwo}
                                hasSound={hasSound}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                setInputFocused={setInputFocused}
                                onChange={(value: string) => setCustomerBillingStreetTwo(value)}
                            />
                        </InputContainer>
                        <InputGroupContainer
                            stackOnMobile
                        >
                            <InputContainer>
                                <PixelInput
                                    isRequired
                                    customCursorActive={customCursorActive}
                                    currentSessionId={currentSessionId}
                                    isValid={!billingCityNotProvided && (
                                        customerBillingCity.length === 0
                                        || billingAddressIsValid
                                    )}
                                    placeholderText="City"
                                    value={customerBillingCity}
                                    hasSound={hasSound}
                                    onCursorEnter={onCursorEnter}
                                    onCursorLeave={onCursorLeave}
                                    setInputFocused={setInputFocused}
                                    onChange={(value: string) => setCustomerBillingCity(value)}
                                />
                            </InputContainer>
                            {billingStates.length > 0 && (
                                <InputContainer
                                    paddingLeft
                                >
                                    <PixelDropdown
                                        isRequired
                                        customCursorActive={customCursorActive}
                                        hasSound={hasSound}
                                        isValid={!billingStateNotSelected && billingAddressIsValid}
                                        currentValue={customerBillingState}
                                        values={billingStates}
                                        placeholderText={billingStatePlaceholderText}
                                        onCursorEnter={onCursorEnter}
                                        onCursorLeave={onCursorLeave}
                                        onChange={onSelectBillingState}
                                    />
                                </InputContainer>
                            )}
                            <InputContainer
                                paddingLeft
                            >
                                <PixelInput
                                    isRequired
                                    customCursorActive={customCursorActive}
                                    currentSessionId={currentSessionId}
                                    isValid={!billingZipCodeNotProvided && (
                                        customerBillingZipCode.length === 0
                                        || billingAddressIsValid
                                    )}
                                    placeholderText="Zip Code"
                                    value={customerBillingZipCode}
                                    hasSound={hasSound}
                                    onCursorEnter={onCursorEnter}
                                    onCursorLeave={onCursorLeave}
                                    setInputFocused={setInputFocused}
                                    onChange={(value: string) => setCustomerBillingZipCode(value)}
                                />
                            </InputContainer>
                        </InputGroupContainer>
                        <InputContainer>
                            <PixelInput
                                isRequired
                                customCursorActive={customCursorActive}
                                currentSessionId={currentSessionId}
                                isValid={!billingPhoneNumberNotProvided && (
                                    customerBillingPhone.length === 0
                                    || isPhoneValidForCountry(customerBillingPhone, customerBillingCountry?.value)
                                )}
                                placeholderText="Phone"
                                value={customerBillingPhone}
                                hasSound={hasSound}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                setInputFocused={setInputFocused}
                                onChange={(value: string) => setCustomerBillingPhone(value)}
                            />
                        </InputContainer>
                    </BillingAddressContainer>
                )}
                {shouldRenderBookInviteSectionOnBilling && bookInviteAssignmentSection}
                {!userIsAnonymous && !userIsSubscribedToMailingList && (
                    <MailingListContainer
                        largeMarginTop={physicalItemsInCart.length > 0}
                    >
                        <PixelCheckbox
                            checked={subscribeToMailingList}
                            text="Subscribe to mailing list for news and offers"
                            customCursorActive={customCursorActive}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            onChange={(checked) => setSubscribeToMailingList(checked)}
                        />
                    </MailingListContainer>
                )}
            </BillingContainer>
            <ReviewContainer
                visible={checkpoint === CHECKOUT_CHECKPOINT.review}
            >
                <ReviewShippingContainer>
                    <ReviewShippingDetailContainer>
                        <ReviewShippingDetail>
                            <ReviewShippingDetailTitle>
                                Email
                            </ReviewShippingDetailTitle>
                            <ReviewShippingDetailBody>
                                <ReviewShippingDetailBodyLine>
                                    {getCheckoutEmail() || 'No Email'}
                                </ReviewShippingDetailBodyLine>
                            </ReviewShippingDetailBody>
                        </ReviewShippingDetail>
                        {addressIsValid
                        && physicalItemsInCart.length > 0 && (
                            <ReviewShippingDetail>
                                <ReviewShippingDetailTitle>
                                    Ship To
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    <ReviewShippingDetailEditButtonContainer>
                                        <ReviewShippingDetailEditButton
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            detectTouchDevice={detectTouchDevice(document)}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onActionButtonEnter,
                                                onMouseLeave: onActionButtonLeave,
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: (e) => onReviewEditButtonClick(CHECKOUT_CHECKPOINT.info, e) })}
                                        >
                                            Edit
                                        </ReviewShippingDetailEditButton>
                                    </ReviewShippingDetailEditButtonContainer>
                                    <ReviewShippingDetailBodyLine>
                                        {`${customerFirstName} ${customerLastName}`}
                                    </ReviewShippingDetailBodyLine>
                                    {customerCompany.length > 0 && (
                                        <ReviewShippingDetailBodyLine>
                                            <ReviewShippingDetailBodyLineHighlight>
                                                {customerCompany}
                                            </ReviewShippingDetailBodyLineHighlight>
                                        </ReviewShippingDetailBodyLine>
                                    )}
                                    <ReviewShippingDetailBodyLine>
                                        <ReviewShippingDetailBodyLineHighlight>
                                            {customerStreetOne}
                                        </ReviewShippingDetailBodyLineHighlight>
                                    </ReviewShippingDetailBodyLine>
                                    {customerStreetTwo.length > 0 && (
                                        <ReviewShippingDetailBodyLine>
                                            <ReviewShippingDetailBodyLineHighlight>
                                                {customerStreetTwo}
                                            </ReviewShippingDetailBodyLineHighlight>
                                        </ReviewShippingDetailBodyLine>
                                    )}
                                    <ReviewShippingDetailBodyLine>
                                        <ReviewShippingDetailBodyLineHighlight>
                                            {`${
                                                customerCity
                                            }${customerState
                                                ? ` ${customerState.name}`
                                                : ''
                                            } ${
                                                customerZipCode
                                            }, ${
                                                customerCountry?.name
                                            }`}
                                        </ReviewShippingDetailBodyLineHighlight>
                                    </ReviewShippingDetailBodyLine>
                                    <ReviewShippingDetailBodyLine>
                                        <ReviewShippingDetailBodyLineHighlight>
                                            {customerPhone.substring(0, 2) === '00'
                                                ? `+${customerPhone.substring(2)}`
                                                : customerPhone}
                                        </ReviewShippingDetailBodyLineHighlight>
                                    </ReviewShippingDetailBodyLine>
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                        )}
                        {selectedShippingRateID && (
                            <ReviewShippingDetail>
                                <ReviewShippingDetailTitle>
                                    Shipping Method
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    <ReviewShippingDetailEditButtonContainer>
                                        <ReviewShippingDetailEditButton
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            detectTouchDevice={detectTouchDevice(document)}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onActionButtonEnter,
                                                onMouseLeave: onActionButtonLeave,
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: (e) => onReviewEditButtonClick(CHECKOUT_CHECKPOINT.shipping, e) })}
                                        >
                                            Edit
                                        </ReviewShippingDetailEditButton>
                                    </ReviewShippingDetailEditButtonContainer>
                                    <ReviewShippingDetailBodyLine>
                                        {selectedShippingRateID &&  shippingMethods.get(selectedShippingRateID)?.title}
                                    </ReviewShippingDetailBodyLine>
                                    <ReviewShippingDetailBodyLine>
                                        <ReviewShippingDetailBodyLineHighlight>
                                            {` ${selectedShippingRateID && shippingMethods.get(selectedShippingRateID)?.detail}`}
                                        </ReviewShippingDetailBodyLineHighlight>
                                    </ReviewShippingDetailBodyLine>
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                        )}
                        {selectedBillingAddressType
                        && (
                            <ReviewShippingDetail
                                noSeparator={bookInvites.length === 0}
                            >
                                <ReviewShippingDetailTitle>
                                    Billing Method
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    <ReviewShippingDetailEditButtonContainer>
                                        <ReviewShippingDetailEditButton
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            detectTouchDevice={detectTouchDevice(document)}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onActionButtonEnter,
                                                onMouseLeave: onActionButtonLeave,
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: (e) => onReviewEditButtonClick(CHECKOUT_CHECKPOINT.billing, e) })}
                                        >
                                            Edit
                                        </ReviewShippingDetailEditButton>
                                    </ReviewShippingDetailEditButtonContainer>
                                    {stripePaymentMethodType && (
                                        <ReviewShippingDetailBodyLine>
                                            {capitalizeFirstLetter(stripePaymentMethodType.replace('_', ' ').toLowerCase())}
                                        </ReviewShippingDetailBodyLine>
                                    )}
                                    <ReviewShippingDetailBodyLine>
                                        <ReviewShippingDetailBodyLineHighlight>
                                            {selectedBillingAddressType.value === BILLING_ADDRESS_TYPE.same
                                                // eslint-disable-next-line max-len
                                                ? `${
                                                    customerStreetOne
                                                }${
                                                    customerStreetTwo
                                                        ? `, ${customerStreetTwo}`
                                                        : ''
                                                }, ${
                                                    customerCity
                                                }${
                                                    customerState
                                                        ? ` ${customerState.name}`
                                                        : ''
                                                } ${
                                                    customerZipCode
                                                }, ${
                                                    customerCountry?.name
                                                }, ${
                                                    customerPhone.substring(0, 2) === '00'
                                                        ? `+${customerPhone.substring(2)}`
                                                        : customerPhone}`
                                                // eslint-disable-next-line max-len
                                                : `${
                                                    customerBillingStreetOne
                                                }${
                                                    customerBillingStreetTwo
                                                        ? `, ${customerBillingStreetTwo}`
                                                        : ''
                                                }, ${
                                                    customerBillingCity
                                                }${
                                                    customerBillingState
                                                        ? ` ${customerBillingState.name}`
                                                        : ''
                                                } ${
                                                    customerBillingZipCode
                                                }, ${
                                                    customerBillingCountry?.name
                                                }, ${
                                                    customerBillingPhone.substring(0, 2) === '00'
                                                        ? `+${customerBillingPhone.substring(2)}`
                                                        : customerBillingPhone}`}
                                        </ReviewShippingDetailBodyLineHighlight>
                                    </ReviewShippingDetailBodyLine>
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                        )}
                        {bookInvites.length > 0 && (
                            <ReviewShippingDetail
                                noSeparator
                            >
                                <ReviewShippingDetailTitle>
                                    Book Invites
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    <ReviewShippingDetailEditButtonContainer>
                                        <ReviewShippingDetailEditButton
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            detectTouchDevice={detectTouchDevice(document)}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onActionButtonEnter,
                                                onMouseLeave: onActionButtonLeave,
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: (e) => onReviewBookInvitesEditButtonClick(e) })}
                                        >
                                            Edit
                                        </ReviewShippingDetailEditButton>
                                    </ReviewShippingDetailEditButtonContainer>
                                    {bookInvites.map((invite: IBookInviteWithLocalId, index: number) => {
                                        const trimmedRecipientName = invite.recipientName.trim();
                                        const trimmedInviteMessage = invite.inviteMessage.trim();
                                        return (
                                            <ReviewBookInviteEntry
                                                key={invite.id}
                                                hasTopGap={index > 0}
                                            >
                                                <ReviewShippingDetailBodyLine>
                                                    <ReviewBookInviteIndexText>
                                                        {`Invite #${index + 1}`}
                                                    </ReviewBookInviteIndexText>
                                                </ReviewShippingDetailBodyLine>
                                                <ReviewShippingDetailBodyLine>
                                                    <ReviewBookInviteBodyText>
                                                        {`Email: ${invite.recipientEmail}`}
                                                    </ReviewBookInviteBodyText>
                                                </ReviewShippingDetailBodyLine>
                                                {trimmedRecipientName.length > 0 && (
                                                    <ReviewShippingDetailBodyLine>
                                                        <ReviewBookInviteBodyText>
                                                            {`Name: ${trimmedRecipientName}`}
                                                        </ReviewBookInviteBodyText>
                                                    </ReviewShippingDetailBodyLine>
                                                )}
                                                {trimmedInviteMessage.length > 0 && (
                                                    <ReviewShippingDetailBodyLine>
                                                        <ReviewBookInviteBodyText>
                                                            {`Message: ${trimmedInviteMessage}`}
                                                        </ReviewBookInviteBodyText>
                                                    </ReviewShippingDetailBodyLine>
                                                )}
                                            </ReviewBookInviteEntry>
                                        );
                                    })}
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                        )}
                    </ReviewShippingDetailContainer>
                    {orderCost && (
                        <ReviewTransactionDetailContainer>
                            <ReviewTransactionDetail>
                                <ReviewTransactionDetailTitle>
                                    Subtotal
                                </ReviewTransactionDetailTitle>
                                <ReviewTransactionDetailBody>
                                    {`$${roundToNDecimals(orderCost.subtotal / CENTS_IN_A_DOLLAR, 2)}`}
                                </ReviewTransactionDetailBody>
                            </ReviewTransactionDetail>
                            {physicalItemsInCart.length > 0
                            && orderCost.shipping != null && (
                                <ReviewTransactionDetail>
                                    <ReviewTransactionDetailTitle>
                                        Shipping
                                    </ReviewTransactionDetailTitle>
                                    <ReviewTransactionDetailBody>
                                        {`$${roundToNDecimals(orderCost.shipping / CENTS_IN_A_DOLLAR, 2)}`}
                                    </ReviewTransactionDetailBody>
                                </ReviewTransactionDetail>
                            )}
                            {orderCost.tax != null && (
                                <ReviewTransactionDetail>
                                    <ReviewTransactionDetailTitle>
                                        Tax
                                    </ReviewTransactionDetailTitle>
                                    <ReviewTransactionDetailBody>
                                        {`$${roundToNDecimals(orderCost.tax / CENTS_IN_A_DOLLAR, 2)}`}
                                    </ReviewTransactionDetailBody>
                                </ReviewTransactionDetail>
                            )}
                            {orderCost.total != null && (
                                <ReviewTransactionTotal>
                                    <ReviewTransactionTotalTitle>
                                        Total
                                    </ReviewTransactionTotalTitle>
                                    <ReviewTransactionTotalBody>
                                        {`$${roundToNDecimals(orderCost.total / CENTS_IN_A_DOLLAR, 2)}`}
                                    </ReviewTransactionTotalBody>
                                </ReviewTransactionTotal>
                            )}
                        </ReviewTransactionDetailContainer>
                    )}
                </ReviewShippingContainer>
            </ReviewContainer>
            <ButtonsContainer>
                <BackButton
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    detectTouchDevice={detectTouchDevice(document)}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: onBackButtonEnter,
                        onMouseLeave: onBackButtonLeave,
                    })}
                    {...getPressEventHandlers({ onMouseDown: onBackButtonClick })}
                >
                    <BackButtonIconContainer>
                        <ReactSVG
                            src={PixelChevronIcon}
                        />
                    </BackButtonIconContainer>
                    <BackButtonTextContainer>
                        {checkpoint === CHECKOUT_CHECKPOINT.billing
                        && physicalItemsInCart.length > 0 && (
                            'Shipping'
                        )}
                        {checkpoint === CHECKOUT_CHECKPOINT.billing
                        && physicalItemsInCart.length === 0 && (
                            'Cart'
                        )}
                        {checkpoint === CHECKOUT_CHECKPOINT.review
                        && (
                            'Billing'
                        )}
                    </BackButtonTextContainer>
                </BackButton>
                <ActionButton
                    customCursorActive={customCursorActive}
                    noHover={actionButtonIsLoading || stripePaymentIsLoading}
                    detectTouchDevice={detectTouchDevice(document)}
                    {...(actionButtonInteractionProperties)}
                    {...(!(actionButtonIsLoading || stripePaymentIsLoading || !stripe || !elements)
                        ? {
                            onMouseDown: onActionButtonClick,
                        } : {})}
                >
                    {actionButtonIsLoading || stripePaymentIsLoading || !stripe || !elements
                        ? (
                            <ActionButtonLoadingContainer>
                                <SpinnerContainer
                                    src={Spinner}
                                    alt="spinner"
                                />
                                {actionButtonLoadingMessage.length > 0 && (
                                    <ActionButtonLoadingMessageText>
                                        {`${actionButtonLoadingMessage}${actionButtonMessageEllipsis}`}
                                    </ActionButtonLoadingMessageText>
                                )}
                            </ActionButtonLoadingContainer>
                        )
                        : `${checkpoint === CHECKOUT_CHECKPOINT.billing ? 'Review' : 'Buy'}`}
                </ActionButton>
            </ButtonsContainer>
            {checkpoint === CHECKOUT_CHECKPOINT.review && (
                <>
                    <CheckoutPolicyText>
                        By completing your purchase, you agree to our
                        {' '}
                        <CheckoutPolicyLink
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            href={`/${PAGE_ROUTE.termsOfUse}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onTermsOfUseLinkEnter,
                                onMouseLeave: onLinkLeave,
                            })}
                        >
                            Terms
                        </CheckoutPolicyLink>
                        {' '}
                        and no-refund policy.
                    </CheckoutPolicyText>
                    <CheckoutPolicyText>
                        Instant email receipt after payment.
                    </CheckoutPolicyText>
                </>
            )}
        </CheckpointFormContainer>
    ), [
        customCursorActive,
        currentSessionId,
        checkpointFormContainerRef.current,
        checkpointTitle,
        hasSound,
        snackbarData,
        stripePaymentElementOptions,
        physicalItemsInCart,
        selectedBillingAddressType,
        savedAddresses,
        selectedSavedBillingAddress,
        dropdownSavedAddresses,
        savedStripePaymentMethods,
        dropdownSavedStripePaymentMethods,
        selectedSavedStripePaymentMethod,
        checkoutEmail,
        emailNotProvided,
        emailInvalid,
        validatedAnonymousCheckoutEmail,
        userEmail,
        user?.id,
        billingCountryNotSelected,
        customerBillingCountry,
        countries,
        billingFirstNameNotProvided,
        customerBillingFirstName,
        billingLastNameNotProvided,
        customerBillingLastName,
        customerBillingCompany,
        billingStreetOneNotProvided,
        customerBillingStreetOne,
        billingAddressIsValid,
        customerBillingStreetTwo,
        billingCityNotProvided,
        customerBillingCity,
        billingStates,
        billingStateNotSelected,
        customerBillingState,
        billingZipCodeNotProvided,
        customerBillingZipCode,
        billingPhoneNumberNotProvided,
        customerBillingPhone,
        addressIsValid,
        customerFirstName,
        customerLastName,
        customerCompany,
        customerStreetOne,
        customerStreetTwo,
        customerCity,
        customerState,
        customerZipCode,
        customerCountry,
        customerPhone,
        selectedShippingRateID,
        shippingMethods,
        subscribeToMailingList,
        savePaymentMethod,
        shouldRenderBookInviteSectionOnBilling,
        bookInviteAssignmentSection,
        stripePaymentSucceeded,
        orderCost,
        stripePaymentMethodType,
        actionButtonIsLoading,
        actionButtonLoadingMessage,
        actionButtonMessageEllipsis,
        stripePaymentIsLoading,
        actionButtonInteractionProperties,
        stripePaymentSucceeded,
        stripeSucceededPaymentIntent,
        stripe,
        elements,
    ]);

    const ConfirmCheckpoint = useMemo(() => {
        let stripePaymentIntentPhone: string | undefined;
        let billingAddress: IAddress | undefined;
        if (stripeSucceededPaymentIntent?.shipping?.phone) {
            stripePaymentIntentPhone = stripeSucceededPaymentIntent.shipping.phone.substring(0, 2) === '00'
                ? `+${stripeSucceededPaymentIntent.shipping.phone.substring(2)}`
                : stripeSucceededPaymentIntent.shipping.phone;
        }
        if (
            stripeSucceededPaymentIntent
            && stripeSucceededPaymentIntent.metadata.billingAddress
        ) {
            billingAddress = JSON.parse(stripeSucceededPaymentIntent.metadata.billingAddress) as IAddress;
            if (
                billingAddress.phone
                && !stripePaymentIntentPhone
            ) {
                stripePaymentIntentPhone = billingAddress.phone.substring(0, 2) === '00'
                    ? `+${billingAddress.phone.substring(2)}`
                    : billingAddress.phone;
            }
        }
        let billingMethodLabel: string | undefined;
        if (stripePaymentMethod) {
            billingMethodLabel = stripePaymentMethod.type === 'card' && stripePaymentMethod.card
                ? `${
                    capitalizeFirstLetter(stripePaymentMethod.card.brand.replace('_', ' ').toLowerCase())
                } ending with ${
                    stripePaymentMethod.card.last4
                }`
                : capitalizeFirstLetter(stripePaymentMethod.type.replace('_', ' ').toLowerCase());
        }
        return (
            <CheckpointFormContainer
                extraPadding
                ref={checkpointFormContainerRef}
            >
                <CheckpointTitle
                    noMarginTop={stripePaymentSucceeded && !!stripeSucceededPaymentIntent}
                >
                    {checkpointTitle}
                </CheckpointTitle>
                <ReviewContainer
                    visible
                >
                    <ReviewShippingContainer>
                        <ReviewShippingDetailContainer>
                            <ReviewShippingDetail>
                                <ReviewShippingDetailTitle>
                                    Order Number
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    <ReviewShippingDetailBodyLine>
                                        {orderSaleItem?.receiptNumber
                                            ? `#${orderSaleItem.receiptNumber}`
                                            : (
                                                <PlaceholderBox
                                                    maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                    height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                />
                                            )}
                                    </ReviewShippingDetailBodyLine>
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                            <ReviewShippingDetail>
                                <ReviewShippingDetailTitle>
                                    Date
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    <ReviewShippingDetailBodyLine>
                                        {orderSaleItem
                                            ? moment.utc(orderSaleItem.timestamp)
                                                .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
                                                .format('dddd D MMMM YYYY')
                                            : (
                                                <PlaceholderBox
                                                    maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                    height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                />
                                            )}
                                    </ReviewShippingDetailBodyLine>
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                            <ReviewShippingDetail>
                                <ReviewShippingDetailTitle>
                                    Receipt
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    <ReviewShippingDetailBodyLine>
                                        {stripeSucceededPaymentIntent?.receipt_email
                                            ? `Sent to ${stripeSucceededPaymentIntent.receipt_email}`
                                            : (
                                                <PlaceholderBox
                                                    maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                    height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                />
                                            )}
                                    </ReviewShippingDetailBodyLine>
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                            <ReviewShippingDetail>
                                <ReviewShippingDetailTitle>
                                    Support
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    <ReviewShippingDetailBodyLine>
                                        Need help? Contact
                                        {' '}
                                        <CheckoutPolicyLink
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onLinkEnter,
                                                onMouseLeave: onLinkLeave,
                                            })}
                                        >
                                            {SUPPORT_EMAIL_ADDRESS}
                                        </CheckoutPolicyLink>
                                    </ReviewShippingDetailBodyLine>
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                            <ReviewShippingDetail>
                                <ReviewShippingDetailTitle>
                                    Phone
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    <ReviewShippingDetailBodyLine>
                                        {stripePaymentIntentPhone || (
                                            <PlaceholderBox
                                                maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                            />
                                        )}
                                    </ReviewShippingDetailBodyLine>
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                            <ReviewShippingDetail>
                                <ReviewShippingDetailTitle>
                                    Receipt Link
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    {orderSaleItem?.receiptURL
                                        ? (
                                            <ReviewShippingDetailBodyLink
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                href={orderSaleItem.receiptURL}
                                                target="_blank"
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onLinkEnter,
                                                    onMouseLeave: onLinkLeave,
                                                })}
                                                {...getPressEventHandlers({
                                                    onMouseDown: () => {
                                                        if (currentSessionId) {
                                                            // Record user action
                                                            recordUserAction({
                                                                test: isTestMode(),
                                                                type: USER_ACTION_TYPE.viewCheckoutReceipt,
                                                                sessionId: currentSessionId,
                                                            });
                                                        }
                                                    },
                                                })}
                                            >
                                                View Receipt
                                            </ReviewShippingDetailBodyLink>
                                        ) : (
                                            <PlaceholderBox
                                                maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                            />
                                        )}
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                            {physicalItemsInCart.length > 0
                            && (
                                <ReviewShippingDetail>
                                    <ReviewShippingDetailTitle>
                                        Ship To
                                    </ReviewShippingDetailTitle>
                                    <ReviewShippingDetailBody>
                                        <ReviewShippingDetailBodyLine>
                                            {stripeSucceededPaymentIntent?.shipping?.name
                                                ? stripeSucceededPaymentIntent.shipping.name
                                                : (
                                                    <PlaceholderBox
                                                        maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                        height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                    />
                                                )}
                                        </ReviewShippingDetailBodyLine>
                                        <ReviewShippingDetailBodyLine>
                                            <ReviewShippingDetailBodyLineHighlight>
                                                {stripeSucceededPaymentIntent?.shipping?.address?.line1
                                                    ? stripeSucceededPaymentIntent.shipping.address.line1
                                                    : (
                                                        <PlaceholderBox
                                                            maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                            height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                        />
                                                    )}
                                            </ReviewShippingDetailBodyLineHighlight>
                                        </ReviewShippingDetailBodyLine>
                                        {stripeSucceededPaymentIntent?.shipping?.address?.line2
                                        && stripeSucceededPaymentIntent.shipping.address.line2.length > 0 && (
                                            <ReviewShippingDetailBodyLine>
                                                <ReviewShippingDetailBodyLineHighlight>
                                                    {stripeSucceededPaymentIntent.shipping.address.line2}
                                                </ReviewShippingDetailBodyLineHighlight>
                                            </ReviewShippingDetailBodyLine>
                                        )}
                                        <ReviewShippingDetailBodyLine>
                                            <ReviewShippingDetailBodyLineHighlight>
                                                {stripeSucceededPaymentIntent?.shipping?.address
                                                    ? `${
                                                        stripeSucceededPaymentIntent.shipping.address.city
                                                    } ${
                                                        stripeSucceededPaymentIntent.shipping.address.state
                                                    } ${
                                                        stripeSucceededPaymentIntent.shipping.address.postal_code
                                                    }, ${
                                                        stripeSucceededPaymentIntent.shipping.address.country
                                                    }`
                                                    : (
                                                        <PlaceholderBox
                                                            maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                            height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                        />
                                                    )}
                                            </ReviewShippingDetailBodyLineHighlight>
                                        </ReviewShippingDetailBodyLine>
                                    </ReviewShippingDetailBody>
                                </ReviewShippingDetail>
                            )}
                            {physicalItemsInCart.length > 0
                            && (
                                <ReviewShippingDetail>
                                    <ReviewShippingDetailTitle>
                                        Shipping Carrier
                                    </ReviewShippingDetailTitle>
                                    <ReviewShippingDetailBody>
                                        <ReviewShippingDetailBodyLine>
                                            {stripeSucceededPaymentIntent?.shipping?.carrier
                                                ? stripeSucceededPaymentIntent.shipping.carrier
                                                : (
                                                    <PlaceholderBox
                                                        maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                        height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                    />
                                                )}
                                        </ReviewShippingDetailBodyLine>
                                    </ReviewShippingDetailBody>
                                </ReviewShippingDetail>
                            )}
                            {physicalItemsInCart.length > 0 && (
                                <ReviewShippingDetail>
                                    <ReviewShippingDetailTitle>
                                        Tracking Number
                                    </ReviewShippingDetailTitle>
                                    <ReviewShippingDetailBody>
                                        <ReviewShippingDetailBodyLine>
                                            {orderSaleItem?.shipping?.trackingNumber
                                                ? orderSaleItem.shipping.trackingNumber
                                                : (
                                                    <PlaceholderBox
                                                        maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                        height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                    />
                                                )}
                                        </ReviewShippingDetailBodyLine>
                                    </ReviewShippingDetailBody>
                                </ReviewShippingDetail>
                            )}
                            {physicalItemsInCart.length > 0 && (
                                <ReviewShippingDetail
                                    noSeparator={!(billingAddress && stripePaymentMethod)}
                                >
                                    <ReviewShippingDetailTitle>
                                        Tracking Link
                                    </ReviewShippingDetailTitle>
                                    <ReviewShippingDetailBody>
                                        {orderSaleItem?.shipping?.trackingURLProvider
                                            ? (
                                                <ReviewShippingDetailBodyLink
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    customCursorActive={customCursorActive}
                                                    href={orderSaleItem.shipping.trackingURLProvider}
                                                    target="_blank"
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: onLinkEnter,
                                                        onMouseLeave: onLinkLeave,
                                                    })}
                                                    {...getPressEventHandlers({
                                                        onMouseDown: () => {
                                                            if (currentSessionId) {
                                                                // Record user action
                                                                recordUserAction({
                                                                    test: isTestMode(),
                                                                    type: USER_ACTION_TYPE.viewTrackingWebsite,
                                                                    sessionId: currentSessionId,
                                                                });
                                                            }
                                                        },
                                                    })}
                                                >
                                                    Track your order
                                                </ReviewShippingDetailBodyLink>
                                            ) : (
                                                <PlaceholderBox
                                                    height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                />
                                            )}
                                    </ReviewShippingDetailBody>
                                </ReviewShippingDetail>
                            )}
                            <ReviewShippingDetail
                                noSeparator
                            >
                                <ReviewShippingDetailTitle>
                                    Billing Method
                                </ReviewShippingDetailTitle>
                                <ReviewShippingDetailBody>
                                    <ReviewShippingDetailBodyLine>
                                        {billingMethodLabel || (
                                            <PlaceholderBox
                                                maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                            />
                                        )}
                                    </ReviewShippingDetailBodyLine>
                                    <ReviewShippingDetailBodyLine>
                                        <ReviewShippingDetailBodyLineHighlight>
                                            {billingAddress
                                                ? `${
                                                    billingAddress.street1
                                                }${
                                                    billingAddress.street2
                                                        ? `, ${billingAddress.street2}`
                                                        : ''
                                                }, ${
                                                    billingAddress.city
                                                }${
                                                    billingAddress.state
                                                        ? ` ${billingAddress.state}`
                                                        : ''
                                                } ${
                                                    billingAddress.zip
                                                }, ${
                                                    billingAddress.country
                                                }${billingAddress.phone
                                                    ? `, ${
                                                        billingAddress.phone.substring(0, 2) === '00'
                                                            ? `+${billingAddress.phone.substring(2)}`
                                                            : billingAddress.phone
                                                    }`
                                                    : ''
                                                }` : (
                                                    <PlaceholderBox
                                                        maxWidth={CONFIRMATION_DETAIL_PLACEHOLDER_MAX_WIDTH}
                                                        height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                    />
                                                )}
                                        </ReviewShippingDetailBodyLineHighlight>
                                    </ReviewShippingDetailBodyLine>
                                </ReviewShippingDetailBody>
                            </ReviewShippingDetail>
                            {bookInvites.length > 0 && (
                                <ReviewShippingDetail
                                    noSeparator
                                >
                                    <ReviewShippingDetailTitle>
                                        Book Invites
                                    </ReviewShippingDetailTitle>
                                    <ReviewShippingDetailBody>
                                        {bookInvites.map((invite: IBookInviteWithLocalId, index: number) => {
                                            const trimmedRecipientName = invite.recipientName.trim();
                                            const trimmedInviteMessage = invite.inviteMessage.trim();
                                            return (
                                                <ReviewBookInviteEntry
                                                    key={invite.id}
                                                    hasTopGap={index > 0}
                                                >
                                                    <ReviewShippingDetailBodyLine>
                                                        <ReviewBookInviteIndexText>
                                                            {`Invite #${index + 1}`}
                                                        </ReviewBookInviteIndexText>
                                                    </ReviewShippingDetailBodyLine>
                                                    <ReviewShippingDetailBodyLine>
                                                        <ReviewBookInviteBodyText>
                                                            {`Email: ${invite.recipientEmail}`}
                                                        </ReviewBookInviteBodyText>
                                                    </ReviewShippingDetailBodyLine>
                                                    {trimmedRecipientName.length > 0 && (
                                                        <ReviewShippingDetailBodyLine>
                                                            <ReviewBookInviteBodyText>
                                                                {`Name: ${trimmedRecipientName}`}
                                                            </ReviewBookInviteBodyText>
                                                        </ReviewShippingDetailBodyLine>
                                                    )}
                                                    {trimmedInviteMessage.length > 0 && (
                                                        <ReviewShippingDetailBodyLine>
                                                            <ReviewBookInviteBodyText>
                                                                {`Message: ${trimmedInviteMessage}`}
                                                            </ReviewBookInviteBodyText>
                                                        </ReviewShippingDetailBodyLine>
                                                    )}
                                                </ReviewBookInviteEntry>
                                            );
                                        })}
                                    </ReviewShippingDetailBody>
                                </ReviewShippingDetail>
                            )}
                        </ReviewShippingDetailContainer>
                        <ReviewTransactionDetailContainer>
                            <ReviewTransactionDetail>
                                <ReviewTransactionDetailTitle>
                                    Subtotal
                                </ReviewTransactionDetailTitle>
                                <ReviewTransactionDetailBody>
                                    {orderCost
                                        ? `$${roundToNDecimals(orderCost.subtotal / CENTS_IN_A_DOLLAR, 2)}`
                                        : (
                                            <PlaceholderBox
                                                maxWidth={CONFIRMATION_PRICE_PLACEHOLDER_MAX_WIDTH}
                                                height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                margin="0 0 0 auto"
                                            />
                                        )}
                                </ReviewTransactionDetailBody>
                            </ReviewTransactionDetail>
                            {physicalItemsInCart.length > 0 && (
                                <ReviewTransactionDetail>
                                    <ReviewTransactionDetailTitle>
                                        Shipping
                                    </ReviewTransactionDetailTitle>
                                    <ReviewTransactionDetailBody>
                                        {orderCost && orderCost.shipping != null
                                            ? `$${roundToNDecimals(orderCost.shipping / CENTS_IN_A_DOLLAR, 2)}`
                                            : (
                                                <PlaceholderBox
                                                    maxWidth={CONFIRMATION_PRICE_PLACEHOLDER_MAX_WIDTH}
                                                    height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                    margin="0 0 0 auto"
                                                />
                                            )}
                                    </ReviewTransactionDetailBody>
                                </ReviewTransactionDetail>
                            )}
                            <ReviewTransactionDetail>
                                <ReviewTransactionDetailTitle>
                                    Tax
                                </ReviewTransactionDetailTitle>
                                <ReviewTransactionDetailBody>
                                    {orderCost && orderCost.tax != null
                                        ? `$${roundToNDecimals(orderCost.tax / CENTS_IN_A_DOLLAR, 2)}`
                                        : (
                                            <PlaceholderBox
                                                maxWidth={CONFIRMATION_PRICE_PLACEHOLDER_MAX_WIDTH}
                                                height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                margin="0 0 0 auto"
                                            />
                                        )}
                                </ReviewTransactionDetailBody>
                            </ReviewTransactionDetail>
                            <ReviewTransactionTotal>
                                <ReviewTransactionTotalTitle>
                                    Total
                                </ReviewTransactionTotalTitle>
                                <ReviewTransactionTotalBody>
                                    {orderCost && orderCost.total != null
                                        ? `$${roundToNDecimals(orderCost.total / CENTS_IN_A_DOLLAR, 2)}`
                                        : (
                                            <PlaceholderBox
                                                maxWidth={CONFIRMATION_PRICE_PLACEHOLDER_MAX_WIDTH}
                                                height={CONFIRMATION_DETAIL_PLACEHOLDER_HEIGHT}
                                                margin="0 0 0 auto"
                                            />
                                        )}
                                </ReviewTransactionTotalBody>
                            </ReviewTransactionTotal>
                        </ReviewTransactionDetailContainer>
                    </ReviewShippingContainer>
                </ReviewContainer>
            </CheckpointFormContainer>
        );
    }, [
        customCursorActive,
        checkpointFormContainerRef,
        checkpointTitle,
        stripeSucceededPaymentIntent,
        stripePaymentMethod,
        orderSaleItem,
        stripePaymentSucceeded,
        orderCost,
        bookInvites,
    ]);

    const ActiveCheckpoint = (): JSX.Element => {
        switch (checkpoint) {
        case CHECKOUT_CHECKPOINT.info:
            return InfoCheckpoint;
        case CHECKOUT_CHECKPOINT.shipping:
            return ShippingCheckpoint;
        case CHECKOUT_CHECKPOINT.billing:
        case CHECKOUT_CHECKPOINT.review:
            return BillingAndReviewCheckpoint;
        default:
            // CHECKOUT_CHECKPOINT.confirmation
            return ConfirmCheckpoint;
        }
    };

    return ActiveCheckpoint();
}

export default CheckoutCheckpoint;
